(() => {


/*!
Turbo 8.0.5
Copyright © 2024 37signals LLC
 */ /**
 * The MIT License (MIT)
 *
 * Copyright (c) 2019 Javan Makhmali
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */ (function(prototype) {
    if (typeof prototype.requestSubmit == "function") return;
    prototype.requestSubmit = function(submitter) {
        if (submitter) {
            validateSubmitter(submitter, this);
            submitter.click();
        } else {
            submitter = document.createElement("input");
            submitter.type = "submit";
            submitter.hidden = true;
            this.appendChild(submitter);
            submitter.click();
            this.removeChild(submitter);
        }
    };
    function validateSubmitter(submitter, form) {
        submitter instanceof HTMLElement || raise(TypeError, "parameter 1 is not of type 'HTMLElement'");
        submitter.type == "submit" || raise(TypeError, "The specified element is not a submit button");
        submitter.form == form || raise(DOMException, "The specified element is not owned by this form element", "NotFoundError");
    }
    function raise(errorConstructor, message, name) {
        throw new errorConstructor("Failed to execute 'requestSubmit' on 'HTMLFormElement': " + message + ".", name);
    }
})(HTMLFormElement.prototype);
const $dae3d0e46d061380$var$submittersByForm = new WeakMap();
function $dae3d0e46d061380$var$findSubmitterFromClickTarget(target) {
    const element = target instanceof Element ? target : target instanceof Node ? target.parentElement : null;
    const candidate = element ? element.closest("input, button") : null;
    return candidate?.type == "submit" ? candidate : null;
}
function $dae3d0e46d061380$var$clickCaptured(event) {
    const submitter = $dae3d0e46d061380$var$findSubmitterFromClickTarget(event.target);
    if (submitter && submitter.form) $dae3d0e46d061380$var$submittersByForm.set(submitter.form, submitter);
}
(function() {
    if ("submitter" in Event.prototype) return;
    let prototype = window.Event.prototype;
    // Certain versions of Safari 15 have a bug where they won't
    // populate the submitter. This hurts TurboDrive's enable/disable detection.
    // See https://bugs.webkit.org/show_bug.cgi?id=229660
    if ("SubmitEvent" in window) {
        const prototypeOfSubmitEvent = window.SubmitEvent.prototype;
        if (/Apple Computer/.test(navigator.vendor) && !("submitter" in prototypeOfSubmitEvent)) prototype = prototypeOfSubmitEvent;
        else return; // polyfill not needed
    }
    addEventListener("click", $dae3d0e46d061380$var$clickCaptured, true);
    Object.defineProperty(prototype, "submitter", {
        get () {
            if (this.type == "submit" && this.target instanceof HTMLFormElement) return $dae3d0e46d061380$var$submittersByForm.get(this.target);
        }
    });
})();
const $dae3d0e46d061380$export$65f98fefe20dbcb6 = {
    eager: "eager",
    lazy: "lazy"
};
/**
 * Contains a fragment of HTML which is updated based on navigation within
 * it (e.g. via links or form submissions).
 *
 * @customElement turbo-frame
 * @example
 *   <turbo-frame id="messages">
 *     <a href="/messages/expanded">
 *       Show all expanded messages in this frame.
 *     </a>
 *
 *     <form action="/messages">
 *       Show response from this form within this frame.
 *     </form>
 *   </turbo-frame>
 */ class $dae3d0e46d061380$export$f464a8f0ffa32c44 extends HTMLElement {
    static delegateConstructor = undefined;
    loaded = Promise.resolve();
    static get observedAttributes() {
        return [
            "disabled",
            "loading",
            "src"
        ];
    }
    constructor(){
        super();
        this.delegate = new $dae3d0e46d061380$export$f464a8f0ffa32c44.delegateConstructor(this);
    }
    connectedCallback() {
        this.delegate.connect();
    }
    disconnectedCallback() {
        this.delegate.disconnect();
    }
    reload() {
        return this.delegate.sourceURLReloaded();
    }
    attributeChangedCallback(name) {
        if (name == "loading") this.delegate.loadingStyleChanged();
        else if (name == "src") this.delegate.sourceURLChanged();
        else if (name == "disabled") this.delegate.disabledChanged();
    }
    /**
   * Gets the URL to lazily load source HTML from
   */ get src() {
        return this.getAttribute("src");
    }
    /**
   * Sets the URL to lazily load source HTML from
   */ set src(value) {
        if (value) this.setAttribute("src", value);
        else this.removeAttribute("src");
    }
    /**
   * Gets the refresh mode for the frame.
   */ get refresh() {
        return this.getAttribute("refresh");
    }
    /**
   * Sets the refresh mode for the frame.
   */ set refresh(value) {
        if (value) this.setAttribute("refresh", value);
        else this.removeAttribute("refresh");
    }
    /**
   * Determines if the element is loading
   */ get loading() {
        return $dae3d0e46d061380$var$frameLoadingStyleFromString(this.getAttribute("loading") || "");
    }
    /**
   * Sets the value of if the element is loading
   */ set loading(value) {
        if (value) this.setAttribute("loading", value);
        else this.removeAttribute("loading");
    }
    /**
   * Gets the disabled state of the frame.
   *
   * If disabled, no requests will be intercepted by the frame.
   */ get disabled() {
        return this.hasAttribute("disabled");
    }
    /**
   * Sets the disabled state of the frame.
   *
   * If disabled, no requests will be intercepted by the frame.
   */ set disabled(value) {
        if (value) this.setAttribute("disabled", "");
        else this.removeAttribute("disabled");
    }
    /**
   * Gets the autoscroll state of the frame.
   *
   * If true, the frame will be scrolled into view automatically on update.
   */ get autoscroll() {
        return this.hasAttribute("autoscroll");
    }
    /**
   * Sets the autoscroll state of the frame.
   *
   * If true, the frame will be scrolled into view automatically on update.
   */ set autoscroll(value) {
        if (value) this.setAttribute("autoscroll", "");
        else this.removeAttribute("autoscroll");
    }
    /**
   * Determines if the element has finished loading
   */ get complete() {
        return !this.delegate.isLoading;
    }
    /**
   * Gets the active state of the frame.
   *
   * If inactive, source changes will not be observed.
   */ get isActive() {
        return this.ownerDocument === document && !this.isPreview;
    }
    /**
   * Sets the active state of the frame.
   *
   * If inactive, source changes will not be observed.
   */ get isPreview() {
        return this.ownerDocument?.documentElement?.hasAttribute("data-turbo-preview");
    }
}
function $dae3d0e46d061380$var$frameLoadingStyleFromString(style) {
    switch(style.toLowerCase()){
        case "lazy":
            return $dae3d0e46d061380$export$65f98fefe20dbcb6.lazy;
        default:
            return $dae3d0e46d061380$export$65f98fefe20dbcb6.eager;
    }
}
function $dae3d0e46d061380$var$expandURL(locatable) {
    return new URL(locatable.toString(), document.baseURI);
}
function $dae3d0e46d061380$var$getAnchor(url) {
    let anchorMatch;
    if (url.hash) return url.hash.slice(1);
    else if (anchorMatch = url.href.match(/#(.*)$/)) return anchorMatch[1];
}
function $dae3d0e46d061380$var$getAction$1(form, submitter) {
    const action = submitter?.getAttribute("formaction") || form.getAttribute("action") || form.action;
    return $dae3d0e46d061380$var$expandURL(action);
}
function $dae3d0e46d061380$var$getExtension(url) {
    return ($dae3d0e46d061380$var$getLastPathComponent(url).match(/\.[^.]*$/) || [])[0] || "";
}
function $dae3d0e46d061380$var$isHTML(url) {
    return !!$dae3d0e46d061380$var$getExtension(url).match(/^(?:|\.(?:htm|html|xhtml|php))$/);
}
function $dae3d0e46d061380$var$isPrefixedBy(baseURL, url) {
    const prefix = $dae3d0e46d061380$var$getPrefix(url);
    return baseURL.href === $dae3d0e46d061380$var$expandURL(prefix).href || baseURL.href.startsWith(prefix);
}
function $dae3d0e46d061380$var$locationIsVisitable(location1, rootLocation) {
    return $dae3d0e46d061380$var$isPrefixedBy(location1, rootLocation) && $dae3d0e46d061380$var$isHTML(location1);
}
function $dae3d0e46d061380$var$getRequestURL(url) {
    const anchor = $dae3d0e46d061380$var$getAnchor(url);
    return anchor != null ? url.href.slice(0, -(anchor.length + 1)) : url.href;
}
function $dae3d0e46d061380$var$toCacheKey(url) {
    return $dae3d0e46d061380$var$getRequestURL(url);
}
function $dae3d0e46d061380$var$urlsAreEqual(left, right) {
    return $dae3d0e46d061380$var$expandURL(left).href == $dae3d0e46d061380$var$expandURL(right).href;
}
function $dae3d0e46d061380$var$getPathComponents(url) {
    return url.pathname.split("/").slice(1);
}
function $dae3d0e46d061380$var$getLastPathComponent(url) {
    return $dae3d0e46d061380$var$getPathComponents(url).slice(-1)[0];
}
function $dae3d0e46d061380$var$getPrefix(url) {
    return $dae3d0e46d061380$var$addTrailingSlash(url.origin + url.pathname);
}
function $dae3d0e46d061380$var$addTrailingSlash(value) {
    return value.endsWith("/") ? value : value + "/";
}
class $dae3d0e46d061380$export$3ae09bba5b6b906e {
    constructor(response){
        this.response = response;
    }
    get succeeded() {
        return this.response.ok;
    }
    get failed() {
        return !this.succeeded;
    }
    get clientError() {
        return this.statusCode >= 400 && this.statusCode <= 499;
    }
    get serverError() {
        return this.statusCode >= 500 && this.statusCode <= 599;
    }
    get redirected() {
        return this.response.redirected;
    }
    get location() {
        return $dae3d0e46d061380$var$expandURL(this.response.url);
    }
    get isHTML() {
        return this.contentType && this.contentType.match(/^(?:text\/([^\s;,]+\b)?html|application\/xhtml\+xml)\b/);
    }
    get statusCode() {
        return this.response.status;
    }
    get contentType() {
        return this.header("Content-Type");
    }
    get responseText() {
        return this.response.clone().text();
    }
    get responseHTML() {
        if (this.isHTML) return this.response.clone().text();
        else return Promise.resolve(undefined);
    }
    header(name) {
        return this.response.headers.get(name);
    }
}
function $dae3d0e46d061380$var$activateScriptElement(element) {
    if (element.getAttribute("data-turbo-eval") == "false") return element;
    else {
        const createdScriptElement = document.createElement("script");
        const cspNonce = $dae3d0e46d061380$var$getMetaContent("csp-nonce");
        if (cspNonce) createdScriptElement.nonce = cspNonce;
        createdScriptElement.textContent = element.textContent;
        createdScriptElement.async = false;
        $dae3d0e46d061380$var$copyElementAttributes(createdScriptElement, element);
        return createdScriptElement;
    }
}
function $dae3d0e46d061380$var$copyElementAttributes(destinationElement, sourceElement) {
    for (const { name: name, value: value } of sourceElement.attributes)destinationElement.setAttribute(name, value);
}
function $dae3d0e46d061380$var$createDocumentFragment(html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    return template.content;
}
function $dae3d0e46d061380$var$dispatch(eventName, { target: target, cancelable: cancelable, detail: detail } = {}) {
    const event = new CustomEvent(eventName, {
        cancelable: cancelable,
        bubbles: true,
        composed: true,
        detail: detail
    });
    if (target && target.isConnected) target.dispatchEvent(event);
    else document.documentElement.dispatchEvent(event);
    return event;
}
function $dae3d0e46d061380$var$nextRepaint() {
    if (document.visibilityState === "hidden") return $dae3d0e46d061380$var$nextEventLoopTick();
    else return $dae3d0e46d061380$var$nextAnimationFrame();
}
function $dae3d0e46d061380$var$nextAnimationFrame() {
    return new Promise((resolve)=>requestAnimationFrame(()=>resolve()));
}
function $dae3d0e46d061380$var$nextEventLoopTick() {
    return new Promise((resolve)=>setTimeout(()=>resolve(), 0));
}
function $dae3d0e46d061380$var$nextMicrotask() {
    return Promise.resolve();
}
function $dae3d0e46d061380$var$parseHTMLDocument(html = "") {
    return new DOMParser().parseFromString(html, "text/html");
}
function $dae3d0e46d061380$var$unindent(strings, ...values) {
    const lines = $dae3d0e46d061380$var$interpolate(strings, values).replace(/^\n/, "").split("\n");
    const match = lines[0].match(/^\s+/);
    const indent = match ? match[0].length : 0;
    return lines.map((line)=>line.slice(indent)).join("\n");
}
function $dae3d0e46d061380$var$interpolate(strings, values) {
    return strings.reduce((result, string, i)=>{
        const value = values[i] == undefined ? "" : values[i];
        return result + string + value;
    }, "");
}
function $dae3d0e46d061380$var$uuid() {
    return Array.from({
        length: 36
    }).map((_, i)=>{
        if (i == 8 || i == 13 || i == 18 || i == 23) return "-";
        else if (i == 14) return "4";
        else if (i == 19) return (Math.floor(Math.random() * 4) + 8).toString(16);
        else return Math.floor(Math.random() * 15).toString(16);
    }).join("");
}
function $dae3d0e46d061380$var$getAttribute(attributeName, ...elements) {
    for (const value of elements.map((element)=>element?.getAttribute(attributeName))){
        if (typeof value == "string") return value;
    }
    return null;
}
function $dae3d0e46d061380$var$hasAttribute(attributeName, ...elements) {
    return elements.some((element)=>element && element.hasAttribute(attributeName));
}
function $dae3d0e46d061380$var$markAsBusy(...elements) {
    for (const element of elements){
        if (element.localName == "turbo-frame") element.setAttribute("busy", "");
        element.setAttribute("aria-busy", "true");
    }
}
function $dae3d0e46d061380$var$clearBusyState(...elements) {
    for (const element of elements){
        if (element.localName == "turbo-frame") element.removeAttribute("busy");
        element.removeAttribute("aria-busy");
    }
}
function $dae3d0e46d061380$var$waitForLoad(element, timeoutInMilliseconds = 2000) {
    return new Promise((resolve)=>{
        const onComplete = ()=>{
            element.removeEventListener("error", onComplete);
            element.removeEventListener("load", onComplete);
            resolve();
        };
        element.addEventListener("load", onComplete, {
            once: true
        });
        element.addEventListener("error", onComplete, {
            once: true
        });
        setTimeout(resolve, timeoutInMilliseconds);
    });
}
function $dae3d0e46d061380$var$getHistoryMethodForAction(action) {
    switch(action){
        case "replace":
            return history.replaceState;
        case "advance":
        case "restore":
            return history.pushState;
    }
}
function $dae3d0e46d061380$var$isAction(action) {
    return action == "advance" || action == "replace" || action == "restore";
}
function $dae3d0e46d061380$var$getVisitAction(...elements) {
    const action = $dae3d0e46d061380$var$getAttribute("data-turbo-action", ...elements);
    return $dae3d0e46d061380$var$isAction(action) ? action : null;
}
function $dae3d0e46d061380$var$getMetaElement(name) {
    return document.querySelector(`meta[name="${name}"]`);
}
function $dae3d0e46d061380$var$getMetaContent(name) {
    const element = $dae3d0e46d061380$var$getMetaElement(name);
    return element && element.content;
}
function $dae3d0e46d061380$var$setMetaContent(name, content) {
    let element = $dae3d0e46d061380$var$getMetaElement(name);
    if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
    }
    element.setAttribute("content", content);
    return element;
}
function $dae3d0e46d061380$var$findClosestRecursively(element, selector) {
    if (element instanceof Element) return element.closest(selector) || $dae3d0e46d061380$var$findClosestRecursively(element.assignedSlot || element.getRootNode()?.host, selector);
}
function $dae3d0e46d061380$var$elementIsFocusable(element) {
    const inertDisabledOrHidden = "[inert], :disabled, [hidden], details:not([open]), dialog:not([open])";
    return !!element && element.closest(inertDisabledOrHidden) == null && typeof element.focus == "function";
}
function $dae3d0e46d061380$var$queryAutofocusableElement(elementOrDocumentFragment) {
    return Array.from(elementOrDocumentFragment.querySelectorAll("[autofocus]")).find($dae3d0e46d061380$var$elementIsFocusable);
}
async function $dae3d0e46d061380$var$around(callback, reader) {
    const before = reader();
    callback();
    await $dae3d0e46d061380$var$nextAnimationFrame();
    const after = reader();
    return [
        before,
        after
    ];
}
function $dae3d0e46d061380$var$doesNotTargetIFrame(name) {
    if (name === "_blank") return false;
    else if (name) {
        for (const element of document.getElementsByName(name)){
            if (element instanceof HTMLIFrameElement) return false;
        }
        return true;
    } else return true;
}
function $dae3d0e46d061380$var$findLinkFromClickTarget(target) {
    return $dae3d0e46d061380$var$findClosestRecursively(target, "a[href]:not([target^=_]):not([download])");
}
function $dae3d0e46d061380$var$getLocationForLink(link) {
    return $dae3d0e46d061380$var$expandURL(link.getAttribute("href") || "");
}
function $dae3d0e46d061380$var$debounce(fn, delay) {
    let timeoutId = null;
    return (...args)=>{
        const callback = ()=>fn.apply(this, args);
        clearTimeout(timeoutId);
        timeoutId = setTimeout(callback, delay);
    };
}
class $dae3d0e46d061380$var$LimitedSet extends Set {
    constructor(maxSize){
        super();
        this.maxSize = maxSize;
    }
    add(value) {
        if (this.size >= this.maxSize) {
            const iterator = this.values();
            const oldestValue = iterator.next().value;
            this.delete(oldestValue);
        }
        super.add(value);
    }
}
const $dae3d0e46d061380$var$recentRequests = new $dae3d0e46d061380$var$LimitedSet(20);
const $dae3d0e46d061380$var$nativeFetch = window.fetch;
function $dae3d0e46d061380$export$e7aa7bc5c1b3cfb3(url, options = {}) {
    const modifiedHeaders = new Headers(options.headers || {});
    const requestUID = $dae3d0e46d061380$var$uuid();
    $dae3d0e46d061380$var$recentRequests.add(requestUID);
    modifiedHeaders.append("X-Turbo-Request-Id", requestUID);
    return $dae3d0e46d061380$var$nativeFetch(url, {
        ...options,
        headers: modifiedHeaders
    });
}
function $dae3d0e46d061380$export$4d71dd6e9f25af75(method) {
    switch(method.toLowerCase()){
        case "get":
            return $dae3d0e46d061380$export$76f6869e29ff06ee.get;
        case "post":
            return $dae3d0e46d061380$export$76f6869e29ff06ee.post;
        case "put":
            return $dae3d0e46d061380$export$76f6869e29ff06ee.put;
        case "patch":
            return $dae3d0e46d061380$export$76f6869e29ff06ee.patch;
        case "delete":
            return $dae3d0e46d061380$export$76f6869e29ff06ee.delete;
    }
}
const $dae3d0e46d061380$export$76f6869e29ff06ee = {
    get: "get",
    post: "post",
    put: "put",
    patch: "patch",
    delete: "delete"
};
function $dae3d0e46d061380$export$4894fff9b16f993d(encoding) {
    switch(encoding.toLowerCase()){
        case $dae3d0e46d061380$export$12bb94f0959cac5.multipart:
            return $dae3d0e46d061380$export$12bb94f0959cac5.multipart;
        case $dae3d0e46d061380$export$12bb94f0959cac5.plain:
            return $dae3d0e46d061380$export$12bb94f0959cac5.plain;
        default:
            return $dae3d0e46d061380$export$12bb94f0959cac5.urlEncoded;
    }
}
const $dae3d0e46d061380$export$12bb94f0959cac5 = {
    urlEncoded: "application/x-www-form-urlencoded",
    multipart: "multipart/form-data",
    plain: "text/plain"
};
class $dae3d0e46d061380$export$319b96fe59834522 {
    abortController = new AbortController();
    #resolveRequestPromise = (_value)=>{};
    constructor(delegate, method, location1, requestBody = new URLSearchParams(), target = null, enctype = $dae3d0e46d061380$export$12bb94f0959cac5.urlEncoded){
        const [url, body] = $dae3d0e46d061380$var$buildResourceAndBody($dae3d0e46d061380$var$expandURL(location1), method, requestBody, enctype);
        this.delegate = delegate;
        this.url = url;
        this.target = target;
        this.fetchOptions = {
            credentials: "same-origin",
            redirect: "follow",
            method: method.toUpperCase(),
            headers: {
                ...this.defaultHeaders
            },
            body: body,
            signal: this.abortSignal,
            referrer: this.delegate.referrer?.href
        };
        this.enctype = enctype;
    }
    get method() {
        return this.fetchOptions.method;
    }
    set method(value) {
        const fetchBody = this.isSafe ? this.url.searchParams : this.fetchOptions.body || new FormData();
        const fetchMethod = $dae3d0e46d061380$export$4d71dd6e9f25af75(value) || $dae3d0e46d061380$export$76f6869e29ff06ee.get;
        this.url.search = "";
        const [url, body] = $dae3d0e46d061380$var$buildResourceAndBody(this.url, fetchMethod, fetchBody, this.enctype);
        this.url = url;
        this.fetchOptions.body = body;
        this.fetchOptions.method = fetchMethod.toUpperCase();
    }
    get headers() {
        return this.fetchOptions.headers;
    }
    set headers(value) {
        this.fetchOptions.headers = value;
    }
    get body() {
        if (this.isSafe) return this.url.searchParams;
        else return this.fetchOptions.body;
    }
    set body(value) {
        this.fetchOptions.body = value;
    }
    get location() {
        return this.url;
    }
    get params() {
        return this.url.searchParams;
    }
    get entries() {
        return this.body ? Array.from(this.body.entries()) : [];
    }
    cancel() {
        this.abortController.abort();
    }
    async perform() {
        const { fetchOptions: fetchOptions } = this;
        this.delegate.prepareRequest(this);
        const event = await this.#allowRequestToBeIntercepted(fetchOptions);
        try {
            this.delegate.requestStarted(this);
            if (event.detail.fetchRequest) this.response = event.detail.fetchRequest.response;
            else this.response = $dae3d0e46d061380$export$e7aa7bc5c1b3cfb3(this.url.href, fetchOptions);
            const response = await this.response;
            return await this.receive(response);
        } catch (error) {
            if (error.name !== "AbortError") {
                if (this.#willDelegateErrorHandling(error)) this.delegate.requestErrored(this, error);
                throw error;
            }
        } finally{
            this.delegate.requestFinished(this);
        }
    }
    async receive(response) {
        const fetchResponse = new $dae3d0e46d061380$export$3ae09bba5b6b906e(response);
        const event = $dae3d0e46d061380$var$dispatch("turbo:before-fetch-response", {
            cancelable: true,
            detail: {
                fetchResponse: fetchResponse
            },
            target: this.target
        });
        if (event.defaultPrevented) this.delegate.requestPreventedHandlingResponse(this, fetchResponse);
        else if (fetchResponse.succeeded) this.delegate.requestSucceededWithResponse(this, fetchResponse);
        else this.delegate.requestFailedWithResponse(this, fetchResponse);
        return fetchResponse;
    }
    get defaultHeaders() {
        return {
            Accept: "text/html, application/xhtml+xml"
        };
    }
    get isSafe() {
        return $dae3d0e46d061380$export$26e0721b40973527(this.method);
    }
    get abortSignal() {
        return this.abortController.signal;
    }
    acceptResponseType(mimeType) {
        this.headers["Accept"] = [
            mimeType,
            this.headers["Accept"]
        ].join(", ");
    }
    async #allowRequestToBeIntercepted(fetchOptions) {
        const requestInterception = new Promise((resolve)=>this.#resolveRequestPromise = resolve);
        const event = $dae3d0e46d061380$var$dispatch("turbo:before-fetch-request", {
            cancelable: true,
            detail: {
                fetchOptions: fetchOptions,
                url: this.url,
                resume: this.#resolveRequestPromise
            },
            target: this.target
        });
        this.url = event.detail.url;
        if (event.defaultPrevented) await requestInterception;
        return event;
    }
    #willDelegateErrorHandling(error) {
        const event = $dae3d0e46d061380$var$dispatch("turbo:fetch-request-error", {
            target: this.target,
            cancelable: true,
            detail: {
                request: this,
                error: error
            }
        });
        return !event.defaultPrevented;
    }
}
function $dae3d0e46d061380$export$26e0721b40973527(fetchMethod) {
    return $dae3d0e46d061380$export$4d71dd6e9f25af75(fetchMethod) == $dae3d0e46d061380$export$76f6869e29ff06ee.get;
}
function $dae3d0e46d061380$var$buildResourceAndBody(resource, method, requestBody, enctype) {
    const searchParams = Array.from(requestBody).length > 0 ? new URLSearchParams($dae3d0e46d061380$var$entriesExcludingFiles(requestBody)) : resource.searchParams;
    if ($dae3d0e46d061380$export$26e0721b40973527(method)) return [
        $dae3d0e46d061380$var$mergeIntoURLSearchParams(resource, searchParams),
        null
    ];
    else if (enctype == $dae3d0e46d061380$export$12bb94f0959cac5.urlEncoded) return [
        resource,
        searchParams
    ];
    else return [
        resource,
        requestBody
    ];
}
function $dae3d0e46d061380$var$entriesExcludingFiles(requestBody) {
    const entries = [];
    for (const [name, value] of requestBody){
        if (value instanceof File) continue;
        else entries.push([
            name,
            value
        ]);
    }
    return entries;
}
function $dae3d0e46d061380$var$mergeIntoURLSearchParams(url, requestBody) {
    const searchParams = new URLSearchParams($dae3d0e46d061380$var$entriesExcludingFiles(requestBody));
    url.search = searchParams.toString();
    return url;
}
class $dae3d0e46d061380$var$AppearanceObserver {
    started = false;
    constructor(delegate, element){
        this.delegate = delegate;
        this.element = element;
        this.intersectionObserver = new IntersectionObserver(this.intersect);
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.intersectionObserver.observe(this.element);
        }
    }
    stop() {
        if (this.started) {
            this.started = false;
            this.intersectionObserver.unobserve(this.element);
        }
    }
    intersect = (entries)=>{
        const lastEntry = entries.slice(-1)[0];
        if (lastEntry?.isIntersecting) this.delegate.elementAppearedInViewport(this.element);
    };
}
class $dae3d0e46d061380$var$StreamMessage {
    static contentType = "text/vnd.turbo-stream.html";
    static wrap(message) {
        if (typeof message == "string") return new this($dae3d0e46d061380$var$createDocumentFragment(message));
        else return message;
    }
    constructor(fragment){
        this.fragment = $dae3d0e46d061380$var$importStreamElements(fragment);
    }
}
function $dae3d0e46d061380$var$importStreamElements(fragment) {
    for (const element of fragment.querySelectorAll("turbo-stream")){
        const streamElement = document.importNode(element, true);
        for (const inertScriptElement of streamElement.templateElement.content.querySelectorAll("script"))inertScriptElement.replaceWith($dae3d0e46d061380$var$activateScriptElement(inertScriptElement));
        element.replaceWith(streamElement);
    }
    return fragment;
}
const $dae3d0e46d061380$var$PREFETCH_DELAY = 100;
class $dae3d0e46d061380$var$PrefetchCache {
    #prefetchTimeout = null;
    #prefetched = null;
    get(url) {
        if (this.#prefetched && this.#prefetched.url === url && this.#prefetched.expire > Date.now()) return this.#prefetched.request;
    }
    setLater(url, request, ttl) {
        this.clear();
        this.#prefetchTimeout = setTimeout(()=>{
            request.perform();
            this.set(url, request, ttl);
            this.#prefetchTimeout = null;
        }, $dae3d0e46d061380$var$PREFETCH_DELAY);
    }
    set(url, request, ttl) {
        this.#prefetched = {
            url: url,
            request: request,
            expire: new Date(new Date().getTime() + ttl)
        };
    }
    clear() {
        if (this.#prefetchTimeout) clearTimeout(this.#prefetchTimeout);
        this.#prefetched = null;
    }
}
const $dae3d0e46d061380$var$cacheTtl = 10000;
const $dae3d0e46d061380$var$prefetchCache = new $dae3d0e46d061380$var$PrefetchCache();
const $dae3d0e46d061380$var$FormSubmissionState = {
    initialized: "initialized",
    requesting: "requesting",
    waiting: "waiting",
    receiving: "receiving",
    stopping: "stopping",
    stopped: "stopped"
};
class $dae3d0e46d061380$var$FormSubmission {
    state = $dae3d0e46d061380$var$FormSubmissionState.initialized;
    static confirmMethod(message, _element, _submitter) {
        return Promise.resolve(confirm(message));
    }
    constructor(delegate, formElement, submitter, mustRedirect = false){
        const method = $dae3d0e46d061380$var$getMethod(formElement, submitter);
        const action = $dae3d0e46d061380$var$getAction($dae3d0e46d061380$var$getFormAction(formElement, submitter), method);
        const body = $dae3d0e46d061380$var$buildFormData(formElement, submitter);
        const enctype = $dae3d0e46d061380$var$getEnctype(formElement, submitter);
        this.delegate = delegate;
        this.formElement = formElement;
        this.submitter = submitter;
        this.fetchRequest = new $dae3d0e46d061380$export$319b96fe59834522(this, method, action, body, formElement, enctype);
        this.mustRedirect = mustRedirect;
    }
    get method() {
        return this.fetchRequest.method;
    }
    set method(value) {
        this.fetchRequest.method = value;
    }
    get action() {
        return this.fetchRequest.url.toString();
    }
    set action(value) {
        this.fetchRequest.url = $dae3d0e46d061380$var$expandURL(value);
    }
    get body() {
        return this.fetchRequest.body;
    }
    get enctype() {
        return this.fetchRequest.enctype;
    }
    get isSafe() {
        return this.fetchRequest.isSafe;
    }
    get location() {
        return this.fetchRequest.url;
    }
    // The submission process
    async start() {
        const { initialized: initialized, requesting: requesting } = $dae3d0e46d061380$var$FormSubmissionState;
        const confirmationMessage = $dae3d0e46d061380$var$getAttribute("data-turbo-confirm", this.submitter, this.formElement);
        if (typeof confirmationMessage === "string") {
            const answer = await $dae3d0e46d061380$var$FormSubmission.confirmMethod(confirmationMessage, this.formElement, this.submitter);
            if (!answer) return;
        }
        if (this.state == initialized) {
            this.state = requesting;
            return this.fetchRequest.perform();
        }
    }
    stop() {
        const { stopping: stopping, stopped: stopped } = $dae3d0e46d061380$var$FormSubmissionState;
        if (this.state != stopping && this.state != stopped) {
            this.state = stopping;
            this.fetchRequest.cancel();
            return true;
        }
    }
    // Fetch request delegate
    prepareRequest(request) {
        if (!request.isSafe) {
            const token = $dae3d0e46d061380$var$getCookieValue($dae3d0e46d061380$var$getMetaContent("csrf-param")) || $dae3d0e46d061380$var$getMetaContent("csrf-token");
            if (token) request.headers["X-CSRF-Token"] = token;
        }
        if (this.requestAcceptsTurboStreamResponse(request)) request.acceptResponseType($dae3d0e46d061380$var$StreamMessage.contentType);
    }
    requestStarted(_request) {
        this.state = $dae3d0e46d061380$var$FormSubmissionState.waiting;
        this.submitter?.setAttribute("disabled", "");
        this.setSubmitsWith();
        $dae3d0e46d061380$var$markAsBusy(this.formElement);
        $dae3d0e46d061380$var$dispatch("turbo:submit-start", {
            target: this.formElement,
            detail: {
                formSubmission: this
            }
        });
        this.delegate.formSubmissionStarted(this);
    }
    requestPreventedHandlingResponse(request, response) {
        $dae3d0e46d061380$var$prefetchCache.clear();
        this.result = {
            success: response.succeeded,
            fetchResponse: response
        };
    }
    requestSucceededWithResponse(request, response) {
        if (response.clientError || response.serverError) {
            this.delegate.formSubmissionFailedWithResponse(this, response);
            return;
        }
        $dae3d0e46d061380$var$prefetchCache.clear();
        if (this.requestMustRedirect(request) && $dae3d0e46d061380$var$responseSucceededWithoutRedirect(response)) {
            const error = new Error("Form responses must redirect to another location");
            this.delegate.formSubmissionErrored(this, error);
        } else {
            this.state = $dae3d0e46d061380$var$FormSubmissionState.receiving;
            this.result = {
                success: true,
                fetchResponse: response
            };
            this.delegate.formSubmissionSucceededWithResponse(this, response);
        }
    }
    requestFailedWithResponse(request, response) {
        this.result = {
            success: false,
            fetchResponse: response
        };
        this.delegate.formSubmissionFailedWithResponse(this, response);
    }
    requestErrored(request, error) {
        this.result = {
            success: false,
            error: error
        };
        this.delegate.formSubmissionErrored(this, error);
    }
    requestFinished(_request) {
        this.state = $dae3d0e46d061380$var$FormSubmissionState.stopped;
        this.submitter?.removeAttribute("disabled");
        this.resetSubmitterText();
        $dae3d0e46d061380$var$clearBusyState(this.formElement);
        $dae3d0e46d061380$var$dispatch("turbo:submit-end", {
            target: this.formElement,
            detail: {
                formSubmission: this,
                ...this.result
            }
        });
        this.delegate.formSubmissionFinished(this);
    }
    // Private
    setSubmitsWith() {
        if (!this.submitter || !this.submitsWith) return;
        if (this.submitter.matches("button")) {
            this.originalSubmitText = this.submitter.innerHTML;
            this.submitter.innerHTML = this.submitsWith;
        } else if (this.submitter.matches("input")) {
            const input = this.submitter;
            this.originalSubmitText = input.value;
            input.value = this.submitsWith;
        }
    }
    resetSubmitterText() {
        if (!this.submitter || !this.originalSubmitText) return;
        if (this.submitter.matches("button")) this.submitter.innerHTML = this.originalSubmitText;
        else if (this.submitter.matches("input")) {
            const input = this.submitter;
            input.value = this.originalSubmitText;
        }
    }
    requestMustRedirect(request) {
        return !request.isSafe && this.mustRedirect;
    }
    requestAcceptsTurboStreamResponse(request) {
        return !request.isSafe || $dae3d0e46d061380$var$hasAttribute("data-turbo-stream", this.submitter, this.formElement);
    }
    get submitsWith() {
        return this.submitter?.getAttribute("data-turbo-submits-with");
    }
}
function $dae3d0e46d061380$var$buildFormData(formElement, submitter) {
    const formData = new FormData(formElement);
    const name = submitter?.getAttribute("name");
    const value = submitter?.getAttribute("value");
    if (name) formData.append(name, value || "");
    return formData;
}
function $dae3d0e46d061380$var$getCookieValue(cookieName) {
    if (cookieName != null) {
        const cookies = document.cookie ? document.cookie.split("; ") : [];
        const cookie = cookies.find((cookie)=>cookie.startsWith(cookieName));
        if (cookie) {
            const value = cookie.split("=").slice(1).join("=");
            return value ? decodeURIComponent(value) : undefined;
        }
    }
}
function $dae3d0e46d061380$var$responseSucceededWithoutRedirect(response) {
    return response.statusCode == 200 && !response.redirected;
}
function $dae3d0e46d061380$var$getFormAction(formElement, submitter) {
    const formElementAction = typeof formElement.action === "string" ? formElement.action : null;
    if (submitter?.hasAttribute("formaction")) return submitter.getAttribute("formaction") || "";
    else return formElement.getAttribute("action") || formElementAction || "";
}
function $dae3d0e46d061380$var$getAction(formAction, fetchMethod) {
    const action = $dae3d0e46d061380$var$expandURL(formAction);
    if ($dae3d0e46d061380$export$26e0721b40973527(fetchMethod)) action.search = "";
    return action;
}
function $dae3d0e46d061380$var$getMethod(formElement, submitter) {
    const method = submitter?.getAttribute("formmethod") || formElement.getAttribute("method") || "";
    return $dae3d0e46d061380$export$4d71dd6e9f25af75(method.toLowerCase()) || $dae3d0e46d061380$export$76f6869e29ff06ee.get;
}
function $dae3d0e46d061380$var$getEnctype(formElement, submitter) {
    return $dae3d0e46d061380$export$4894fff9b16f993d(submitter?.getAttribute("formenctype") || formElement.enctype);
}
class $dae3d0e46d061380$var$Snapshot {
    constructor(element){
        this.element = element;
    }
    get activeElement() {
        return this.element.ownerDocument.activeElement;
    }
    get children() {
        return [
            ...this.element.children
        ];
    }
    hasAnchor(anchor) {
        return this.getElementForAnchor(anchor) != null;
    }
    getElementForAnchor(anchor) {
        return anchor ? this.element.querySelector(`[id='${anchor}'], a[name='${anchor}']`) : null;
    }
    get isConnected() {
        return this.element.isConnected;
    }
    get firstAutofocusableElement() {
        return $dae3d0e46d061380$var$queryAutofocusableElement(this.element);
    }
    get permanentElements() {
        return $dae3d0e46d061380$var$queryPermanentElementsAll(this.element);
    }
    getPermanentElementById(id) {
        return $dae3d0e46d061380$var$getPermanentElementById(this.element, id);
    }
    getPermanentElementMapForSnapshot(snapshot) {
        const permanentElementMap = {};
        for (const currentPermanentElement of this.permanentElements){
            const { id: id } = currentPermanentElement;
            const newPermanentElement = snapshot.getPermanentElementById(id);
            if (newPermanentElement) permanentElementMap[id] = [
                currentPermanentElement,
                newPermanentElement
            ];
        }
        return permanentElementMap;
    }
}
function $dae3d0e46d061380$var$getPermanentElementById(node, id) {
    return node.querySelector(`#${id}[data-turbo-permanent]`);
}
function $dae3d0e46d061380$var$queryPermanentElementsAll(node) {
    return node.querySelectorAll("[id][data-turbo-permanent]");
}
class $dae3d0e46d061380$var$FormSubmitObserver {
    started = false;
    constructor(delegate, eventTarget){
        this.delegate = delegate;
        this.eventTarget = eventTarget;
    }
    start() {
        if (!this.started) {
            this.eventTarget.addEventListener("submit", this.submitCaptured, true);
            this.started = true;
        }
    }
    stop() {
        if (this.started) {
            this.eventTarget.removeEventListener("submit", this.submitCaptured, true);
            this.started = false;
        }
    }
    submitCaptured = ()=>{
        this.eventTarget.removeEventListener("submit", this.submitBubbled, false);
        this.eventTarget.addEventListener("submit", this.submitBubbled, false);
    };
    submitBubbled = (event)=>{
        if (!event.defaultPrevented) {
            const form = event.target instanceof HTMLFormElement ? event.target : undefined;
            const submitter = event.submitter || undefined;
            if (form && $dae3d0e46d061380$var$submissionDoesNotDismissDialog(form, submitter) && $dae3d0e46d061380$var$submissionDoesNotTargetIFrame(form, submitter) && this.delegate.willSubmitForm(form, submitter)) {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.delegate.formSubmitted(form, submitter);
            }
        }
    };
}
function $dae3d0e46d061380$var$submissionDoesNotDismissDialog(form, submitter) {
    const method = submitter?.getAttribute("formmethod") || form.getAttribute("method");
    return method != "dialog";
}
function $dae3d0e46d061380$var$submissionDoesNotTargetIFrame(form, submitter) {
    const target = submitter?.getAttribute("formtarget") || form.getAttribute("target");
    return $dae3d0e46d061380$var$doesNotTargetIFrame(target);
}
class $dae3d0e46d061380$var$View {
    #resolveRenderPromise = (_value)=>{};
    #resolveInterceptionPromise = (_value)=>{};
    constructor(delegate, element){
        this.delegate = delegate;
        this.element = element;
    }
    // Scrolling
    scrollToAnchor(anchor) {
        const element = this.snapshot.getElementForAnchor(anchor);
        if (element) {
            this.scrollToElement(element);
            this.focusElement(element);
        } else this.scrollToPosition({
            x: 0,
            y: 0
        });
    }
    scrollToAnchorFromLocation(location1) {
        this.scrollToAnchor($dae3d0e46d061380$var$getAnchor(location1));
    }
    scrollToElement(element) {
        element.scrollIntoView();
    }
    focusElement(element) {
        if (element instanceof HTMLElement) {
            if (element.hasAttribute("tabindex")) element.focus();
            else {
                element.setAttribute("tabindex", "-1");
                element.focus();
                element.removeAttribute("tabindex");
            }
        }
    }
    scrollToPosition({ x: x, y: y }) {
        this.scrollRoot.scrollTo(x, y);
    }
    scrollToTop() {
        this.scrollToPosition({
            x: 0,
            y: 0
        });
    }
    get scrollRoot() {
        return window;
    }
    // Rendering
    async render(renderer) {
        const { isPreview: isPreview, shouldRender: shouldRender, willRender: willRender, newSnapshot: snapshot } = renderer;
        // A workaround to ignore tracked element mismatch reloads when performing
        // a promoted Visit from a frame navigation
        const shouldInvalidate = willRender;
        if (shouldRender) try {
            this.renderPromise = new Promise((resolve)=>this.#resolveRenderPromise = resolve);
            this.renderer = renderer;
            await this.prepareToRenderSnapshot(renderer);
            const renderInterception = new Promise((resolve)=>this.#resolveInterceptionPromise = resolve);
            const options = {
                resume: this.#resolveInterceptionPromise,
                render: this.renderer.renderElement,
                renderMethod: this.renderer.renderMethod
            };
            const immediateRender = this.delegate.allowsImmediateRender(snapshot, options);
            if (!immediateRender) await renderInterception;
            await this.renderSnapshot(renderer);
            this.delegate.viewRenderedSnapshot(snapshot, isPreview, this.renderer.renderMethod);
            this.delegate.preloadOnLoadLinksForView(this.element);
            this.finishRenderingSnapshot(renderer);
        } finally{
            delete this.renderer;
            this.#resolveRenderPromise(undefined);
            delete this.renderPromise;
        }
        else if (shouldInvalidate) this.invalidate(renderer.reloadReason);
    }
    invalidate(reason) {
        this.delegate.viewInvalidated(reason);
    }
    async prepareToRenderSnapshot(renderer) {
        this.markAsPreview(renderer.isPreview);
        await renderer.prepareToRender();
    }
    markAsPreview(isPreview) {
        if (isPreview) this.element.setAttribute("data-turbo-preview", "");
        else this.element.removeAttribute("data-turbo-preview");
    }
    markVisitDirection(direction) {
        this.element.setAttribute("data-turbo-visit-direction", direction);
    }
    unmarkVisitDirection() {
        this.element.removeAttribute("data-turbo-visit-direction");
    }
    async renderSnapshot(renderer) {
        await renderer.render();
    }
    finishRenderingSnapshot(renderer) {
        renderer.finishRendering();
    }
}
class $dae3d0e46d061380$var$FrameView extends $dae3d0e46d061380$var$View {
    missing() {
        this.element.innerHTML = `<strong class="turbo-frame-error">Content missing</strong>`;
    }
    get snapshot() {
        return new $dae3d0e46d061380$var$Snapshot(this.element);
    }
}
class $dae3d0e46d061380$var$LinkInterceptor {
    constructor(delegate, element){
        this.delegate = delegate;
        this.element = element;
    }
    start() {
        this.element.addEventListener("click", this.clickBubbled);
        document.addEventListener("turbo:click", this.linkClicked);
        document.addEventListener("turbo:before-visit", this.willVisit);
    }
    stop() {
        this.element.removeEventListener("click", this.clickBubbled);
        document.removeEventListener("turbo:click", this.linkClicked);
        document.removeEventListener("turbo:before-visit", this.willVisit);
    }
    clickBubbled = (event)=>{
        if (this.clickEventIsSignificant(event)) this.clickEvent = event;
        else delete this.clickEvent;
    };
    linkClicked = (event)=>{
        if (this.clickEvent && this.clickEventIsSignificant(event)) {
            if (this.delegate.shouldInterceptLinkClick(event.target, event.detail.url, event.detail.originalEvent)) {
                this.clickEvent.preventDefault();
                event.preventDefault();
                this.delegate.linkClickIntercepted(event.target, event.detail.url, event.detail.originalEvent);
            }
        }
        delete this.clickEvent;
    };
    willVisit = (_event)=>{
        delete this.clickEvent;
    };
    clickEventIsSignificant(event) {
        const target = event.composed ? event.target?.parentElement : event.target;
        const element = $dae3d0e46d061380$var$findLinkFromClickTarget(target) || target;
        return element instanceof Element && element.closest("turbo-frame, html") == this.element;
    }
}
class $dae3d0e46d061380$var$LinkClickObserver {
    started = false;
    constructor(delegate, eventTarget){
        this.delegate = delegate;
        this.eventTarget = eventTarget;
    }
    start() {
        if (!this.started) {
            this.eventTarget.addEventListener("click", this.clickCaptured, true);
            this.started = true;
        }
    }
    stop() {
        if (this.started) {
            this.eventTarget.removeEventListener("click", this.clickCaptured, true);
            this.started = false;
        }
    }
    clickCaptured = ()=>{
        this.eventTarget.removeEventListener("click", this.clickBubbled, false);
        this.eventTarget.addEventListener("click", this.clickBubbled, false);
    };
    clickBubbled = (event)=>{
        if (event instanceof MouseEvent && this.clickEventIsSignificant(event)) {
            const target = event.composedPath && event.composedPath()[0] || event.target;
            const link = $dae3d0e46d061380$var$findLinkFromClickTarget(target);
            if (link && $dae3d0e46d061380$var$doesNotTargetIFrame(link.target)) {
                const location1 = $dae3d0e46d061380$var$getLocationForLink(link);
                if (this.delegate.willFollowLinkToLocation(link, location1, event)) {
                    event.preventDefault();
                    this.delegate.followedLinkToLocation(link, location1);
                }
            }
        }
    };
    clickEventIsSignificant(event) {
        return !(event.target && event.target.isContentEditable || event.defaultPrevented || event.which > 1 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey);
    }
}
class $dae3d0e46d061380$var$FormLinkClickObserver {
    constructor(delegate, element){
        this.delegate = delegate;
        this.linkInterceptor = new $dae3d0e46d061380$var$LinkClickObserver(this, element);
    }
    start() {
        this.linkInterceptor.start();
    }
    stop() {
        this.linkInterceptor.stop();
    }
    // Link hover observer delegate
    canPrefetchRequestToLocation(link, location1) {
        return false;
    }
    prefetchAndCacheRequestToLocation(link, location1) {
        return;
    }
    // Link click observer delegate
    willFollowLinkToLocation(link, location1, originalEvent) {
        return this.delegate.willSubmitFormLinkToLocation(link, location1, originalEvent) && (link.hasAttribute("data-turbo-method") || link.hasAttribute("data-turbo-stream"));
    }
    followedLinkToLocation(link, location1) {
        const form = document.createElement("form");
        const type = "hidden";
        for (const [name, value] of location1.searchParams)form.append(Object.assign(document.createElement("input"), {
            type: type,
            name: name,
            value: value
        }));
        const action = Object.assign(location1, {
            search: ""
        });
        form.setAttribute("data-turbo", "true");
        form.setAttribute("action", action.href);
        form.setAttribute("hidden", "");
        const method = link.getAttribute("data-turbo-method");
        if (method) form.setAttribute("method", method);
        const turboFrame = link.getAttribute("data-turbo-frame");
        if (turboFrame) form.setAttribute("data-turbo-frame", turboFrame);
        const turboAction = $dae3d0e46d061380$var$getVisitAction(link);
        if (turboAction) form.setAttribute("data-turbo-action", turboAction);
        const turboConfirm = link.getAttribute("data-turbo-confirm");
        if (turboConfirm) form.setAttribute("data-turbo-confirm", turboConfirm);
        const turboStream = link.hasAttribute("data-turbo-stream");
        if (turboStream) form.setAttribute("data-turbo-stream", "");
        this.delegate.submittedFormLinkToLocation(link, location1, form);
        document.body.appendChild(form);
        form.addEventListener("turbo:submit-end", ()=>form.remove(), {
            once: true
        });
        requestAnimationFrame(()=>form.requestSubmit());
    }
}
class $dae3d0e46d061380$var$Bardo {
    static async preservingPermanentElements(delegate, permanentElementMap, callback) {
        const bardo = new this(delegate, permanentElementMap);
        bardo.enter();
        await callback();
        bardo.leave();
    }
    constructor(delegate, permanentElementMap){
        this.delegate = delegate;
        this.permanentElementMap = permanentElementMap;
    }
    enter() {
        for(const id in this.permanentElementMap){
            const [currentPermanentElement, newPermanentElement] = this.permanentElementMap[id];
            this.delegate.enteringBardo(currentPermanentElement, newPermanentElement);
            this.replaceNewPermanentElementWithPlaceholder(newPermanentElement);
        }
    }
    leave() {
        for(const id in this.permanentElementMap){
            const [currentPermanentElement] = this.permanentElementMap[id];
            this.replaceCurrentPermanentElementWithClone(currentPermanentElement);
            this.replacePlaceholderWithPermanentElement(currentPermanentElement);
            this.delegate.leavingBardo(currentPermanentElement);
        }
    }
    replaceNewPermanentElementWithPlaceholder(permanentElement) {
        const placeholder = $dae3d0e46d061380$var$createPlaceholderForPermanentElement(permanentElement);
        permanentElement.replaceWith(placeholder);
    }
    replaceCurrentPermanentElementWithClone(permanentElement) {
        const clone = permanentElement.cloneNode(true);
        permanentElement.replaceWith(clone);
    }
    replacePlaceholderWithPermanentElement(permanentElement) {
        const placeholder = this.getPlaceholderById(permanentElement.id);
        placeholder?.replaceWith(permanentElement);
    }
    getPlaceholderById(id) {
        return this.placeholders.find((element)=>element.content == id);
    }
    get placeholders() {
        return [
            ...document.querySelectorAll("meta[name=turbo-permanent-placeholder][content]")
        ];
    }
}
function $dae3d0e46d061380$var$createPlaceholderForPermanentElement(permanentElement) {
    const element = document.createElement("meta");
    element.setAttribute("name", "turbo-permanent-placeholder");
    element.setAttribute("content", permanentElement.id);
    return element;
}
class $dae3d0e46d061380$var$Renderer {
    #activeElement = null;
    constructor(currentSnapshot, newSnapshot, renderElement, isPreview, willRender = true){
        this.currentSnapshot = currentSnapshot;
        this.newSnapshot = newSnapshot;
        this.isPreview = isPreview;
        this.willRender = willRender;
        this.renderElement = renderElement;
        this.promise = new Promise((resolve, reject)=>this.resolvingFunctions = {
                resolve: resolve,
                reject: reject
            });
    }
    get shouldRender() {
        return true;
    }
    get shouldAutofocus() {
        return true;
    }
    get reloadReason() {
        return;
    }
    prepareToRender() {
        return;
    }
    render() {
    // Abstract method
    }
    finishRendering() {
        if (this.resolvingFunctions) {
            this.resolvingFunctions.resolve();
            delete this.resolvingFunctions;
        }
    }
    async preservingPermanentElements(callback) {
        await $dae3d0e46d061380$var$Bardo.preservingPermanentElements(this, this.permanentElementMap, callback);
    }
    focusFirstAutofocusableElement() {
        if (this.shouldAutofocus) {
            const element = this.connectedSnapshot.firstAutofocusableElement;
            if (element) element.focus();
        }
    }
    // Bardo delegate
    enteringBardo(currentPermanentElement) {
        if (this.#activeElement) return;
        if (currentPermanentElement.contains(this.currentSnapshot.activeElement)) this.#activeElement = this.currentSnapshot.activeElement;
    }
    leavingBardo(currentPermanentElement) {
        if (currentPermanentElement.contains(this.#activeElement) && this.#activeElement instanceof HTMLElement) {
            this.#activeElement.focus();
            this.#activeElement = null;
        }
    }
    get connectedSnapshot() {
        return this.newSnapshot.isConnected ? this.newSnapshot : this.currentSnapshot;
    }
    get currentElement() {
        return this.currentSnapshot.element;
    }
    get newElement() {
        return this.newSnapshot.element;
    }
    get permanentElementMap() {
        return this.currentSnapshot.getPermanentElementMapForSnapshot(this.newSnapshot);
    }
    get renderMethod() {
        return "replace";
    }
}
class $dae3d0e46d061380$export$f09c4165d16bb2b7 extends $dae3d0e46d061380$var$Renderer {
    static renderElement(currentElement, newElement) {
        const destinationRange = document.createRange();
        destinationRange.selectNodeContents(currentElement);
        destinationRange.deleteContents();
        const frameElement = newElement;
        const sourceRange = frameElement.ownerDocument?.createRange();
        if (sourceRange) {
            sourceRange.selectNodeContents(frameElement);
            currentElement.appendChild(sourceRange.extractContents());
        }
    }
    constructor(delegate, currentSnapshot, newSnapshot, renderElement, isPreview, willRender = true){
        super(currentSnapshot, newSnapshot, renderElement, isPreview, willRender);
        this.delegate = delegate;
    }
    get shouldRender() {
        return true;
    }
    async render() {
        await $dae3d0e46d061380$var$nextRepaint();
        this.preservingPermanentElements(()=>{
            this.loadFrameElement();
        });
        this.scrollFrameIntoView();
        await $dae3d0e46d061380$var$nextRepaint();
        this.focusFirstAutofocusableElement();
        await $dae3d0e46d061380$var$nextRepaint();
        this.activateScriptElements();
    }
    loadFrameElement() {
        this.delegate.willRenderFrame(this.currentElement, this.newElement);
        this.renderElement(this.currentElement, this.newElement);
    }
    scrollFrameIntoView() {
        if (this.currentElement.autoscroll || this.newElement.autoscroll) {
            const element = this.currentElement.firstElementChild;
            const block = $dae3d0e46d061380$var$readScrollLogicalPosition(this.currentElement.getAttribute("data-autoscroll-block"), "end");
            const behavior = $dae3d0e46d061380$var$readScrollBehavior(this.currentElement.getAttribute("data-autoscroll-behavior"), "auto");
            if (element) {
                element.scrollIntoView({
                    block: block,
                    behavior: behavior
                });
                return true;
            }
        }
        return false;
    }
    activateScriptElements() {
        for (const inertScriptElement of this.newScriptElements){
            const activatedScriptElement = $dae3d0e46d061380$var$activateScriptElement(inertScriptElement);
            inertScriptElement.replaceWith(activatedScriptElement);
        }
    }
    get newScriptElements() {
        return this.currentElement.querySelectorAll("script");
    }
}
function $dae3d0e46d061380$var$readScrollLogicalPosition(value, defaultValue) {
    if (value == "end" || value == "start" || value == "center" || value == "nearest") return value;
    else return defaultValue;
}
function $dae3d0e46d061380$var$readScrollBehavior(value, defaultValue) {
    if (value == "auto" || value == "smooth") return value;
    else return defaultValue;
}
class $dae3d0e46d061380$var$ProgressBar {
    static animationDuration = 300 /*ms*/ ;
    static get defaultCSS() {
        return $dae3d0e46d061380$var$unindent`
      .turbo-progress-bar {
        position: fixed;
        display: block;
        top: 0;
        left: 0;
        height: 3px;
        background: #0076ff;
        z-index: 2147483647;
        transition:
          width ${$dae3d0e46d061380$var$ProgressBar.animationDuration}ms ease-out,
          opacity ${$dae3d0e46d061380$var$ProgressBar.animationDuration / 2}ms ${$dae3d0e46d061380$var$ProgressBar.animationDuration / 2}ms ease-in;
        transform: translate3d(0, 0, 0);
      }
    `;
    }
    hiding = false;
    value = 0;
    visible = false;
    constructor(){
        this.stylesheetElement = this.createStylesheetElement();
        this.progressElement = this.createProgressElement();
        this.installStylesheetElement();
        this.setValue(0);
    }
    show() {
        if (!this.visible) {
            this.visible = true;
            this.installProgressElement();
            this.startTrickling();
        }
    }
    hide() {
        if (this.visible && !this.hiding) {
            this.hiding = true;
            this.fadeProgressElement(()=>{
                this.uninstallProgressElement();
                this.stopTrickling();
                this.visible = false;
                this.hiding = false;
            });
        }
    }
    setValue(value) {
        this.value = value;
        this.refresh();
    }
    // Private
    installStylesheetElement() {
        document.head.insertBefore(this.stylesheetElement, document.head.firstChild);
    }
    installProgressElement() {
        this.progressElement.style.width = "0";
        this.progressElement.style.opacity = "1";
        document.documentElement.insertBefore(this.progressElement, document.body);
        this.refresh();
    }
    fadeProgressElement(callback) {
        this.progressElement.style.opacity = "0";
        setTimeout(callback, $dae3d0e46d061380$var$ProgressBar.animationDuration * 1.5);
    }
    uninstallProgressElement() {
        if (this.progressElement.parentNode) document.documentElement.removeChild(this.progressElement);
    }
    startTrickling() {
        if (!this.trickleInterval) this.trickleInterval = window.setInterval(this.trickle, $dae3d0e46d061380$var$ProgressBar.animationDuration);
    }
    stopTrickling() {
        window.clearInterval(this.trickleInterval);
        delete this.trickleInterval;
    }
    trickle = ()=>{
        this.setValue(this.value + Math.random() / 100);
    };
    refresh() {
        requestAnimationFrame(()=>{
            this.progressElement.style.width = `${10 + this.value * 90}%`;
        });
    }
    createStylesheetElement() {
        const element = document.createElement("style");
        element.type = "text/css";
        element.textContent = $dae3d0e46d061380$var$ProgressBar.defaultCSS;
        if (this.cspNonce) element.nonce = this.cspNonce;
        return element;
    }
    createProgressElement() {
        const element = document.createElement("div");
        element.className = "turbo-progress-bar";
        return element;
    }
    get cspNonce() {
        return $dae3d0e46d061380$var$getMetaContent("csp-nonce");
    }
}
class $dae3d0e46d061380$var$HeadSnapshot extends $dae3d0e46d061380$var$Snapshot {
    detailsByOuterHTML = this.children.filter((element)=>!$dae3d0e46d061380$var$elementIsNoscript(element)).map((element)=>$dae3d0e46d061380$var$elementWithoutNonce(element)).reduce((result, element)=>{
        const { outerHTML: outerHTML } = element;
        const details = outerHTML in result ? result[outerHTML] : {
            type: $dae3d0e46d061380$var$elementType(element),
            tracked: $dae3d0e46d061380$var$elementIsTracked(element),
            elements: []
        };
        return {
            ...result,
            [outerHTML]: {
                ...details,
                elements: [
                    ...details.elements,
                    element
                ]
            }
        };
    }, {});
    get trackedElementSignature() {
        return Object.keys(this.detailsByOuterHTML).filter((outerHTML)=>this.detailsByOuterHTML[outerHTML].tracked).join("");
    }
    getScriptElementsNotInSnapshot(snapshot) {
        return this.getElementsMatchingTypeNotInSnapshot("script", snapshot);
    }
    getStylesheetElementsNotInSnapshot(snapshot) {
        return this.getElementsMatchingTypeNotInSnapshot("stylesheet", snapshot);
    }
    getElementsMatchingTypeNotInSnapshot(matchedType, snapshot) {
        return Object.keys(this.detailsByOuterHTML).filter((outerHTML)=>!(outerHTML in snapshot.detailsByOuterHTML)).map((outerHTML)=>this.detailsByOuterHTML[outerHTML]).filter(({ type: type })=>type == matchedType).map(({ elements: [element] })=>element);
    }
    get provisionalElements() {
        return Object.keys(this.detailsByOuterHTML).reduce((result, outerHTML)=>{
            const { type: type, tracked: tracked, elements: elements } = this.detailsByOuterHTML[outerHTML];
            if (type == null && !tracked) return [
                ...result,
                ...elements
            ];
            else if (elements.length > 1) return [
                ...result,
                ...elements.slice(1)
            ];
            else return result;
        }, []);
    }
    getMetaValue(name) {
        const element = this.findMetaElementByName(name);
        return element ? element.getAttribute("content") : null;
    }
    findMetaElementByName(name) {
        return Object.keys(this.detailsByOuterHTML).reduce((result, outerHTML)=>{
            const { elements: [element] } = this.detailsByOuterHTML[outerHTML];
            return $dae3d0e46d061380$var$elementIsMetaElementWithName(element, name) ? element : result;
        }, 0);
    }
}
function $dae3d0e46d061380$var$elementType(element) {
    if ($dae3d0e46d061380$var$elementIsScript(element)) return "script";
    else if ($dae3d0e46d061380$var$elementIsStylesheet(element)) return "stylesheet";
}
function $dae3d0e46d061380$var$elementIsTracked(element) {
    return element.getAttribute("data-turbo-track") == "reload";
}
function $dae3d0e46d061380$var$elementIsScript(element) {
    const tagName = element.localName;
    return tagName == "script";
}
function $dae3d0e46d061380$var$elementIsNoscript(element) {
    const tagName = element.localName;
    return tagName == "noscript";
}
function $dae3d0e46d061380$var$elementIsStylesheet(element) {
    const tagName = element.localName;
    return tagName == "style" || tagName == "link" && element.getAttribute("rel") == "stylesheet";
}
function $dae3d0e46d061380$var$elementIsMetaElementWithName(element, name) {
    const tagName = element.localName;
    return tagName == "meta" && element.getAttribute("name") == name;
}
function $dae3d0e46d061380$var$elementWithoutNonce(element) {
    if (element.hasAttribute("nonce")) element.setAttribute("nonce", "");
    return element;
}
class $dae3d0e46d061380$export$4f11b95e473fd113 extends $dae3d0e46d061380$var$Snapshot {
    static fromHTMLString(html = "") {
        return this.fromDocument($dae3d0e46d061380$var$parseHTMLDocument(html));
    }
    static fromElement(element) {
        return this.fromDocument(element.ownerDocument);
    }
    static fromDocument({ documentElement: documentElement, body: body, head: head }) {
        return new this(documentElement, body, new $dae3d0e46d061380$var$HeadSnapshot(head));
    }
    constructor(documentElement, body, headSnapshot){
        super(body);
        this.documentElement = documentElement;
        this.headSnapshot = headSnapshot;
    }
    clone() {
        const clonedElement = this.element.cloneNode(true);
        const selectElements = this.element.querySelectorAll("select");
        const clonedSelectElements = clonedElement.querySelectorAll("select");
        for (const [index, source] of selectElements.entries()){
            const clone = clonedSelectElements[index];
            for (const option of clone.selectedOptions)option.selected = false;
            for (const option of source.selectedOptions)clone.options[option.index].selected = true;
        }
        for (const clonedPasswordInput of clonedElement.querySelectorAll('input[type="password"]'))clonedPasswordInput.value = "";
        return new $dae3d0e46d061380$export$4f11b95e473fd113(this.documentElement, clonedElement, this.headSnapshot);
    }
    get lang() {
        return this.documentElement.getAttribute("lang");
    }
    get headElement() {
        return this.headSnapshot.element;
    }
    get rootLocation() {
        const root = this.getSetting("root") ?? "/";
        return $dae3d0e46d061380$var$expandURL(root);
    }
    get cacheControlValue() {
        return this.getSetting("cache-control");
    }
    get isPreviewable() {
        return this.cacheControlValue != "no-preview";
    }
    get isCacheable() {
        return this.cacheControlValue != "no-cache";
    }
    get isVisitable() {
        return this.getSetting("visit-control") != "reload";
    }
    get prefersViewTransitions() {
        return this.headSnapshot.getMetaValue("view-transition") === "same-origin";
    }
    get shouldMorphPage() {
        return this.getSetting("refresh-method") === "morph";
    }
    get shouldPreserveScrollPosition() {
        return this.getSetting("refresh-scroll") === "preserve";
    }
    // Private
    getSetting(name) {
        return this.headSnapshot.getMetaValue(`turbo-${name}`);
    }
}
class $dae3d0e46d061380$var$ViewTransitioner {
    #viewTransitionStarted = false;
    #lastOperation = Promise.resolve();
    renderChange(useViewTransition, render) {
        if (useViewTransition && this.viewTransitionsAvailable && !this.#viewTransitionStarted) {
            this.#viewTransitionStarted = true;
            this.#lastOperation = this.#lastOperation.then(async ()=>{
                await document.startViewTransition(render).finished;
            });
        } else this.#lastOperation = this.#lastOperation.then(render);
        return this.#lastOperation;
    }
    get viewTransitionsAvailable() {
        return document.startViewTransition;
    }
}
const $dae3d0e46d061380$var$defaultOptions = {
    action: "advance",
    historyChanged: false,
    visitCachedSnapshot: ()=>{},
    willRender: true,
    updateHistory: true,
    shouldCacheSnapshot: true,
    acceptsStreamResponse: false
};
const $dae3d0e46d061380$var$TimingMetric = {
    visitStart: "visitStart",
    requestStart: "requestStart",
    requestEnd: "requestEnd",
    visitEnd: "visitEnd"
};
const $dae3d0e46d061380$var$VisitState = {
    initialized: "initialized",
    started: "started",
    canceled: "canceled",
    failed: "failed",
    completed: "completed"
};
const $dae3d0e46d061380$var$SystemStatusCode = {
    networkFailure: 0,
    timeoutFailure: -1,
    contentTypeMismatch: -2
};
const $dae3d0e46d061380$var$Direction = {
    advance: "forward",
    restore: "back",
    replace: "none"
};
class $dae3d0e46d061380$var$Visit {
    identifier = $dae3d0e46d061380$var$uuid() // Required by turbo-ios
    ;
    timingMetrics = {};
    followedRedirect = false;
    historyChanged = false;
    scrolled = false;
    shouldCacheSnapshot = true;
    acceptsStreamResponse = false;
    snapshotCached = false;
    state = $dae3d0e46d061380$var$VisitState.initialized;
    viewTransitioner = new $dae3d0e46d061380$var$ViewTransitioner();
    constructor(delegate, location1, restorationIdentifier, options = {}){
        this.delegate = delegate;
        this.location = location1;
        this.restorationIdentifier = restorationIdentifier || $dae3d0e46d061380$var$uuid();
        const { action: action, historyChanged: historyChanged, referrer: referrer, snapshot: snapshot, snapshotHTML: snapshotHTML, response: response, visitCachedSnapshot: visitCachedSnapshot, willRender: willRender, updateHistory: updateHistory, shouldCacheSnapshot: shouldCacheSnapshot, acceptsStreamResponse: acceptsStreamResponse, direction: direction } = {
            ...$dae3d0e46d061380$var$defaultOptions,
            ...options
        };
        this.action = action;
        this.historyChanged = historyChanged;
        this.referrer = referrer;
        this.snapshot = snapshot;
        this.snapshotHTML = snapshotHTML;
        this.response = response;
        this.isSamePage = this.delegate.locationWithActionIsSamePage(this.location, this.action);
        this.isPageRefresh = this.view.isPageRefresh(this);
        this.visitCachedSnapshot = visitCachedSnapshot;
        this.willRender = willRender;
        this.updateHistory = updateHistory;
        this.scrolled = !willRender;
        this.shouldCacheSnapshot = shouldCacheSnapshot;
        this.acceptsStreamResponse = acceptsStreamResponse;
        this.direction = direction || $dae3d0e46d061380$var$Direction[action];
    }
    get adapter() {
        return this.delegate.adapter;
    }
    get view() {
        return this.delegate.view;
    }
    get history() {
        return this.delegate.history;
    }
    get restorationData() {
        return this.history.getRestorationDataForIdentifier(this.restorationIdentifier);
    }
    get silent() {
        return this.isSamePage;
    }
    start() {
        if (this.state == $dae3d0e46d061380$var$VisitState.initialized) {
            this.recordTimingMetric($dae3d0e46d061380$var$TimingMetric.visitStart);
            this.state = $dae3d0e46d061380$var$VisitState.started;
            this.adapter.visitStarted(this);
            this.delegate.visitStarted(this);
        }
    }
    cancel() {
        if (this.state == $dae3d0e46d061380$var$VisitState.started) {
            if (this.request) this.request.cancel();
            this.cancelRender();
            this.state = $dae3d0e46d061380$var$VisitState.canceled;
        }
    }
    complete() {
        if (this.state == $dae3d0e46d061380$var$VisitState.started) {
            this.recordTimingMetric($dae3d0e46d061380$var$TimingMetric.visitEnd);
            this.adapter.visitCompleted(this);
            this.state = $dae3d0e46d061380$var$VisitState.completed;
            this.followRedirect();
            if (!this.followedRedirect) this.delegate.visitCompleted(this);
        }
    }
    fail() {
        if (this.state == $dae3d0e46d061380$var$VisitState.started) {
            this.state = $dae3d0e46d061380$var$VisitState.failed;
            this.adapter.visitFailed(this);
            this.delegate.visitCompleted(this);
        }
    }
    changeHistory() {
        if (!this.historyChanged && this.updateHistory) {
            const actionForHistory = this.location.href === this.referrer?.href ? "replace" : this.action;
            const method = $dae3d0e46d061380$var$getHistoryMethodForAction(actionForHistory);
            this.history.update(method, this.location, this.restorationIdentifier);
            this.historyChanged = true;
        }
    }
    issueRequest() {
        if (this.hasPreloadedResponse()) this.simulateRequest();
        else if (this.shouldIssueRequest() && !this.request) {
            this.request = new $dae3d0e46d061380$export$319b96fe59834522(this, $dae3d0e46d061380$export$76f6869e29ff06ee.get, this.location);
            this.request.perform();
        }
    }
    simulateRequest() {
        if (this.response) {
            this.startRequest();
            this.recordResponse();
            this.finishRequest();
        }
    }
    startRequest() {
        this.recordTimingMetric($dae3d0e46d061380$var$TimingMetric.requestStart);
        this.adapter.visitRequestStarted(this);
    }
    recordResponse(response = this.response) {
        this.response = response;
        if (response) {
            const { statusCode: statusCode } = response;
            if ($dae3d0e46d061380$var$isSuccessful(statusCode)) this.adapter.visitRequestCompleted(this);
            else this.adapter.visitRequestFailedWithStatusCode(this, statusCode);
        }
    }
    finishRequest() {
        this.recordTimingMetric($dae3d0e46d061380$var$TimingMetric.requestEnd);
        this.adapter.visitRequestFinished(this);
    }
    loadResponse() {
        if (this.response) {
            const { statusCode: statusCode, responseHTML: responseHTML } = this.response;
            this.render(async ()=>{
                if (this.shouldCacheSnapshot) this.cacheSnapshot();
                if (this.view.renderPromise) await this.view.renderPromise;
                if ($dae3d0e46d061380$var$isSuccessful(statusCode) && responseHTML != null) {
                    const snapshot = $dae3d0e46d061380$export$4f11b95e473fd113.fromHTMLString(responseHTML);
                    await this.renderPageSnapshot(snapshot, false);
                    this.adapter.visitRendered(this);
                    this.complete();
                } else {
                    await this.view.renderError($dae3d0e46d061380$export$4f11b95e473fd113.fromHTMLString(responseHTML), this);
                    this.adapter.visitRendered(this);
                    this.fail();
                }
            });
        }
    }
    getCachedSnapshot() {
        const snapshot = this.view.getCachedSnapshotForLocation(this.location) || this.getPreloadedSnapshot();
        if (snapshot && (!$dae3d0e46d061380$var$getAnchor(this.location) || snapshot.hasAnchor($dae3d0e46d061380$var$getAnchor(this.location)))) {
            if (this.action == "restore" || snapshot.isPreviewable) return snapshot;
        }
    }
    getPreloadedSnapshot() {
        if (this.snapshotHTML) return $dae3d0e46d061380$export$4f11b95e473fd113.fromHTMLString(this.snapshotHTML);
    }
    hasCachedSnapshot() {
        return this.getCachedSnapshot() != null;
    }
    loadCachedSnapshot() {
        const snapshot = this.getCachedSnapshot();
        if (snapshot) {
            const isPreview = this.shouldIssueRequest();
            this.render(async ()=>{
                this.cacheSnapshot();
                if (this.isSamePage || this.isPageRefresh) this.adapter.visitRendered(this);
                else {
                    if (this.view.renderPromise) await this.view.renderPromise;
                    await this.renderPageSnapshot(snapshot, isPreview);
                    this.adapter.visitRendered(this);
                    if (!isPreview) this.complete();
                }
            });
        }
    }
    followRedirect() {
        if (this.redirectedToLocation && !this.followedRedirect && this.response?.redirected) {
            this.adapter.visitProposedToLocation(this.redirectedToLocation, {
                action: "replace",
                response: this.response,
                shouldCacheSnapshot: false,
                willRender: false
            });
            this.followedRedirect = true;
        }
    }
    goToSamePageAnchor() {
        if (this.isSamePage) this.render(async ()=>{
            this.cacheSnapshot();
            this.performScroll();
            this.changeHistory();
            this.adapter.visitRendered(this);
        });
    }
    // Fetch request delegate
    prepareRequest(request) {
        if (this.acceptsStreamResponse) request.acceptResponseType($dae3d0e46d061380$var$StreamMessage.contentType);
    }
    requestStarted() {
        this.startRequest();
    }
    requestPreventedHandlingResponse(_request, _response) {}
    async requestSucceededWithResponse(request, response) {
        const responseHTML = await response.responseHTML;
        const { redirected: redirected, statusCode: statusCode } = response;
        if (responseHTML == undefined) this.recordResponse({
            statusCode: $dae3d0e46d061380$var$SystemStatusCode.contentTypeMismatch,
            redirected: redirected
        });
        else {
            this.redirectedToLocation = response.redirected ? response.location : undefined;
            this.recordResponse({
                statusCode: statusCode,
                responseHTML: responseHTML,
                redirected: redirected
            });
        }
    }
    async requestFailedWithResponse(request, response) {
        const responseHTML = await response.responseHTML;
        const { redirected: redirected, statusCode: statusCode } = response;
        if (responseHTML == undefined) this.recordResponse({
            statusCode: $dae3d0e46d061380$var$SystemStatusCode.contentTypeMismatch,
            redirected: redirected
        });
        else this.recordResponse({
            statusCode: statusCode,
            responseHTML: responseHTML,
            redirected: redirected
        });
    }
    requestErrored(_request, _error) {
        this.recordResponse({
            statusCode: $dae3d0e46d061380$var$SystemStatusCode.networkFailure,
            redirected: false
        });
    }
    requestFinished() {
        this.finishRequest();
    }
    // Scrolling
    performScroll() {
        if (!this.scrolled && !this.view.forceReloaded && !this.view.shouldPreserveScrollPosition(this)) {
            if (this.action == "restore") this.scrollToRestoredPosition() || this.scrollToAnchor() || this.view.scrollToTop();
            else this.scrollToAnchor() || this.view.scrollToTop();
            if (this.isSamePage) this.delegate.visitScrolledToSamePageLocation(this.view.lastRenderedLocation, this.location);
            this.scrolled = true;
        }
    }
    scrollToRestoredPosition() {
        const { scrollPosition: scrollPosition } = this.restorationData;
        if (scrollPosition) {
            this.view.scrollToPosition(scrollPosition);
            return true;
        }
    }
    scrollToAnchor() {
        const anchor = $dae3d0e46d061380$var$getAnchor(this.location);
        if (anchor != null) {
            this.view.scrollToAnchor(anchor);
            return true;
        }
    }
    // Instrumentation
    recordTimingMetric(metric) {
        this.timingMetrics[metric] = new Date().getTime();
    }
    getTimingMetrics() {
        return {
            ...this.timingMetrics
        };
    }
    // Private
    getHistoryMethodForAction(action) {
        switch(action){
            case "replace":
                return history.replaceState;
            case "advance":
            case "restore":
                return history.pushState;
        }
    }
    hasPreloadedResponse() {
        return typeof this.response == "object";
    }
    shouldIssueRequest() {
        if (this.isSamePage) return false;
        else if (this.action == "restore") return !this.hasCachedSnapshot();
        else return this.willRender;
    }
    cacheSnapshot() {
        if (!this.snapshotCached) {
            this.view.cacheSnapshot(this.snapshot).then((snapshot)=>snapshot && this.visitCachedSnapshot(snapshot));
            this.snapshotCached = true;
        }
    }
    async render(callback) {
        this.cancelRender();
        this.frame = await $dae3d0e46d061380$var$nextRepaint();
        await callback();
        delete this.frame;
    }
    async renderPageSnapshot(snapshot, isPreview) {
        await this.viewTransitioner.renderChange(this.view.shouldTransitionTo(snapshot), async ()=>{
            await this.view.renderPage(snapshot, isPreview, this.willRender, this);
            this.performScroll();
        });
    }
    cancelRender() {
        if (this.frame) {
            cancelAnimationFrame(this.frame);
            delete this.frame;
        }
    }
}
function $dae3d0e46d061380$var$isSuccessful(statusCode) {
    return statusCode >= 200 && statusCode < 300;
}
class $dae3d0e46d061380$var$BrowserAdapter {
    progressBar = new $dae3d0e46d061380$var$ProgressBar();
    constructor(session){
        this.session = session;
    }
    visitProposedToLocation(location1, options) {
        if ($dae3d0e46d061380$var$locationIsVisitable(location1, this.navigator.rootLocation)) this.navigator.startVisit(location1, options?.restorationIdentifier || $dae3d0e46d061380$var$uuid(), options);
        else window.location.href = location1.toString();
    }
    visitStarted(visit) {
        this.location = visit.location;
        visit.loadCachedSnapshot();
        visit.issueRequest();
        visit.goToSamePageAnchor();
    }
    visitRequestStarted(visit) {
        this.progressBar.setValue(0);
        if (visit.hasCachedSnapshot() || visit.action != "restore") this.showVisitProgressBarAfterDelay();
        else this.showProgressBar();
    }
    visitRequestCompleted(visit) {
        visit.loadResponse();
    }
    visitRequestFailedWithStatusCode(visit, statusCode) {
        switch(statusCode){
            case $dae3d0e46d061380$var$SystemStatusCode.networkFailure:
            case $dae3d0e46d061380$var$SystemStatusCode.timeoutFailure:
            case $dae3d0e46d061380$var$SystemStatusCode.contentTypeMismatch:
                return this.reload({
                    reason: "request_failed",
                    context: {
                        statusCode: statusCode
                    }
                });
            default:
                return visit.loadResponse();
        }
    }
    visitRequestFinished(_visit) {}
    visitCompleted(_visit) {
        this.progressBar.setValue(1);
        this.hideVisitProgressBar();
    }
    pageInvalidated(reason) {
        this.reload(reason);
    }
    visitFailed(_visit) {
        this.progressBar.setValue(1);
        this.hideVisitProgressBar();
    }
    visitRendered(_visit) {}
    // Form Submission Delegate
    formSubmissionStarted(_formSubmission) {
        this.progressBar.setValue(0);
        this.showFormProgressBarAfterDelay();
    }
    formSubmissionFinished(_formSubmission) {
        this.progressBar.setValue(1);
        this.hideFormProgressBar();
    }
    // Private
    showVisitProgressBarAfterDelay() {
        this.visitProgressBarTimeout = window.setTimeout(this.showProgressBar, this.session.progressBarDelay);
    }
    hideVisitProgressBar() {
        this.progressBar.hide();
        if (this.visitProgressBarTimeout != null) {
            window.clearTimeout(this.visitProgressBarTimeout);
            delete this.visitProgressBarTimeout;
        }
    }
    showFormProgressBarAfterDelay() {
        if (this.formProgressBarTimeout == null) this.formProgressBarTimeout = window.setTimeout(this.showProgressBar, this.session.progressBarDelay);
    }
    hideFormProgressBar() {
        this.progressBar.hide();
        if (this.formProgressBarTimeout != null) {
            window.clearTimeout(this.formProgressBarTimeout);
            delete this.formProgressBarTimeout;
        }
    }
    showProgressBar = ()=>{
        this.progressBar.show();
    };
    reload(reason) {
        $dae3d0e46d061380$var$dispatch("turbo:reload", {
            detail: reason
        });
        window.location.href = this.location?.toString() || window.location.href;
    }
    get navigator() {
        return this.session.navigator;
    }
}
class $dae3d0e46d061380$var$CacheObserver {
    selector = "[data-turbo-temporary]";
    deprecatedSelector = "[data-turbo-cache=false]";
    started = false;
    start() {
        if (!this.started) {
            this.started = true;
            addEventListener("turbo:before-cache", this.removeTemporaryElements, false);
        }
    }
    stop() {
        if (this.started) {
            this.started = false;
            removeEventListener("turbo:before-cache", this.removeTemporaryElements, false);
        }
    }
    removeTemporaryElements = (_event)=>{
        for (const element of this.temporaryElements)element.remove();
    };
    get temporaryElements() {
        return [
            ...document.querySelectorAll(this.selector),
            ...this.temporaryElementsWithDeprecation
        ];
    }
    get temporaryElementsWithDeprecation() {
        const elements = document.querySelectorAll(this.deprecatedSelector);
        if (elements.length) console.warn(`The ${this.deprecatedSelector} selector is deprecated and will be removed in a future version. Use ${this.selector} instead.`);
        return [
            ...elements
        ];
    }
}
class $dae3d0e46d061380$var$FrameRedirector {
    constructor(session, element){
        this.session = session;
        this.element = element;
        this.linkInterceptor = new $dae3d0e46d061380$var$LinkInterceptor(this, element);
        this.formSubmitObserver = new $dae3d0e46d061380$var$FormSubmitObserver(this, element);
    }
    start() {
        this.linkInterceptor.start();
        this.formSubmitObserver.start();
    }
    stop() {
        this.linkInterceptor.stop();
        this.formSubmitObserver.stop();
    }
    // Link interceptor delegate
    shouldInterceptLinkClick(element, _location, _event) {
        return this.#shouldRedirect(element);
    }
    linkClickIntercepted(element, url, event) {
        const frame = this.#findFrameElement(element);
        if (frame) frame.delegate.linkClickIntercepted(element, url, event);
    }
    // Form submit observer delegate
    willSubmitForm(element, submitter) {
        return element.closest("turbo-frame") == null && this.#shouldSubmit(element, submitter) && this.#shouldRedirect(element, submitter);
    }
    formSubmitted(element, submitter) {
        const frame = this.#findFrameElement(element, submitter);
        if (frame) frame.delegate.formSubmitted(element, submitter);
    }
    #shouldSubmit(form, submitter) {
        const action = $dae3d0e46d061380$var$getAction$1(form, submitter);
        const meta = this.element.ownerDocument.querySelector(`meta[name="turbo-root"]`);
        const rootLocation = $dae3d0e46d061380$var$expandURL(meta?.content ?? "/");
        return this.#shouldRedirect(form, submitter) && $dae3d0e46d061380$var$locationIsVisitable(action, rootLocation);
    }
    #shouldRedirect(element, submitter) {
        const isNavigatable = element instanceof HTMLFormElement ? this.session.submissionIsNavigatable(element, submitter) : this.session.elementIsNavigatable(element);
        if (isNavigatable) {
            const frame = this.#findFrameElement(element, submitter);
            return frame ? frame != element.closest("turbo-frame") : false;
        } else return false;
    }
    #findFrameElement(element, submitter) {
        const id = submitter?.getAttribute("data-turbo-frame") || element.getAttribute("data-turbo-frame");
        if (id && id != "_top") {
            const frame = this.element.querySelector(`#${id}:not([disabled])`);
            if (frame instanceof $dae3d0e46d061380$export$f464a8f0ffa32c44) return frame;
        }
    }
}
class $dae3d0e46d061380$var$History {
    location;
    restorationIdentifier = $dae3d0e46d061380$var$uuid();
    restorationData = {};
    started = false;
    pageLoaded = false;
    currentIndex = 0;
    constructor(delegate){
        this.delegate = delegate;
    }
    start() {
        if (!this.started) {
            addEventListener("popstate", this.onPopState, false);
            addEventListener("load", this.onPageLoad, false);
            this.currentIndex = history.state?.turbo?.restorationIndex || 0;
            this.started = true;
            this.replace(new URL(window.location.href));
        }
    }
    stop() {
        if (this.started) {
            removeEventListener("popstate", this.onPopState, false);
            removeEventListener("load", this.onPageLoad, false);
            this.started = false;
        }
    }
    push(location1, restorationIdentifier) {
        this.update(history.pushState, location1, restorationIdentifier);
    }
    replace(location1, restorationIdentifier) {
        this.update(history.replaceState, location1, restorationIdentifier);
    }
    update(method, location1, restorationIdentifier = $dae3d0e46d061380$var$uuid()) {
        if (method === history.pushState) ++this.currentIndex;
        const state = {
            turbo: {
                restorationIdentifier: restorationIdentifier,
                restorationIndex: this.currentIndex
            }
        };
        method.call(history, state, "", location1.href);
        this.location = location1;
        this.restorationIdentifier = restorationIdentifier;
    }
    // Restoration data
    getRestorationDataForIdentifier(restorationIdentifier) {
        return this.restorationData[restorationIdentifier] || {};
    }
    updateRestorationData(additionalData) {
        const { restorationIdentifier: restorationIdentifier } = this;
        const restorationData = this.restorationData[restorationIdentifier];
        this.restorationData[restorationIdentifier] = {
            ...restorationData,
            ...additionalData
        };
    }
    // Scroll restoration
    assumeControlOfScrollRestoration() {
        if (!this.previousScrollRestoration) {
            this.previousScrollRestoration = history.scrollRestoration ?? "auto";
            history.scrollRestoration = "manual";
        }
    }
    relinquishControlOfScrollRestoration() {
        if (this.previousScrollRestoration) {
            history.scrollRestoration = this.previousScrollRestoration;
            delete this.previousScrollRestoration;
        }
    }
    // Event handlers
    onPopState = (event)=>{
        if (this.shouldHandlePopState()) {
            const { turbo: turbo } = event.state || {};
            if (turbo) {
                this.location = new URL(window.location.href);
                const { restorationIdentifier: restorationIdentifier, restorationIndex: restorationIndex } = turbo;
                this.restorationIdentifier = restorationIdentifier;
                const direction = restorationIndex > this.currentIndex ? "forward" : "back";
                this.delegate.historyPoppedToLocationWithRestorationIdentifierAndDirection(this.location, restorationIdentifier, direction);
                this.currentIndex = restorationIndex;
            }
        }
    };
    onPageLoad = async (_event)=>{
        await $dae3d0e46d061380$var$nextMicrotask();
        this.pageLoaded = true;
    };
    // Private
    shouldHandlePopState() {
        // Safari dispatches a popstate event after window's load event, ignore it
        return this.pageIsLoaded();
    }
    pageIsLoaded() {
        return this.pageLoaded || document.readyState == "complete";
    }
}
class $dae3d0e46d061380$var$LinkPrefetchObserver {
    started = false;
    #prefetchedLink = null;
    constructor(delegate, eventTarget){
        this.delegate = delegate;
        this.eventTarget = eventTarget;
    }
    start() {
        if (this.started) return;
        if (this.eventTarget.readyState === "loading") this.eventTarget.addEventListener("DOMContentLoaded", this.#enable, {
            once: true
        });
        else this.#enable();
    }
    stop() {
        if (!this.started) return;
        this.eventTarget.removeEventListener("mouseenter", this.#tryToPrefetchRequest, {
            capture: true,
            passive: true
        });
        this.eventTarget.removeEventListener("mouseleave", this.#cancelRequestIfObsolete, {
            capture: true,
            passive: true
        });
        this.eventTarget.removeEventListener("turbo:before-fetch-request", this.#tryToUsePrefetchedRequest, true);
        this.started = false;
    }
    #enable = ()=>{
        this.eventTarget.addEventListener("mouseenter", this.#tryToPrefetchRequest, {
            capture: true,
            passive: true
        });
        this.eventTarget.addEventListener("mouseleave", this.#cancelRequestIfObsolete, {
            capture: true,
            passive: true
        });
        this.eventTarget.addEventListener("turbo:before-fetch-request", this.#tryToUsePrefetchedRequest, true);
        this.started = true;
    };
    #tryToPrefetchRequest = (event)=>{
        if ($dae3d0e46d061380$var$getMetaContent("turbo-prefetch") === "false") return;
        const target = event.target;
        const isLink = target.matches && target.matches("a[href]:not([target^=_]):not([download])");
        if (isLink && this.#isPrefetchable(target)) {
            const link = target;
            const location1 = $dae3d0e46d061380$var$getLocationForLink(link);
            if (this.delegate.canPrefetchRequestToLocation(link, location1)) {
                this.#prefetchedLink = link;
                const fetchRequest = new $dae3d0e46d061380$export$319b96fe59834522(this, $dae3d0e46d061380$export$76f6869e29ff06ee.get, location1, new URLSearchParams(), target);
                $dae3d0e46d061380$var$prefetchCache.setLater(location1.toString(), fetchRequest, this.#cacheTtl);
            }
        }
    };
    #cancelRequestIfObsolete = (event)=>{
        if (event.target === this.#prefetchedLink) this.#cancelPrefetchRequest();
    };
    #cancelPrefetchRequest = ()=>{
        $dae3d0e46d061380$var$prefetchCache.clear();
        this.#prefetchedLink = null;
    };
    #tryToUsePrefetchedRequest = (event)=>{
        if (event.target.tagName !== "FORM" && event.detail.fetchOptions.method === "GET") {
            const cached = $dae3d0e46d061380$var$prefetchCache.get(event.detail.url.toString());
            if (cached) // User clicked link, use cache response
            event.detail.fetchRequest = cached;
            $dae3d0e46d061380$var$prefetchCache.clear();
        }
    };
    prepareRequest(request) {
        const link = request.target;
        request.headers["X-Sec-Purpose"] = "prefetch";
        const turboFrame = link.closest("turbo-frame");
        const turboFrameTarget = link.getAttribute("data-turbo-frame") || turboFrame?.getAttribute("target") || turboFrame?.id;
        if (turboFrameTarget && turboFrameTarget !== "_top") request.headers["Turbo-Frame"] = turboFrameTarget;
    }
    // Fetch request interface
    requestSucceededWithResponse() {}
    requestStarted(fetchRequest) {}
    requestErrored(fetchRequest) {}
    requestFinished(fetchRequest) {}
    requestPreventedHandlingResponse(fetchRequest, fetchResponse) {}
    requestFailedWithResponse(fetchRequest, fetchResponse) {}
    get #cacheTtl() {
        return Number($dae3d0e46d061380$var$getMetaContent("turbo-prefetch-cache-time")) || $dae3d0e46d061380$var$cacheTtl;
    }
    #isPrefetchable(link) {
        const href = link.getAttribute("href");
        if (!href) return false;
        if ($dae3d0e46d061380$var$unfetchableLink(link)) return false;
        if ($dae3d0e46d061380$var$linkToTheSamePage(link)) return false;
        if ($dae3d0e46d061380$var$linkOptsOut(link)) return false;
        if ($dae3d0e46d061380$var$nonSafeLink(link)) return false;
        if ($dae3d0e46d061380$var$eventPrevented(link)) return false;
        return true;
    }
}
const $dae3d0e46d061380$var$unfetchableLink = (link)=>{
    return link.origin !== document.location.origin || ![
        "http:",
        "https:"
    ].includes(link.protocol) || link.hasAttribute("target");
};
const $dae3d0e46d061380$var$linkToTheSamePage = (link)=>{
    return link.pathname + link.search === document.location.pathname + document.location.search || link.href.startsWith("#");
};
const $dae3d0e46d061380$var$linkOptsOut = (link)=>{
    if (link.getAttribute("data-turbo-prefetch") === "false") return true;
    if (link.getAttribute("data-turbo") === "false") return true;
    const turboPrefetchParent = $dae3d0e46d061380$var$findClosestRecursively(link, "[data-turbo-prefetch]");
    if (turboPrefetchParent && turboPrefetchParent.getAttribute("data-turbo-prefetch") === "false") return true;
    return false;
};
const $dae3d0e46d061380$var$nonSafeLink = (link)=>{
    const turboMethod = link.getAttribute("data-turbo-method");
    if (turboMethod && turboMethod.toLowerCase() !== "get") return true;
    if ($dae3d0e46d061380$var$isUJS(link)) return true;
    if (link.hasAttribute("data-turbo-confirm")) return true;
    if (link.hasAttribute("data-turbo-stream")) return true;
    return false;
};
const $dae3d0e46d061380$var$isUJS = (link)=>{
    return link.hasAttribute("data-remote") || link.hasAttribute("data-behavior") || link.hasAttribute("data-confirm") || link.hasAttribute("data-method");
};
const $dae3d0e46d061380$var$eventPrevented = (link)=>{
    const event = $dae3d0e46d061380$var$dispatch("turbo:before-prefetch", {
        target: link,
        cancelable: true
    });
    return event.defaultPrevented;
};
class $dae3d0e46d061380$var$Navigator {
    constructor(delegate){
        this.delegate = delegate;
    }
    proposeVisit(location1, options = {}) {
        if (this.delegate.allowsVisitingLocationWithAction(location1, options.action)) this.delegate.visitProposedToLocation(location1, options);
    }
    startVisit(locatable, restorationIdentifier, options = {}) {
        this.stop();
        this.currentVisit = new $dae3d0e46d061380$var$Visit(this, $dae3d0e46d061380$var$expandURL(locatable), restorationIdentifier, {
            referrer: this.location,
            ...options
        });
        this.currentVisit.start();
    }
    submitForm(form, submitter) {
        this.stop();
        this.formSubmission = new $dae3d0e46d061380$var$FormSubmission(this, form, submitter, true);
        this.formSubmission.start();
    }
    stop() {
        if (this.formSubmission) {
            this.formSubmission.stop();
            delete this.formSubmission;
        }
        if (this.currentVisit) {
            this.currentVisit.cancel();
            delete this.currentVisit;
        }
    }
    get adapter() {
        return this.delegate.adapter;
    }
    get view() {
        return this.delegate.view;
    }
    get rootLocation() {
        return this.view.snapshot.rootLocation;
    }
    get history() {
        return this.delegate.history;
    }
    // Form submission delegate
    formSubmissionStarted(formSubmission) {
        // Not all adapters implement formSubmissionStarted
        if (typeof this.adapter.formSubmissionStarted === "function") this.adapter.formSubmissionStarted(formSubmission);
    }
    async formSubmissionSucceededWithResponse(formSubmission, fetchResponse) {
        if (formSubmission == this.formSubmission) {
            const responseHTML = await fetchResponse.responseHTML;
            if (responseHTML) {
                const shouldCacheSnapshot = formSubmission.isSafe;
                if (!shouldCacheSnapshot) this.view.clearSnapshotCache();
                const { statusCode: statusCode, redirected: redirected } = fetchResponse;
                const action = this.#getActionForFormSubmission(formSubmission, fetchResponse);
                const visitOptions = {
                    action: action,
                    shouldCacheSnapshot: shouldCacheSnapshot,
                    response: {
                        statusCode: statusCode,
                        responseHTML: responseHTML,
                        redirected: redirected
                    }
                };
                this.proposeVisit(fetchResponse.location, visitOptions);
            }
        }
    }
    async formSubmissionFailedWithResponse(formSubmission, fetchResponse) {
        const responseHTML = await fetchResponse.responseHTML;
        if (responseHTML) {
            const snapshot = $dae3d0e46d061380$export$4f11b95e473fd113.fromHTMLString(responseHTML);
            if (fetchResponse.serverError) await this.view.renderError(snapshot, this.currentVisit);
            else await this.view.renderPage(snapshot, false, true, this.currentVisit);
            if (!snapshot.shouldPreserveScrollPosition) this.view.scrollToTop();
            this.view.clearSnapshotCache();
        }
    }
    formSubmissionErrored(formSubmission, error) {
        console.error(error);
    }
    formSubmissionFinished(formSubmission) {
        // Not all adapters implement formSubmissionFinished
        if (typeof this.adapter.formSubmissionFinished === "function") this.adapter.formSubmissionFinished(formSubmission);
    }
    // Visit delegate
    visitStarted(visit) {
        this.delegate.visitStarted(visit);
    }
    visitCompleted(visit) {
        this.delegate.visitCompleted(visit);
        delete this.currentVisit;
    }
    locationWithActionIsSamePage(location1, action) {
        const anchor = $dae3d0e46d061380$var$getAnchor(location1);
        const currentAnchor = $dae3d0e46d061380$var$getAnchor(this.view.lastRenderedLocation);
        const isRestorationToTop = action === "restore" && typeof anchor === "undefined";
        return action !== "replace" && $dae3d0e46d061380$var$getRequestURL(location1) === $dae3d0e46d061380$var$getRequestURL(this.view.lastRenderedLocation) && (isRestorationToTop || anchor != null && anchor !== currentAnchor);
    }
    visitScrolledToSamePageLocation(oldURL, newURL) {
        this.delegate.visitScrolledToSamePageLocation(oldURL, newURL);
    }
    // Visits
    get location() {
        return this.history.location;
    }
    get restorationIdentifier() {
        return this.history.restorationIdentifier;
    }
    #getActionForFormSubmission(formSubmission, fetchResponse) {
        const { submitter: submitter, formElement: formElement } = formSubmission;
        return $dae3d0e46d061380$var$getVisitAction(submitter, formElement) || this.#getDefaultAction(fetchResponse);
    }
    #getDefaultAction(fetchResponse) {
        const sameLocationRedirect = fetchResponse.redirected && fetchResponse.location.href === this.location?.href;
        return sameLocationRedirect ? "replace" : "advance";
    }
}
const $dae3d0e46d061380$var$PageStage = {
    initial: 0,
    loading: 1,
    interactive: 2,
    complete: 3
};
class $dae3d0e46d061380$var$PageObserver {
    stage = $dae3d0e46d061380$var$PageStage.initial;
    started = false;
    constructor(delegate){
        this.delegate = delegate;
    }
    start() {
        if (!this.started) {
            if (this.stage == $dae3d0e46d061380$var$PageStage.initial) this.stage = $dae3d0e46d061380$var$PageStage.loading;
            document.addEventListener("readystatechange", this.interpretReadyState, false);
            addEventListener("pagehide", this.pageWillUnload, false);
            this.started = true;
        }
    }
    stop() {
        if (this.started) {
            document.removeEventListener("readystatechange", this.interpretReadyState, false);
            removeEventListener("pagehide", this.pageWillUnload, false);
            this.started = false;
        }
    }
    interpretReadyState = ()=>{
        const { readyState: readyState } = this;
        if (readyState == "interactive") this.pageIsInteractive();
        else if (readyState == "complete") this.pageIsComplete();
    };
    pageIsInteractive() {
        if (this.stage == $dae3d0e46d061380$var$PageStage.loading) {
            this.stage = $dae3d0e46d061380$var$PageStage.interactive;
            this.delegate.pageBecameInteractive();
        }
    }
    pageIsComplete() {
        this.pageIsInteractive();
        if (this.stage == $dae3d0e46d061380$var$PageStage.interactive) {
            this.stage = $dae3d0e46d061380$var$PageStage.complete;
            this.delegate.pageLoaded();
        }
    }
    pageWillUnload = ()=>{
        this.delegate.pageWillUnload();
    };
    get readyState() {
        return document.readyState;
    }
}
class $dae3d0e46d061380$var$ScrollObserver {
    started = false;
    constructor(delegate){
        this.delegate = delegate;
    }
    start() {
        if (!this.started) {
            addEventListener("scroll", this.onScroll, false);
            this.onScroll();
            this.started = true;
        }
    }
    stop() {
        if (this.started) {
            removeEventListener("scroll", this.onScroll, false);
            this.started = false;
        }
    }
    onScroll = ()=>{
        this.updatePosition({
            x: window.pageXOffset,
            y: window.pageYOffset
        });
    };
    // Private
    updatePosition(position) {
        this.delegate.scrollPositionChanged(position);
    }
}
class $dae3d0e46d061380$var$StreamMessageRenderer {
    render({ fragment: fragment }) {
        $dae3d0e46d061380$var$Bardo.preservingPermanentElements(this, $dae3d0e46d061380$var$getPermanentElementMapForFragment(fragment), ()=>{
            $dae3d0e46d061380$var$withAutofocusFromFragment(fragment, ()=>{
                $dae3d0e46d061380$var$withPreservedFocus(()=>{
                    document.documentElement.appendChild(fragment);
                });
            });
        });
    }
    // Bardo delegate
    enteringBardo(currentPermanentElement, newPermanentElement) {
        newPermanentElement.replaceWith(currentPermanentElement.cloneNode(true));
    }
    leavingBardo() {}
}
function $dae3d0e46d061380$var$getPermanentElementMapForFragment(fragment) {
    const permanentElementsInDocument = $dae3d0e46d061380$var$queryPermanentElementsAll(document.documentElement);
    const permanentElementMap = {};
    for (const permanentElementInDocument of permanentElementsInDocument){
        const { id: id } = permanentElementInDocument;
        for (const streamElement of fragment.querySelectorAll("turbo-stream")){
            const elementInStream = $dae3d0e46d061380$var$getPermanentElementById(streamElement.templateElement.content, id);
            if (elementInStream) permanentElementMap[id] = [
                permanentElementInDocument,
                elementInStream
            ];
        }
    }
    return permanentElementMap;
}
async function $dae3d0e46d061380$var$withAutofocusFromFragment(fragment, callback) {
    const generatedID = `turbo-stream-autofocus-${$dae3d0e46d061380$var$uuid()}`;
    const turboStreams = fragment.querySelectorAll("turbo-stream");
    const elementWithAutofocus = $dae3d0e46d061380$var$firstAutofocusableElementInStreams(turboStreams);
    let willAutofocusId = null;
    if (elementWithAutofocus) {
        if (elementWithAutofocus.id) willAutofocusId = elementWithAutofocus.id;
        else willAutofocusId = generatedID;
        elementWithAutofocus.id = willAutofocusId;
    }
    callback();
    await $dae3d0e46d061380$var$nextRepaint();
    const hasNoActiveElement = document.activeElement == null || document.activeElement == document.body;
    if (hasNoActiveElement && willAutofocusId) {
        const elementToAutofocus = document.getElementById(willAutofocusId);
        if ($dae3d0e46d061380$var$elementIsFocusable(elementToAutofocus)) elementToAutofocus.focus();
        if (elementToAutofocus && elementToAutofocus.id == generatedID) elementToAutofocus.removeAttribute("id");
    }
}
async function $dae3d0e46d061380$var$withPreservedFocus(callback) {
    const [activeElementBeforeRender, activeElementAfterRender] = await $dae3d0e46d061380$var$around(callback, ()=>document.activeElement);
    const restoreFocusTo = activeElementBeforeRender && activeElementBeforeRender.id;
    if (restoreFocusTo) {
        const elementToFocus = document.getElementById(restoreFocusTo);
        if ($dae3d0e46d061380$var$elementIsFocusable(elementToFocus) && elementToFocus != activeElementAfterRender) elementToFocus.focus();
    }
}
function $dae3d0e46d061380$var$firstAutofocusableElementInStreams(nodeListOfStreamElements) {
    for (const streamElement of nodeListOfStreamElements){
        const elementWithAutofocus = $dae3d0e46d061380$var$queryAutofocusableElement(streamElement.templateElement.content);
        if (elementWithAutofocus) return elementWithAutofocus;
    }
    return null;
}
class $dae3d0e46d061380$var$StreamObserver {
    sources = new Set();
    #started = false;
    constructor(delegate){
        this.delegate = delegate;
    }
    start() {
        if (!this.#started) {
            this.#started = true;
            addEventListener("turbo:before-fetch-response", this.inspectFetchResponse, false);
        }
    }
    stop() {
        if (this.#started) {
            this.#started = false;
            removeEventListener("turbo:before-fetch-response", this.inspectFetchResponse, false);
        }
    }
    connectStreamSource(source) {
        if (!this.streamSourceIsConnected(source)) {
            this.sources.add(source);
            source.addEventListener("message", this.receiveMessageEvent, false);
        }
    }
    disconnectStreamSource(source) {
        if (this.streamSourceIsConnected(source)) {
            this.sources.delete(source);
            source.removeEventListener("message", this.receiveMessageEvent, false);
        }
    }
    streamSourceIsConnected(source) {
        return this.sources.has(source);
    }
    inspectFetchResponse = (event)=>{
        const response = $dae3d0e46d061380$var$fetchResponseFromEvent(event);
        if (response && $dae3d0e46d061380$var$fetchResponseIsStream(response)) {
            event.preventDefault();
            this.receiveMessageResponse(response);
        }
    };
    receiveMessageEvent = (event)=>{
        if (this.#started && typeof event.data == "string") this.receiveMessageHTML(event.data);
    };
    async receiveMessageResponse(response) {
        const html = await response.responseHTML;
        if (html) this.receiveMessageHTML(html);
    }
    receiveMessageHTML(html) {
        this.delegate.receivedMessageFromStream($dae3d0e46d061380$var$StreamMessage.wrap(html));
    }
}
function $dae3d0e46d061380$var$fetchResponseFromEvent(event) {
    const fetchResponse = event.detail?.fetchResponse;
    if (fetchResponse instanceof $dae3d0e46d061380$export$3ae09bba5b6b906e) return fetchResponse;
}
function $dae3d0e46d061380$var$fetchResponseIsStream(response) {
    const contentType = response.contentType ?? "";
    return contentType.startsWith($dae3d0e46d061380$var$StreamMessage.contentType);
}
class $dae3d0e46d061380$var$ErrorRenderer extends $dae3d0e46d061380$var$Renderer {
    static renderElement(currentElement, newElement) {
        const { documentElement: documentElement, body: body } = document;
        documentElement.replaceChild(newElement, body);
    }
    async render() {
        this.replaceHeadAndBody();
        this.activateScriptElements();
    }
    replaceHeadAndBody() {
        const { documentElement: documentElement, head: head } = document;
        documentElement.replaceChild(this.newHead, head);
        this.renderElement(this.currentElement, this.newElement);
    }
    activateScriptElements() {
        for (const replaceableElement of this.scriptElements){
            const parentNode = replaceableElement.parentNode;
            if (parentNode) {
                const element = $dae3d0e46d061380$var$activateScriptElement(replaceableElement);
                parentNode.replaceChild(element, replaceableElement);
            }
        }
    }
    get newHead() {
        return this.newSnapshot.headSnapshot.element;
    }
    get scriptElements() {
        return document.documentElement.querySelectorAll("script");
    }
}
// base IIFE to define idiomorph
var $dae3d0e46d061380$var$Idiomorph = function() {
    //=============================================================================
    // AND NOW IT BEGINS...
    //=============================================================================
    let EMPTY_SET = new Set();
    // default configuration values, updatable by users now
    let defaults = {
        morphStyle: "outerHTML",
        callbacks: {
            beforeNodeAdded: noOp,
            afterNodeAdded: noOp,
            beforeNodeMorphed: noOp,
            afterNodeMorphed: noOp,
            beforeNodeRemoved: noOp,
            afterNodeRemoved: noOp,
            beforeAttributeUpdated: noOp
        },
        head: {
            style: "merge",
            shouldPreserve: function(elt) {
                return elt.getAttribute("im-preserve") === "true";
            },
            shouldReAppend: function(elt) {
                return elt.getAttribute("im-re-append") === "true";
            },
            shouldRemove: noOp,
            afterHeadMorphed: noOp
        }
    };
    //=============================================================================
    // Core Morphing Algorithm - morph, morphNormalizedContent, morphOldNodeTo, morphChildren
    //=============================================================================
    function morph(oldNode, newContent, config = {}) {
        if (oldNode instanceof Document) oldNode = oldNode.documentElement;
        if (typeof newContent === "string") newContent = parseContent(newContent);
        let normalizedContent = normalizeContent(newContent);
        let ctx = createMorphContext(oldNode, normalizedContent, config);
        return morphNormalizedContent(oldNode, normalizedContent, ctx);
    }
    function morphNormalizedContent(oldNode, normalizedNewContent, ctx) {
        if (ctx.head.block) {
            let oldHead = oldNode.querySelector("head");
            let newHead = normalizedNewContent.querySelector("head");
            if (oldHead && newHead) {
                let promises = handleHeadElement(newHead, oldHead, ctx);
                // when head promises resolve, call morph again, ignoring the head tag
                Promise.all(promises).then(function() {
                    morphNormalizedContent(oldNode, normalizedNewContent, Object.assign(ctx, {
                        head: {
                            block: false,
                            ignore: true
                        }
                    }));
                });
                return;
            }
        }
        if (ctx.morphStyle === "innerHTML") {
            // innerHTML, so we are only updating the children
            morphChildren(normalizedNewContent, oldNode, ctx);
            return oldNode.children;
        } else if (ctx.morphStyle === "outerHTML" || ctx.morphStyle == null) {
            // otherwise find the best element match in the new content, morph that, and merge its siblings
            // into either side of the best match
            let bestMatch = findBestNodeMatch(normalizedNewContent, oldNode, ctx);
            // stash the siblings that will need to be inserted on either side of the best match
            let previousSibling = bestMatch?.previousSibling;
            let nextSibling = bestMatch?.nextSibling;
            // morph it
            let morphedNode = morphOldNodeTo(oldNode, bestMatch, ctx);
            if (bestMatch) // if there was a best match, merge the siblings in too and return the
            // whole bunch
            return insertSiblings(previousSibling, morphedNode, nextSibling);
            else // otherwise nothing was added to the DOM
            return [];
        } else throw "Do not understand how to morph style " + ctx.morphStyle;
    }
    /**
         * @param possibleActiveElement
         * @param ctx
         * @returns {boolean}
         */ function ignoreValueOfActiveElement(possibleActiveElement, ctx) {
        return ctx.ignoreActiveValue && possibleActiveElement === document.activeElement && possibleActiveElement !== document.body;
    }
    /**
         * @param oldNode root node to merge content into
         * @param newContent new content to merge
         * @param ctx the merge context
         * @returns {Element} the element that ended up in the DOM
         */ function morphOldNodeTo(oldNode, newContent, ctx) {
        if (ctx.ignoreActive && oldNode === document.activeElement) ;
        else if (newContent == null) {
            if (ctx.callbacks.beforeNodeRemoved(oldNode) === false) return oldNode;
            oldNode.remove();
            ctx.callbacks.afterNodeRemoved(oldNode);
            return null;
        } else if (!isSoftMatch(oldNode, newContent)) {
            if (ctx.callbacks.beforeNodeRemoved(oldNode) === false) return oldNode;
            if (ctx.callbacks.beforeNodeAdded(newContent) === false) return oldNode;
            oldNode.parentElement.replaceChild(newContent, oldNode);
            ctx.callbacks.afterNodeAdded(newContent);
            ctx.callbacks.afterNodeRemoved(oldNode);
            return newContent;
        } else {
            if (ctx.callbacks.beforeNodeMorphed(oldNode, newContent) === false) return oldNode;
            if (oldNode instanceof HTMLHeadElement && ctx.head.ignore) ;
            else if (oldNode instanceof HTMLHeadElement && ctx.head.style !== "morph") handleHeadElement(newContent, oldNode, ctx);
            else {
                syncNodeFrom(newContent, oldNode, ctx);
                if (!ignoreValueOfActiveElement(oldNode, ctx)) morphChildren(newContent, oldNode, ctx);
            }
            ctx.callbacks.afterNodeMorphed(oldNode, newContent);
            return oldNode;
        }
    }
    /**
         * This is the core algorithm for matching up children.  The idea is to use id sets to try to match up
         * nodes as faithfully as possible.  We greedily match, which allows us to keep the algorithm fast, but
         * by using id sets, we are able to better match up with content deeper in the DOM.
         *
         * Basic algorithm is, for each node in the new content:
         *
         * - if we have reached the end of the old parent, append the new content
         * - if the new content has an id set match with the current insertion point, morph
         * - search for an id set match
         * - if id set match found, morph
         * - otherwise search for a "soft" match
         * - if a soft match is found, morph
         * - otherwise, prepend the new node before the current insertion point
         *
         * The two search algorithms terminate if competing node matches appear to outweigh what can be achieved
         * with the current node.  See findIdSetMatch() and findSoftMatch() for details.
         *
         * @param {Element} newParent the parent element of the new content
         * @param {Element } oldParent the old content that we are merging the new content into
         * @param ctx the merge context
         */ function morphChildren(newParent, oldParent, ctx) {
        let nextNewChild = newParent.firstChild;
        let insertionPoint = oldParent.firstChild;
        let newChild;
        // run through all the new content
        while(nextNewChild){
            newChild = nextNewChild;
            nextNewChild = newChild.nextSibling;
            // if we are at the end of the exiting parent's children, just append
            if (insertionPoint == null) {
                if (ctx.callbacks.beforeNodeAdded(newChild) === false) return;
                oldParent.appendChild(newChild);
                ctx.callbacks.afterNodeAdded(newChild);
                removeIdsFromConsideration(ctx, newChild);
                continue;
            }
            // if the current node has an id set match then morph
            if (isIdSetMatch(newChild, insertionPoint, ctx)) {
                morphOldNodeTo(insertionPoint, newChild, ctx);
                insertionPoint = insertionPoint.nextSibling;
                removeIdsFromConsideration(ctx, newChild);
                continue;
            }
            // otherwise search forward in the existing old children for an id set match
            let idSetMatch = findIdSetMatch(newParent, oldParent, newChild, insertionPoint, ctx);
            // if we found a potential match, remove the nodes until that point and morph
            if (idSetMatch) {
                insertionPoint = removeNodesBetween(insertionPoint, idSetMatch, ctx);
                morphOldNodeTo(idSetMatch, newChild, ctx);
                removeIdsFromConsideration(ctx, newChild);
                continue;
            }
            // no id set match found, so scan forward for a soft match for the current node
            let softMatch = findSoftMatch(newParent, oldParent, newChild, insertionPoint, ctx);
            // if we found a soft match for the current node, morph
            if (softMatch) {
                insertionPoint = removeNodesBetween(insertionPoint, softMatch, ctx);
                morphOldNodeTo(softMatch, newChild, ctx);
                removeIdsFromConsideration(ctx, newChild);
                continue;
            }
            // abandon all hope of morphing, just insert the new child before the insertion point
            // and move on
            if (ctx.callbacks.beforeNodeAdded(newChild) === false) return;
            oldParent.insertBefore(newChild, insertionPoint);
            ctx.callbacks.afterNodeAdded(newChild);
            removeIdsFromConsideration(ctx, newChild);
        }
        // remove any remaining old nodes that didn't match up with new content
        while(insertionPoint !== null){
            let tempNode = insertionPoint;
            insertionPoint = insertionPoint.nextSibling;
            removeNode(tempNode, ctx);
        }
    }
    //=============================================================================
    // Attribute Syncing Code
    //=============================================================================
    /**
         * @param attr {String} the attribute to be mutated
         * @param to {Element} the element that is going to be updated
         * @param updateType {("update"|"remove")}
         * @param ctx the merge context
         * @returns {boolean} true if the attribute should be ignored, false otherwise
         */ function ignoreAttribute(attr, to, updateType, ctx) {
        if (attr === "value" && ctx.ignoreActiveValue && to === document.activeElement) return true;
        return ctx.callbacks.beforeAttributeUpdated(attr, to, updateType) === false;
    }
    /**
         * syncs a given node with another node, copying over all attributes and
         * inner element state from the 'from' node to the 'to' node
         *
         * @param {Element} from the element to copy attributes & state from
         * @param {Element} to the element to copy attributes & state to
         * @param ctx the merge context
         */ function syncNodeFrom(from, to, ctx) {
        let type = from.nodeType;
        // if is an element type, sync the attributes from the
        // new node into the new node
        if (type === 1 /* element type */ ) {
            const fromAttributes = from.attributes;
            const toAttributes = to.attributes;
            for (const fromAttribute of fromAttributes){
                if (ignoreAttribute(fromAttribute.name, to, "update", ctx)) continue;
                if (to.getAttribute(fromAttribute.name) !== fromAttribute.value) to.setAttribute(fromAttribute.name, fromAttribute.value);
            }
            // iterate backwards to avoid skipping over items when a delete occurs
            for(let i = toAttributes.length - 1; 0 <= i; i--){
                const toAttribute = toAttributes[i];
                if (ignoreAttribute(toAttribute.name, to, "remove", ctx)) continue;
                if (!from.hasAttribute(toAttribute.name)) to.removeAttribute(toAttribute.name);
            }
        }
        // sync text nodes
        if (type === 8 /* comment */  || type === 3 /* text */ ) {
            if (to.nodeValue !== from.nodeValue) to.nodeValue = from.nodeValue;
        }
        if (!ignoreValueOfActiveElement(to, ctx)) // sync input values
        syncInputValue(from, to, ctx);
    }
    /**
         * @param from {Element} element to sync the value from
         * @param to {Element} element to sync the value to
         * @param attributeName {String} the attribute name
         * @param ctx the merge context
         */ function syncBooleanAttribute(from, to, attributeName, ctx) {
        if (from[attributeName] !== to[attributeName]) {
            let ignoreUpdate = ignoreAttribute(attributeName, to, "update", ctx);
            if (!ignoreUpdate) to[attributeName] = from[attributeName];
            if (from[attributeName]) {
                if (!ignoreUpdate) to.setAttribute(attributeName, from[attributeName]);
            } else if (!ignoreAttribute(attributeName, to, "remove", ctx)) to.removeAttribute(attributeName);
        }
    }
    /**
         * NB: many bothans died to bring us information:
         *
         *  https://github.com/patrick-steele-idem/morphdom/blob/master/src/specialElHandlers.js
         *  https://github.com/choojs/nanomorph/blob/master/lib/morph.jsL113
         *
         * @param from {Element} the element to sync the input value from
         * @param to {Element} the element to sync the input value to
         * @param ctx the merge context
         */ function syncInputValue(from, to, ctx) {
        if (from instanceof HTMLInputElement && to instanceof HTMLInputElement && from.type !== "file") {
            let fromValue = from.value;
            let toValue = to.value;
            // sync boolean attributes
            syncBooleanAttribute(from, to, "checked", ctx);
            syncBooleanAttribute(from, to, "disabled", ctx);
            if (!from.hasAttribute("value")) {
                if (!ignoreAttribute("value", to, "remove", ctx)) {
                    to.value = "";
                    to.removeAttribute("value");
                }
            } else if (fromValue !== toValue) {
                if (!ignoreAttribute("value", to, "update", ctx)) {
                    to.setAttribute("value", fromValue);
                    to.value = fromValue;
                }
            }
        } else if (from instanceof HTMLOptionElement) syncBooleanAttribute(from, to, "selected", ctx);
        else if (from instanceof HTMLTextAreaElement && to instanceof HTMLTextAreaElement) {
            let fromValue = from.value;
            let toValue = to.value;
            if (ignoreAttribute("value", to, "update", ctx)) return;
            if (fromValue !== toValue) to.value = fromValue;
            if (to.firstChild && to.firstChild.nodeValue !== fromValue) to.firstChild.nodeValue = fromValue;
        }
    }
    //=============================================================================
    // the HEAD tag can be handled specially, either w/ a 'merge' or 'append' style
    //=============================================================================
    function handleHeadElement(newHeadTag, currentHead, ctx) {
        let added = [];
        let removed = [];
        let preserved = [];
        let nodesToAppend = [];
        let headMergeStyle = ctx.head.style;
        // put all new head elements into a Map, by their outerHTML
        let srcToNewHeadNodes = new Map();
        for (const newHeadChild of newHeadTag.children)srcToNewHeadNodes.set(newHeadChild.outerHTML, newHeadChild);
        // for each elt in the current head
        for (const currentHeadElt of currentHead.children){
            // If the current head element is in the map
            let inNewContent = srcToNewHeadNodes.has(currentHeadElt.outerHTML);
            let isReAppended = ctx.head.shouldReAppend(currentHeadElt);
            let isPreserved = ctx.head.shouldPreserve(currentHeadElt);
            if (inNewContent || isPreserved) {
                if (isReAppended) // remove the current version and let the new version replace it and re-execute
                removed.push(currentHeadElt);
                else {
                    // this element already exists and should not be re-appended, so remove it from
                    // the new content map, preserving it in the DOM
                    srcToNewHeadNodes.delete(currentHeadElt.outerHTML);
                    preserved.push(currentHeadElt);
                }
            } else {
                if (headMergeStyle === "append") // we are appending and this existing element is not new content
                // so if and only if it is marked for re-append do we do anything
                {
                    if (isReAppended) {
                        removed.push(currentHeadElt);
                        nodesToAppend.push(currentHeadElt);
                    }
                } else // if this is a merge, we remove this content since it is not in the new head
                if (ctx.head.shouldRemove(currentHeadElt) !== false) removed.push(currentHeadElt);
            }
        }
        // Push the remaining new head elements in the Map into the
        // nodes to append to the head tag
        nodesToAppend.push(...srcToNewHeadNodes.values());
        let promises = [];
        for (const newNode of nodesToAppend){
            let newElt = document.createRange().createContextualFragment(newNode.outerHTML).firstChild;
            if (ctx.callbacks.beforeNodeAdded(newElt) !== false) {
                if (newElt.href || newElt.src) {
                    let resolve = null;
                    let promise = new Promise(function(_resolve) {
                        resolve = _resolve;
                    });
                    newElt.addEventListener("load", function() {
                        resolve();
                    });
                    promises.push(promise);
                }
                currentHead.appendChild(newElt);
                ctx.callbacks.afterNodeAdded(newElt);
                added.push(newElt);
            }
        }
        // remove all removed elements, after we have appended the new elements to avoid
        // additional network requests for things like style sheets
        for (const removedElement of removed)if (ctx.callbacks.beforeNodeRemoved(removedElement) !== false) {
            currentHead.removeChild(removedElement);
            ctx.callbacks.afterNodeRemoved(removedElement);
        }
        ctx.head.afterHeadMorphed(currentHead, {
            added: added,
            kept: preserved,
            removed: removed
        });
        return promises;
    }
    function noOp() {}
    /*
          Deep merges the config object and the Idiomoroph.defaults object to
          produce a final configuration object
         */ function mergeDefaults(config) {
        let finalConfig = {};
        // copy top level stuff into final config
        Object.assign(finalConfig, defaults);
        Object.assign(finalConfig, config);
        // copy callbacks into final config (do this to deep merge the callbacks)
        finalConfig.callbacks = {};
        Object.assign(finalConfig.callbacks, defaults.callbacks);
        Object.assign(finalConfig.callbacks, config.callbacks);
        // copy head config into final config  (do this to deep merge the head)
        finalConfig.head = {};
        Object.assign(finalConfig.head, defaults.head);
        Object.assign(finalConfig.head, config.head);
        return finalConfig;
    }
    function createMorphContext(oldNode, newContent, config) {
        config = mergeDefaults(config);
        return {
            target: oldNode,
            newContent: newContent,
            config: config,
            morphStyle: config.morphStyle,
            ignoreActive: config.ignoreActive,
            ignoreActiveValue: config.ignoreActiveValue,
            idMap: createIdMap(oldNode, newContent),
            deadIds: new Set(),
            callbacks: config.callbacks,
            head: config.head
        };
    }
    function isIdSetMatch(node1, node2, ctx) {
        if (node1 == null || node2 == null) return false;
        if (node1.nodeType === node2.nodeType && node1.tagName === node2.tagName) {
            if (node1.id !== "" && node1.id === node2.id) return true;
            else return getIdIntersectionCount(ctx, node1, node2) > 0;
        }
        return false;
    }
    function isSoftMatch(node1, node2) {
        if (node1 == null || node2 == null) return false;
        return node1.nodeType === node2.nodeType && node1.tagName === node2.tagName;
    }
    function removeNodesBetween(startInclusive, endExclusive, ctx) {
        while(startInclusive !== endExclusive){
            let tempNode = startInclusive;
            startInclusive = startInclusive.nextSibling;
            removeNode(tempNode, ctx);
        }
        removeIdsFromConsideration(ctx, endExclusive);
        return endExclusive.nextSibling;
    }
    //=============================================================================
    // Scans forward from the insertionPoint in the old parent looking for a potential id match
    // for the newChild.  We stop if we find a potential id match for the new child OR
    // if the number of potential id matches we are discarding is greater than the
    // potential id matches for the new child
    //=============================================================================
    function findIdSetMatch(newContent, oldParent, newChild, insertionPoint, ctx) {
        // max id matches we are willing to discard in our search
        let newChildPotentialIdCount = getIdIntersectionCount(ctx, newChild, oldParent);
        let potentialMatch = null;
        // only search forward if there is a possibility of an id match
        if (newChildPotentialIdCount > 0) {
            let potentialMatch = insertionPoint;
            // if there is a possibility of an id match, scan forward
            // keep track of the potential id match count we are discarding (the
            // newChildPotentialIdCount must be greater than this to make it likely
            // worth it)
            let otherMatchCount = 0;
            while(potentialMatch != null){
                // If we have an id match, return the current potential match
                if (isIdSetMatch(newChild, potentialMatch, ctx)) return potentialMatch;
                // computer the other potential matches of this new content
                otherMatchCount += getIdIntersectionCount(ctx, potentialMatch, newContent);
                if (otherMatchCount > newChildPotentialIdCount) // if we have more potential id matches in _other_ content, we
                // do not have a good candidate for an id match, so return null
                return null;
                // advanced to the next old content child
                potentialMatch = potentialMatch.nextSibling;
            }
        }
        return potentialMatch;
    }
    //=============================================================================
    // Scans forward from the insertionPoint in the old parent looking for a potential soft match
    // for the newChild.  We stop if we find a potential soft match for the new child OR
    // if we find a potential id match in the old parents children OR if we find two
    // potential soft matches for the next two pieces of new content
    //=============================================================================
    function findSoftMatch(newContent, oldParent, newChild, insertionPoint, ctx) {
        let potentialSoftMatch = insertionPoint;
        let nextSibling = newChild.nextSibling;
        let siblingSoftMatchCount = 0;
        while(potentialSoftMatch != null){
            if (getIdIntersectionCount(ctx, potentialSoftMatch, newContent) > 0) // the current potential soft match has a potential id set match with the remaining new
            // content so bail out of looking
            return null;
            // if we have a soft match with the current node, return it
            if (isSoftMatch(newChild, potentialSoftMatch)) return potentialSoftMatch;
            if (isSoftMatch(nextSibling, potentialSoftMatch)) {
                // the next new node has a soft match with this node, so
                // increment the count of future soft matches
                siblingSoftMatchCount++;
                nextSibling = nextSibling.nextSibling;
                // If there are two future soft matches, bail to allow the siblings to soft match
                // so that we don't consume future soft matches for the sake of the current node
                if (siblingSoftMatchCount >= 2) return null;
            }
            // advanced to the next old content child
            potentialSoftMatch = potentialSoftMatch.nextSibling;
        }
        return potentialSoftMatch;
    }
    function parseContent(newContent) {
        let parser = new DOMParser();
        // remove svgs to avoid false-positive matches on head, etc.
        let contentWithSvgsRemoved = newContent.replace(/<svg(\s[^>]*>|>)([\s\S]*?)<\/svg>/gim, "");
        // if the newContent contains a html, head or body tag, we can simply parse it w/o wrapping
        if (contentWithSvgsRemoved.match(/<\/html>/) || contentWithSvgsRemoved.match(/<\/head>/) || contentWithSvgsRemoved.match(/<\/body>/)) {
            let content = parser.parseFromString(newContent, "text/html");
            // if it is a full HTML document, return the document itself as the parent container
            if (contentWithSvgsRemoved.match(/<\/html>/)) {
                content.generatedByIdiomorph = true;
                return content;
            } else {
                // otherwise return the html element as the parent container
                let htmlElement = content.firstChild;
                if (htmlElement) {
                    htmlElement.generatedByIdiomorph = true;
                    return htmlElement;
                } else return null;
            }
        } else {
            // if it is partial HTML, wrap it in a template tag to provide a parent element and also to help
            // deal with touchy tags like tr, tbody, etc.
            let responseDoc = parser.parseFromString("<body><template>" + newContent + "</template></body>", "text/html");
            let content = responseDoc.body.querySelector("template").content;
            content.generatedByIdiomorph = true;
            return content;
        }
    }
    function normalizeContent(newContent) {
        if (newContent == null) {
            // noinspection UnnecessaryLocalVariableJS
            const dummyParent = document.createElement("div");
            return dummyParent;
        } else if (newContent.generatedByIdiomorph) // the template tag created by idiomorph parsing can serve as a dummy parent
        return newContent;
        else if (newContent instanceof Node) {
            // a single node is added as a child to a dummy parent
            const dummyParent = document.createElement("div");
            dummyParent.append(newContent);
            return dummyParent;
        } else {
            // all nodes in the array or HTMLElement collection are consolidated under
            // a single dummy parent element
            const dummyParent = document.createElement("div");
            for (const elt of [
                ...newContent
            ])dummyParent.append(elt);
            return dummyParent;
        }
    }
    function insertSiblings(previousSibling, morphedNode, nextSibling) {
        let stack = [];
        let added = [];
        while(previousSibling != null){
            stack.push(previousSibling);
            previousSibling = previousSibling.previousSibling;
        }
        while(stack.length > 0){
            let node = stack.pop();
            added.push(node); // push added preceding siblings on in order and insert
            morphedNode.parentElement.insertBefore(node, morphedNode);
        }
        added.push(morphedNode);
        while(nextSibling != null){
            stack.push(nextSibling);
            added.push(nextSibling); // here we are going in order, so push on as we scan, rather than add
            nextSibling = nextSibling.nextSibling;
        }
        while(stack.length > 0)morphedNode.parentElement.insertBefore(stack.pop(), morphedNode.nextSibling);
        return added;
    }
    function findBestNodeMatch(newContent, oldNode, ctx) {
        let currentElement;
        currentElement = newContent.firstChild;
        let bestElement = currentElement;
        let score = 0;
        while(currentElement){
            let newScore = scoreElement(currentElement, oldNode, ctx);
            if (newScore > score) {
                bestElement = currentElement;
                score = newScore;
            }
            currentElement = currentElement.nextSibling;
        }
        return bestElement;
    }
    function scoreElement(node1, node2, ctx) {
        if (isSoftMatch(node1, node2)) return .5 + getIdIntersectionCount(ctx, node1, node2);
        return 0;
    }
    function removeNode(tempNode, ctx) {
        removeIdsFromConsideration(ctx, tempNode);
        if (ctx.callbacks.beforeNodeRemoved(tempNode) === false) return;
        tempNode.remove();
        ctx.callbacks.afterNodeRemoved(tempNode);
    }
    //=============================================================================
    // ID Set Functions
    //=============================================================================
    function isIdInConsideration(ctx, id) {
        return !ctx.deadIds.has(id);
    }
    function idIsWithinNode(ctx, id, targetNode) {
        let idSet = ctx.idMap.get(targetNode) || EMPTY_SET;
        return idSet.has(id);
    }
    function removeIdsFromConsideration(ctx, node) {
        let idSet = ctx.idMap.get(node) || EMPTY_SET;
        for (const id of idSet)ctx.deadIds.add(id);
    }
    function getIdIntersectionCount(ctx, node1, node2) {
        let sourceSet = ctx.idMap.get(node1) || EMPTY_SET;
        let matchCount = 0;
        for (const id of sourceSet)// a potential match is an id in the source and potentialIdsSet, but
        // that has not already been merged into the DOM
        if (isIdInConsideration(ctx, id) && idIsWithinNode(ctx, id, node2)) ++matchCount;
        return matchCount;
    }
    /**
         * A bottom up algorithm that finds all elements with ids inside of the node
         * argument and populates id sets for those nodes and all their parents, generating
         * a set of ids contained within all nodes for the entire hierarchy in the DOM
         *
         * @param node {Element}
         * @param {Map<Node, Set<String>>} idMap
         */ function populateIdMapForNode(node, idMap) {
        let nodeParent = node.parentElement;
        // find all elements with an id property
        let idElements = node.querySelectorAll("[id]");
        for (const elt of idElements){
            let current = elt;
            // walk up the parent hierarchy of that element, adding the id
            // of element to the parent's id set
            while(current !== nodeParent && current != null){
                let idSet = idMap.get(current);
                // if the id set doesn't exist, create it and insert it in the  map
                if (idSet == null) {
                    idSet = new Set();
                    idMap.set(current, idSet);
                }
                idSet.add(elt.id);
                current = current.parentElement;
            }
        }
    }
    /**
         * This function computes a map of nodes to all ids contained within that node (inclusive of the
         * node).  This map can be used to ask if two nodes have intersecting sets of ids, which allows
         * for a looser definition of "matching" than tradition id matching, and allows child nodes
         * to contribute to a parent nodes matching.
         *
         * @param {Element} oldContent  the old content that will be morphed
         * @param {Element} newContent  the new content to morph to
         * @returns {Map<Node, Set<String>>} a map of nodes to id sets for the
         */ function createIdMap(oldContent, newContent) {
        let idMap = new Map();
        populateIdMapForNode(oldContent, idMap);
        populateIdMapForNode(newContent, idMap);
        return idMap;
    }
    //=============================================================================
    // This is what ends up becoming the Idiomorph global object
    //=============================================================================
    return {
        morph: morph,
        defaults: defaults
    };
}();
function $dae3d0e46d061380$var$morphElements(currentElement, newElement, { callbacks: callbacks, ...options } = {}) {
    $dae3d0e46d061380$var$Idiomorph.morph(currentElement, newElement, {
        ...options,
        callbacks: new $dae3d0e46d061380$var$DefaultIdiomorphCallbacks(callbacks)
    });
}
function $dae3d0e46d061380$var$morphChildren(currentElement, newElement) {
    $dae3d0e46d061380$var$morphElements(currentElement, newElement.children, {
        morphStyle: "innerHTML"
    });
}
class $dae3d0e46d061380$var$DefaultIdiomorphCallbacks {
    #beforeNodeMorphed;
    constructor({ beforeNodeMorphed: beforeNodeMorphed } = {}){
        this.#beforeNodeMorphed = beforeNodeMorphed || (()=>true);
    }
    beforeNodeAdded = (node)=>{
        return !(node.id && node.hasAttribute("data-turbo-permanent") && document.getElementById(node.id));
    };
    beforeNodeMorphed = (currentElement, newElement)=>{
        if (currentElement instanceof Element) {
            if (!currentElement.hasAttribute("data-turbo-permanent") && this.#beforeNodeMorphed(currentElement, newElement)) {
                const event = $dae3d0e46d061380$var$dispatch("turbo:before-morph-element", {
                    cancelable: true,
                    target: currentElement,
                    detail: {
                        currentElement: currentElement,
                        newElement: newElement
                    }
                });
                return !event.defaultPrevented;
            } else return false;
        }
    };
    beforeAttributeUpdated = (attributeName, target, mutationType)=>{
        const event = $dae3d0e46d061380$var$dispatch("turbo:before-morph-attribute", {
            cancelable: true,
            target: target,
            detail: {
                attributeName: attributeName,
                mutationType: mutationType
            }
        });
        return !event.defaultPrevented;
    };
    beforeNodeRemoved = (node)=>{
        return this.beforeNodeMorphed(node);
    };
    afterNodeMorphed = (currentElement, newElement)=>{
        if (currentElement instanceof Element) $dae3d0e46d061380$var$dispatch("turbo:morph-element", {
            target: currentElement,
            detail: {
                currentElement: currentElement,
                newElement: newElement
            }
        });
    };
}
class $dae3d0e46d061380$var$MorphingFrameRenderer extends $dae3d0e46d061380$export$f09c4165d16bb2b7 {
    static renderElement(currentElement, newElement) {
        $dae3d0e46d061380$var$dispatch("turbo:before-frame-morph", {
            target: currentElement,
            detail: {
                currentElement: currentElement,
                newElement: newElement
            }
        });
        $dae3d0e46d061380$var$morphChildren(currentElement, newElement);
    }
}
class $dae3d0e46d061380$export$ac9642242cf7b3ce extends $dae3d0e46d061380$var$Renderer {
    static renderElement(currentElement, newElement) {
        if (document.body && newElement instanceof HTMLBodyElement) document.body.replaceWith(newElement);
        else document.documentElement.appendChild(newElement);
    }
    get shouldRender() {
        return this.newSnapshot.isVisitable && this.trackedElementsAreIdentical;
    }
    get reloadReason() {
        if (!this.newSnapshot.isVisitable) return {
            reason: "turbo_visit_control_is_reload"
        };
        if (!this.trackedElementsAreIdentical) return {
            reason: "tracked_element_mismatch"
        };
    }
    async prepareToRender() {
        this.#setLanguage();
        await this.mergeHead();
    }
    async render() {
        if (this.willRender) await this.replaceBody();
    }
    finishRendering() {
        super.finishRendering();
        if (!this.isPreview) this.focusFirstAutofocusableElement();
    }
    get currentHeadSnapshot() {
        return this.currentSnapshot.headSnapshot;
    }
    get newHeadSnapshot() {
        return this.newSnapshot.headSnapshot;
    }
    get newElement() {
        return this.newSnapshot.element;
    }
    #setLanguage() {
        const { documentElement: documentElement } = this.currentSnapshot;
        const { lang: lang } = this.newSnapshot;
        if (lang) documentElement.setAttribute("lang", lang);
        else documentElement.removeAttribute("lang");
    }
    async mergeHead() {
        const mergedHeadElements = this.mergeProvisionalElements();
        const newStylesheetElements = this.copyNewHeadStylesheetElements();
        this.copyNewHeadScriptElements();
        await mergedHeadElements;
        await newStylesheetElements;
        if (this.willRender) this.removeUnusedDynamicStylesheetElements();
    }
    async replaceBody() {
        await this.preservingPermanentElements(async ()=>{
            this.activateNewBody();
            await this.assignNewBody();
        });
    }
    get trackedElementsAreIdentical() {
        return this.currentHeadSnapshot.trackedElementSignature == this.newHeadSnapshot.trackedElementSignature;
    }
    async copyNewHeadStylesheetElements() {
        const loadingElements = [];
        for (const element of this.newHeadStylesheetElements){
            loadingElements.push($dae3d0e46d061380$var$waitForLoad(element));
            document.head.appendChild(element);
        }
        await Promise.all(loadingElements);
    }
    copyNewHeadScriptElements() {
        for (const element of this.newHeadScriptElements)document.head.appendChild($dae3d0e46d061380$var$activateScriptElement(element));
    }
    removeUnusedDynamicStylesheetElements() {
        for (const element of this.unusedDynamicStylesheetElements)document.head.removeChild(element);
    }
    async mergeProvisionalElements() {
        const newHeadElements = [
            ...this.newHeadProvisionalElements
        ];
        for (const element of this.currentHeadProvisionalElements)if (!this.isCurrentElementInElementList(element, newHeadElements)) document.head.removeChild(element);
        for (const element of newHeadElements)document.head.appendChild(element);
    }
    isCurrentElementInElementList(element, elementList) {
        for (const [index, newElement] of elementList.entries()){
            // if title element...
            if (element.tagName == "TITLE") {
                if (newElement.tagName != "TITLE") continue;
                if (element.innerHTML == newElement.innerHTML) {
                    elementList.splice(index, 1);
                    return true;
                }
            }
            // if any other element...
            if (newElement.isEqualNode(element)) {
                elementList.splice(index, 1);
                return true;
            }
        }
        return false;
    }
    removeCurrentHeadProvisionalElements() {
        for (const element of this.currentHeadProvisionalElements)document.head.removeChild(element);
    }
    copyNewHeadProvisionalElements() {
        for (const element of this.newHeadProvisionalElements)document.head.appendChild(element);
    }
    activateNewBody() {
        document.adoptNode(this.newElement);
        this.activateNewBodyScriptElements();
    }
    activateNewBodyScriptElements() {
        for (const inertScriptElement of this.newBodyScriptElements){
            const activatedScriptElement = $dae3d0e46d061380$var$activateScriptElement(inertScriptElement);
            inertScriptElement.replaceWith(activatedScriptElement);
        }
    }
    async assignNewBody() {
        await this.renderElement(this.currentElement, this.newElement);
    }
    get unusedDynamicStylesheetElements() {
        return this.oldHeadStylesheetElements.filter((element)=>{
            return element.getAttribute("data-turbo-track") === "dynamic";
        });
    }
    get oldHeadStylesheetElements() {
        return this.currentHeadSnapshot.getStylesheetElementsNotInSnapshot(this.newHeadSnapshot);
    }
    get newHeadStylesheetElements() {
        return this.newHeadSnapshot.getStylesheetElementsNotInSnapshot(this.currentHeadSnapshot);
    }
    get newHeadScriptElements() {
        return this.newHeadSnapshot.getScriptElementsNotInSnapshot(this.currentHeadSnapshot);
    }
    get currentHeadProvisionalElements() {
        return this.currentHeadSnapshot.provisionalElements;
    }
    get newHeadProvisionalElements() {
        return this.newHeadSnapshot.provisionalElements;
    }
    get newBodyScriptElements() {
        return this.newElement.querySelectorAll("script");
    }
}
class $dae3d0e46d061380$var$MorphingPageRenderer extends $dae3d0e46d061380$export$ac9642242cf7b3ce {
    static renderElement(currentElement, newElement) {
        $dae3d0e46d061380$var$morphElements(currentElement, newElement, {
            callbacks: {
                beforeNodeMorphed: (element)=>!$dae3d0e46d061380$var$canRefreshFrame(element)
            }
        });
        for (const frame of currentElement.querySelectorAll("turbo-frame"))if ($dae3d0e46d061380$var$canRefreshFrame(frame)) $dae3d0e46d061380$var$refreshFrame(frame);
        $dae3d0e46d061380$var$dispatch("turbo:morph", {
            detail: {
                currentElement: currentElement,
                newElement: newElement
            }
        });
    }
    async preservingPermanentElements(callback) {
        return await callback();
    }
    get renderMethod() {
        return "morph";
    }
    get shouldAutofocus() {
        return false;
    }
}
function $dae3d0e46d061380$var$canRefreshFrame(frame) {
    return frame instanceof $dae3d0e46d061380$export$f464a8f0ffa32c44 && frame.src && frame.refresh === "morph" && !frame.closest("[data-turbo-permanent]");
}
function $dae3d0e46d061380$var$refreshFrame(frame) {
    frame.addEventListener("turbo:before-frame-render", ({ detail: detail })=>{
        detail.render = $dae3d0e46d061380$var$MorphingFrameRenderer.renderElement;
    }, {
        once: true
    });
    frame.reload();
}
class $dae3d0e46d061380$var$SnapshotCache {
    keys = [];
    snapshots = {};
    constructor(size){
        this.size = size;
    }
    has(location1) {
        return $dae3d0e46d061380$var$toCacheKey(location1) in this.snapshots;
    }
    get(location1) {
        if (this.has(location1)) {
            const snapshot = this.read(location1);
            this.touch(location1);
            return snapshot;
        }
    }
    put(location1, snapshot) {
        this.write(location1, snapshot);
        this.touch(location1);
        return snapshot;
    }
    clear() {
        this.snapshots = {};
    }
    // Private
    read(location1) {
        return this.snapshots[$dae3d0e46d061380$var$toCacheKey(location1)];
    }
    write(location1, snapshot) {
        this.snapshots[$dae3d0e46d061380$var$toCacheKey(location1)] = snapshot;
    }
    touch(location1) {
        const key = $dae3d0e46d061380$var$toCacheKey(location1);
        const index = this.keys.indexOf(key);
        if (index > -1) this.keys.splice(index, 1);
        this.keys.unshift(key);
        this.trim();
    }
    trim() {
        for (const key of this.keys.splice(this.size))delete this.snapshots[key];
    }
}
class $dae3d0e46d061380$var$PageView extends $dae3d0e46d061380$var$View {
    snapshotCache = new $dae3d0e46d061380$var$SnapshotCache(10);
    lastRenderedLocation = new URL(location.href);
    forceReloaded = false;
    shouldTransitionTo(newSnapshot) {
        return this.snapshot.prefersViewTransitions && newSnapshot.prefersViewTransitions;
    }
    renderPage(snapshot, isPreview = false, willRender = true, visit) {
        const shouldMorphPage = this.isPageRefresh(visit) && this.snapshot.shouldMorphPage;
        const rendererClass = shouldMorphPage ? $dae3d0e46d061380$var$MorphingPageRenderer : $dae3d0e46d061380$export$ac9642242cf7b3ce;
        const renderer = new rendererClass(this.snapshot, snapshot, rendererClass.renderElement, isPreview, willRender);
        if (!renderer.shouldRender) this.forceReloaded = true;
        else visit?.changeHistory();
        return this.render(renderer);
    }
    renderError(snapshot, visit) {
        visit?.changeHistory();
        const renderer = new $dae3d0e46d061380$var$ErrorRenderer(this.snapshot, snapshot, $dae3d0e46d061380$var$ErrorRenderer.renderElement, false);
        return this.render(renderer);
    }
    clearSnapshotCache() {
        this.snapshotCache.clear();
    }
    async cacheSnapshot(snapshot = this.snapshot) {
        if (snapshot.isCacheable) {
            this.delegate.viewWillCacheSnapshot();
            const { lastRenderedLocation: location1 } = this;
            await $dae3d0e46d061380$var$nextEventLoopTick();
            const cachedSnapshot = snapshot.clone();
            this.snapshotCache.put(location1, cachedSnapshot);
            return cachedSnapshot;
        }
    }
    getCachedSnapshotForLocation(location1) {
        return this.snapshotCache.get(location1);
    }
    isPageRefresh(visit) {
        return !visit || this.lastRenderedLocation.pathname === visit.location.pathname && visit.action === "replace";
    }
    shouldPreserveScrollPosition(visit) {
        return this.isPageRefresh(visit) && this.snapshot.shouldPreserveScrollPosition;
    }
    get snapshot() {
        return $dae3d0e46d061380$export$4f11b95e473fd113.fromElement(this.element);
    }
}
class $dae3d0e46d061380$var$Preloader {
    selector = "a[data-turbo-preload]";
    constructor(delegate, snapshotCache){
        this.delegate = delegate;
        this.snapshotCache = snapshotCache;
    }
    start() {
        if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", this.#preloadAll);
        else this.preloadOnLoadLinksForView(document.body);
    }
    stop() {
        document.removeEventListener("DOMContentLoaded", this.#preloadAll);
    }
    preloadOnLoadLinksForView(element) {
        for (const link of element.querySelectorAll(this.selector))if (this.delegate.shouldPreloadLink(link)) this.preloadURL(link);
    }
    async preloadURL(link) {
        const location1 = new URL(link.href);
        if (this.snapshotCache.has(location1)) return;
        const fetchRequest = new $dae3d0e46d061380$export$319b96fe59834522(this, $dae3d0e46d061380$export$76f6869e29ff06ee.get, location1, new URLSearchParams(), link);
        await fetchRequest.perform();
    }
    // Fetch request delegate
    prepareRequest(fetchRequest) {
        fetchRequest.headers["X-Sec-Purpose"] = "prefetch";
    }
    async requestSucceededWithResponse(fetchRequest, fetchResponse) {
        try {
            const responseHTML = await fetchResponse.responseHTML;
            const snapshot = $dae3d0e46d061380$export$4f11b95e473fd113.fromHTMLString(responseHTML);
            this.snapshotCache.put(fetchRequest.url, snapshot);
        } catch (_) {
        // If we cannot preload that is ok!
        }
    }
    requestStarted(fetchRequest) {}
    requestErrored(fetchRequest) {}
    requestFinished(fetchRequest) {}
    requestPreventedHandlingResponse(fetchRequest, fetchResponse) {}
    requestFailedWithResponse(fetchRequest, fetchResponse) {}
    #preloadAll = ()=>{
        this.preloadOnLoadLinksForView(document.body);
    };
}
class $dae3d0e46d061380$var$Cache {
    constructor(session){
        this.session = session;
    }
    clear() {
        this.session.clearCache();
    }
    resetCacheControl() {
        this.#setCacheControl("");
    }
    exemptPageFromCache() {
        this.#setCacheControl("no-cache");
    }
    exemptPageFromPreview() {
        this.#setCacheControl("no-preview");
    }
    #setCacheControl(value) {
        $dae3d0e46d061380$var$setMetaContent("turbo-cache-control", value);
    }
}
class $dae3d0e46d061380$var$Session {
    navigator = new $dae3d0e46d061380$var$Navigator(this);
    history = new $dae3d0e46d061380$var$History(this);
    view = new $dae3d0e46d061380$var$PageView(this, document.documentElement);
    adapter = new $dae3d0e46d061380$var$BrowserAdapter(this);
    pageObserver = new $dae3d0e46d061380$var$PageObserver(this);
    cacheObserver = new $dae3d0e46d061380$var$CacheObserver();
    linkPrefetchObserver = new $dae3d0e46d061380$var$LinkPrefetchObserver(this, document);
    linkClickObserver = new $dae3d0e46d061380$var$LinkClickObserver(this, window);
    formSubmitObserver = new $dae3d0e46d061380$var$FormSubmitObserver(this, document);
    scrollObserver = new $dae3d0e46d061380$var$ScrollObserver(this);
    streamObserver = new $dae3d0e46d061380$var$StreamObserver(this);
    formLinkClickObserver = new $dae3d0e46d061380$var$FormLinkClickObserver(this, document.documentElement);
    frameRedirector = new $dae3d0e46d061380$var$FrameRedirector(this, document.documentElement);
    streamMessageRenderer = new $dae3d0e46d061380$var$StreamMessageRenderer();
    cache = new $dae3d0e46d061380$var$Cache(this);
    drive = true;
    enabled = true;
    progressBarDelay = 500;
    started = false;
    formMode = "on";
    #pageRefreshDebouncePeriod = 150;
    constructor(recentRequests){
        this.recentRequests = recentRequests;
        this.preloader = new $dae3d0e46d061380$var$Preloader(this, this.view.snapshotCache);
        this.debouncedRefresh = this.refresh;
        this.pageRefreshDebouncePeriod = this.pageRefreshDebouncePeriod;
    }
    start() {
        if (!this.started) {
            this.pageObserver.start();
            this.cacheObserver.start();
            this.linkPrefetchObserver.start();
            this.formLinkClickObserver.start();
            this.linkClickObserver.start();
            this.formSubmitObserver.start();
            this.scrollObserver.start();
            this.streamObserver.start();
            this.frameRedirector.start();
            this.history.start();
            this.preloader.start();
            this.started = true;
            this.enabled = true;
        }
    }
    disable() {
        this.enabled = false;
    }
    stop() {
        if (this.started) {
            this.pageObserver.stop();
            this.cacheObserver.stop();
            this.linkPrefetchObserver.stop();
            this.formLinkClickObserver.stop();
            this.linkClickObserver.stop();
            this.formSubmitObserver.stop();
            this.scrollObserver.stop();
            this.streamObserver.stop();
            this.frameRedirector.stop();
            this.history.stop();
            this.preloader.stop();
            this.started = false;
        }
    }
    registerAdapter(adapter) {
        this.adapter = adapter;
    }
    visit(location1, options = {}) {
        const frameElement = options.frame ? document.getElementById(options.frame) : null;
        if (frameElement instanceof $dae3d0e46d061380$export$f464a8f0ffa32c44) {
            const action = options.action || $dae3d0e46d061380$var$getVisitAction(frameElement);
            frameElement.delegate.proposeVisitIfNavigatedWithAction(frameElement, action);
            frameElement.src = location1.toString();
        } else this.navigator.proposeVisit($dae3d0e46d061380$var$expandURL(location1), options);
    }
    refresh(url, requestId) {
        const isRecentRequest = requestId && this.recentRequests.has(requestId);
        if (!isRecentRequest && !this.navigator.currentVisit) this.visit(url, {
            action: "replace",
            shouldCacheSnapshot: false
        });
    }
    connectStreamSource(source) {
        this.streamObserver.connectStreamSource(source);
    }
    disconnectStreamSource(source) {
        this.streamObserver.disconnectStreamSource(source);
    }
    renderStreamMessage(message) {
        this.streamMessageRenderer.render($dae3d0e46d061380$var$StreamMessage.wrap(message));
    }
    clearCache() {
        this.view.clearSnapshotCache();
    }
    setProgressBarDelay(delay) {
        this.progressBarDelay = delay;
    }
    setFormMode(mode) {
        this.formMode = mode;
    }
    get location() {
        return this.history.location;
    }
    get restorationIdentifier() {
        return this.history.restorationIdentifier;
    }
    get pageRefreshDebouncePeriod() {
        return this.#pageRefreshDebouncePeriod;
    }
    set pageRefreshDebouncePeriod(value) {
        this.refresh = $dae3d0e46d061380$var$debounce(this.debouncedRefresh.bind(this), value);
        this.#pageRefreshDebouncePeriod = value;
    }
    // Preloader delegate
    shouldPreloadLink(element) {
        const isUnsafe = element.hasAttribute("data-turbo-method");
        const isStream = element.hasAttribute("data-turbo-stream");
        const frameTarget = element.getAttribute("data-turbo-frame");
        const frame = frameTarget == "_top" ? null : document.getElementById(frameTarget) || $dae3d0e46d061380$var$findClosestRecursively(element, "turbo-frame:not([disabled])");
        if (isUnsafe || isStream || frame instanceof $dae3d0e46d061380$export$f464a8f0ffa32c44) return false;
        else {
            const location1 = new URL(element.href);
            return this.elementIsNavigatable(element) && $dae3d0e46d061380$var$locationIsVisitable(location1, this.snapshot.rootLocation);
        }
    }
    // History delegate
    historyPoppedToLocationWithRestorationIdentifierAndDirection(location1, restorationIdentifier, direction) {
        if (this.enabled) this.navigator.startVisit(location1, restorationIdentifier, {
            action: "restore",
            historyChanged: true,
            direction: direction
        });
        else this.adapter.pageInvalidated({
            reason: "turbo_disabled"
        });
    }
    // Scroll observer delegate
    scrollPositionChanged(position) {
        this.history.updateRestorationData({
            scrollPosition: position
        });
    }
    // Form click observer delegate
    willSubmitFormLinkToLocation(link, location1) {
        return this.elementIsNavigatable(link) && $dae3d0e46d061380$var$locationIsVisitable(location1, this.snapshot.rootLocation);
    }
    submittedFormLinkToLocation() {}
    // Link hover observer delegate
    canPrefetchRequestToLocation(link, location1) {
        return this.elementIsNavigatable(link) && $dae3d0e46d061380$var$locationIsVisitable(location1, this.snapshot.rootLocation);
    }
    // Link click observer delegate
    willFollowLinkToLocation(link, location1, event) {
        return this.elementIsNavigatable(link) && $dae3d0e46d061380$var$locationIsVisitable(location1, this.snapshot.rootLocation) && this.applicationAllowsFollowingLinkToLocation(link, location1, event);
    }
    followedLinkToLocation(link, location1) {
        const action = this.getActionForLink(link);
        const acceptsStreamResponse = link.hasAttribute("data-turbo-stream");
        this.visit(location1.href, {
            action: action,
            acceptsStreamResponse: acceptsStreamResponse
        });
    }
    // Navigator delegate
    allowsVisitingLocationWithAction(location1, action) {
        return this.locationWithActionIsSamePage(location1, action) || this.applicationAllowsVisitingLocation(location1);
    }
    visitProposedToLocation(location1, options) {
        $dae3d0e46d061380$var$extendURLWithDeprecatedProperties(location1);
        this.adapter.visitProposedToLocation(location1, options);
    }
    // Visit delegate
    visitStarted(visit) {
        if (!visit.acceptsStreamResponse) {
            $dae3d0e46d061380$var$markAsBusy(document.documentElement);
            this.view.markVisitDirection(visit.direction);
        }
        $dae3d0e46d061380$var$extendURLWithDeprecatedProperties(visit.location);
        if (!visit.silent) this.notifyApplicationAfterVisitingLocation(visit.location, visit.action);
    }
    visitCompleted(visit) {
        this.view.unmarkVisitDirection();
        $dae3d0e46d061380$var$clearBusyState(document.documentElement);
        this.notifyApplicationAfterPageLoad(visit.getTimingMetrics());
    }
    locationWithActionIsSamePage(location1, action) {
        return this.navigator.locationWithActionIsSamePage(location1, action);
    }
    visitScrolledToSamePageLocation(oldURL, newURL) {
        this.notifyApplicationAfterVisitingSamePageLocation(oldURL, newURL);
    }
    // Form submit observer delegate
    willSubmitForm(form, submitter) {
        const action = $dae3d0e46d061380$var$getAction$1(form, submitter);
        return this.submissionIsNavigatable(form, submitter) && $dae3d0e46d061380$var$locationIsVisitable($dae3d0e46d061380$var$expandURL(action), this.snapshot.rootLocation);
    }
    formSubmitted(form, submitter) {
        this.navigator.submitForm(form, submitter);
    }
    // Page observer delegate
    pageBecameInteractive() {
        this.view.lastRenderedLocation = this.location;
        this.notifyApplicationAfterPageLoad();
    }
    pageLoaded() {
        this.history.assumeControlOfScrollRestoration();
    }
    pageWillUnload() {
        this.history.relinquishControlOfScrollRestoration();
    }
    // Stream observer delegate
    receivedMessageFromStream(message) {
        this.renderStreamMessage(message);
    }
    // Page view delegate
    viewWillCacheSnapshot() {
        if (!this.navigator.currentVisit?.silent) this.notifyApplicationBeforeCachingSnapshot();
    }
    allowsImmediateRender({ element: element }, options) {
        const event = this.notifyApplicationBeforeRender(element, options);
        const { defaultPrevented: defaultPrevented, detail: { render: render } } = event;
        if (this.view.renderer && render) this.view.renderer.renderElement = render;
        return !defaultPrevented;
    }
    viewRenderedSnapshot(_snapshot, _isPreview, renderMethod) {
        this.view.lastRenderedLocation = this.history.location;
        this.notifyApplicationAfterRender(renderMethod);
    }
    preloadOnLoadLinksForView(element) {
        this.preloader.preloadOnLoadLinksForView(element);
    }
    viewInvalidated(reason) {
        this.adapter.pageInvalidated(reason);
    }
    // Frame element
    frameLoaded(frame) {
        this.notifyApplicationAfterFrameLoad(frame);
    }
    frameRendered(fetchResponse, frame) {
        this.notifyApplicationAfterFrameRender(fetchResponse, frame);
    }
    // Application events
    applicationAllowsFollowingLinkToLocation(link, location1, ev) {
        const event = this.notifyApplicationAfterClickingLinkToLocation(link, location1, ev);
        return !event.defaultPrevented;
    }
    applicationAllowsVisitingLocation(location1) {
        const event = this.notifyApplicationBeforeVisitingLocation(location1);
        return !event.defaultPrevented;
    }
    notifyApplicationAfterClickingLinkToLocation(link, location1, event) {
        return $dae3d0e46d061380$var$dispatch("turbo:click", {
            target: link,
            detail: {
                url: location1.href,
                originalEvent: event
            },
            cancelable: true
        });
    }
    notifyApplicationBeforeVisitingLocation(location1) {
        return $dae3d0e46d061380$var$dispatch("turbo:before-visit", {
            detail: {
                url: location1.href
            },
            cancelable: true
        });
    }
    notifyApplicationAfterVisitingLocation(location1, action) {
        return $dae3d0e46d061380$var$dispatch("turbo:visit", {
            detail: {
                url: location1.href,
                action: action
            }
        });
    }
    notifyApplicationBeforeCachingSnapshot() {
        return $dae3d0e46d061380$var$dispatch("turbo:before-cache");
    }
    notifyApplicationBeforeRender(newBody, options) {
        return $dae3d0e46d061380$var$dispatch("turbo:before-render", {
            detail: {
                newBody: newBody,
                ...options
            },
            cancelable: true
        });
    }
    notifyApplicationAfterRender(renderMethod) {
        return $dae3d0e46d061380$var$dispatch("turbo:render", {
            detail: {
                renderMethod: renderMethod
            }
        });
    }
    notifyApplicationAfterPageLoad(timing = {}) {
        return $dae3d0e46d061380$var$dispatch("turbo:load", {
            detail: {
                url: this.location.href,
                timing: timing
            }
        });
    }
    notifyApplicationAfterVisitingSamePageLocation(oldURL, newURL) {
        dispatchEvent(new HashChangeEvent("hashchange", {
            oldURL: oldURL.toString(),
            newURL: newURL.toString()
        }));
    }
    notifyApplicationAfterFrameLoad(frame) {
        return $dae3d0e46d061380$var$dispatch("turbo:frame-load", {
            target: frame
        });
    }
    notifyApplicationAfterFrameRender(fetchResponse, frame) {
        return $dae3d0e46d061380$var$dispatch("turbo:frame-render", {
            detail: {
                fetchResponse: fetchResponse
            },
            target: frame,
            cancelable: true
        });
    }
    // Helpers
    submissionIsNavigatable(form, submitter) {
        if (this.formMode == "off") return false;
        else {
            const submitterIsNavigatable = submitter ? this.elementIsNavigatable(submitter) : true;
            if (this.formMode == "optin") return submitterIsNavigatable && form.closest('[data-turbo="true"]') != null;
            else return submitterIsNavigatable && this.elementIsNavigatable(form);
        }
    }
    elementIsNavigatable(element) {
        const container = $dae3d0e46d061380$var$findClosestRecursively(element, "[data-turbo]");
        const withinFrame = $dae3d0e46d061380$var$findClosestRecursively(element, "turbo-frame");
        // Check if Drive is enabled on the session or we're within a Frame.
        if (this.drive || withinFrame) {
            // Element is navigatable by default, unless `data-turbo="false"`.
            if (container) return container.getAttribute("data-turbo") != "false";
            else return true;
        } else {
            // Element isn't navigatable by default, unless `data-turbo="true"`.
            if (container) return container.getAttribute("data-turbo") == "true";
            else return false;
        }
    }
    // Private
    getActionForLink(link) {
        return $dae3d0e46d061380$var$getVisitAction(link) || "advance";
    }
    get snapshot() {
        return this.view.snapshot;
    }
}
// Older versions of the Turbo Native adapters referenced the
// `Location#absoluteURL` property in their implementations of
// the `Adapter#visitProposedToLocation()` and `#visitStarted()`
// methods. The Location class has since been removed in favor
// of the DOM URL API, and accordingly all Adapter methods now
// receive URL objects.
//
// We alias #absoluteURL to #toString() here to avoid crashing
// older adapters which do not expect URL objects. We should
// consider removing this support at some point in the future.
function $dae3d0e46d061380$var$extendURLWithDeprecatedProperties(url) {
    Object.defineProperties(url, $dae3d0e46d061380$var$deprecatedLocationPropertyDescriptors);
}
const $dae3d0e46d061380$var$deprecatedLocationPropertyDescriptors = {
    absoluteURL: {
        get () {
            return this.toString();
        }
    }
};
const $dae3d0e46d061380$export$55427e926be628d4 = new $dae3d0e46d061380$var$Session($dae3d0e46d061380$var$recentRequests);
const { cache: $dae3d0e46d061380$export$69a3209f1a06c04d, navigator: $dae3d0e46d061380$export$ec7c8efa7f5790ae } = $dae3d0e46d061380$export$55427e926be628d4;
/**
 * Starts the main session.
 * This initialises any necessary observers such as those to monitor
 * link interactions.
 */ function $dae3d0e46d061380$export$b3571188c770cc5a() {
    $dae3d0e46d061380$export$55427e926be628d4.start();
}
/**
 * Registers an adapter for the main session.
 *
 * @param adapter Adapter to register
 */ function $dae3d0e46d061380$export$ddf4d3602a252674(adapter) {
    $dae3d0e46d061380$export$55427e926be628d4.registerAdapter(adapter);
}
/**
 * Performs an application visit to the given location.
 *
 * @param location Location to visit (a URL or path)
 * @param options Options to apply
 * @param options.action Type of history navigation to apply ("restore",
 * "replace" or "advance")
 * @param options.historyChanged Specifies whether the browser history has
 * already been changed for this visit or not
 * @param options.referrer Specifies the referrer of this visit such that
 * navigations to the same page will not result in a new history entry.
 * @param options.snapshotHTML Cached snapshot to render
 * @param options.response Response of the specified location
 */ function $dae3d0e46d061380$export$bf638b60ea8b89b7(location1, options) {
    $dae3d0e46d061380$export$55427e926be628d4.visit(location1, options);
}
/**
 * Connects a stream source to the main session.
 *
 * @param source Stream source to connect
 */ function $dae3d0e46d061380$export$83667c2845c80463(source) {
    $dae3d0e46d061380$export$55427e926be628d4.connectStreamSource(source);
}
/**
 * Disconnects a stream source from the main session.
 *
 * @param source Stream source to disconnect
 */ function $dae3d0e46d061380$export$26c56c74e3b67c59(source) {
    $dae3d0e46d061380$export$55427e926be628d4.disconnectStreamSource(source);
}
/**
 * Renders a stream message to the main session by appending it to the
 * current document.
 *
 * @param message Message to render
 */ function $dae3d0e46d061380$export$ebdee272e46e11d9(message) {
    $dae3d0e46d061380$export$55427e926be628d4.renderStreamMessage(message);
}
/**
 * Removes all entries from the Turbo Drive page cache.
 * Call this when state has changed on the server that may affect cached pages.
 *
 * @deprecated since version 7.2.0 in favor of `Turbo.cache.clear()`
 */ function $dae3d0e46d061380$export$d2adf65b87e47523() {
    console.warn("Please replace `Turbo.clearCache()` with `Turbo.cache.clear()`. The top-level function is deprecated and will be removed in a future version of Turbo.`");
    $dae3d0e46d061380$export$55427e926be628d4.clearCache();
}
/**
 * Sets the delay after which the progress bar will appear during navigation.
 *
 * The progress bar appears after 500ms by default.
 *
 * Note that this method has no effect when used with the iOS or Android
 * adapters.
 *
 * @param delay Time to delay in milliseconds
 */ function $dae3d0e46d061380$export$ddcc6947144c476c(delay) {
    $dae3d0e46d061380$export$55427e926be628d4.setProgressBarDelay(delay);
}
function $dae3d0e46d061380$export$4696f1315e6aae7c(confirmMethod) {
    $dae3d0e46d061380$var$FormSubmission.confirmMethod = confirmMethod;
}
function $dae3d0e46d061380$export$a94cfbb71d90abbd(mode) {
    $dae3d0e46d061380$export$55427e926be628d4.setFormMode(mode);
}
var $dae3d0e46d061380$var$Turbo = /*#__PURE__*/ Object.freeze({
    __proto__: null,
    navigator: $dae3d0e46d061380$export$ec7c8efa7f5790ae,
    session: $dae3d0e46d061380$export$55427e926be628d4,
    cache: $dae3d0e46d061380$export$69a3209f1a06c04d,
    PageRenderer: $dae3d0e46d061380$export$ac9642242cf7b3ce,
    PageSnapshot: $dae3d0e46d061380$export$4f11b95e473fd113,
    FrameRenderer: $dae3d0e46d061380$export$f09c4165d16bb2b7,
    fetch: $dae3d0e46d061380$export$e7aa7bc5c1b3cfb3,
    start: $dae3d0e46d061380$export$b3571188c770cc5a,
    registerAdapter: $dae3d0e46d061380$export$ddf4d3602a252674,
    visit: $dae3d0e46d061380$export$bf638b60ea8b89b7,
    connectStreamSource: $dae3d0e46d061380$export$83667c2845c80463,
    disconnectStreamSource: $dae3d0e46d061380$export$26c56c74e3b67c59,
    renderStreamMessage: $dae3d0e46d061380$export$ebdee272e46e11d9,
    clearCache: $dae3d0e46d061380$export$d2adf65b87e47523,
    setProgressBarDelay: $dae3d0e46d061380$export$ddcc6947144c476c,
    setConfirmMethod: $dae3d0e46d061380$export$4696f1315e6aae7c,
    setFormMode: $dae3d0e46d061380$export$a94cfbb71d90abbd
});
class $dae3d0e46d061380$var$TurboFrameMissingError extends Error {
}
class $dae3d0e46d061380$var$FrameController {
    fetchResponseLoaded = (_fetchResponse)=>Promise.resolve();
    #currentFetchRequest = null;
    #resolveVisitPromise = ()=>{};
    #connected = false;
    #hasBeenLoaded = false;
    #ignoredAttributes = new Set();
    action = null;
    constructor(element){
        this.element = element;
        this.view = new $dae3d0e46d061380$var$FrameView(this, this.element);
        this.appearanceObserver = new $dae3d0e46d061380$var$AppearanceObserver(this, this.element);
        this.formLinkClickObserver = new $dae3d0e46d061380$var$FormLinkClickObserver(this, this.element);
        this.linkInterceptor = new $dae3d0e46d061380$var$LinkInterceptor(this, this.element);
        this.restorationIdentifier = $dae3d0e46d061380$var$uuid();
        this.formSubmitObserver = new $dae3d0e46d061380$var$FormSubmitObserver(this, this.element);
    }
    // Frame delegate
    connect() {
        if (!this.#connected) {
            this.#connected = true;
            if (this.loadingStyle == $dae3d0e46d061380$export$65f98fefe20dbcb6.lazy) this.appearanceObserver.start();
            else this.#loadSourceURL();
            this.formLinkClickObserver.start();
            this.linkInterceptor.start();
            this.formSubmitObserver.start();
        }
    }
    disconnect() {
        if (this.#connected) {
            this.#connected = false;
            this.appearanceObserver.stop();
            this.formLinkClickObserver.stop();
            this.linkInterceptor.stop();
            this.formSubmitObserver.stop();
        }
    }
    disabledChanged() {
        if (this.loadingStyle == $dae3d0e46d061380$export$65f98fefe20dbcb6.eager) this.#loadSourceURL();
    }
    sourceURLChanged() {
        if (this.#isIgnoringChangesTo("src")) return;
        if (this.element.isConnected) this.complete = false;
        if (this.loadingStyle == $dae3d0e46d061380$export$65f98fefe20dbcb6.eager || this.#hasBeenLoaded) this.#loadSourceURL();
    }
    sourceURLReloaded() {
        const { src: src } = this.element;
        this.element.removeAttribute("complete");
        this.element.src = null;
        this.element.src = src;
        return this.element.loaded;
    }
    loadingStyleChanged() {
        if (this.loadingStyle == $dae3d0e46d061380$export$65f98fefe20dbcb6.lazy) this.appearanceObserver.start();
        else {
            this.appearanceObserver.stop();
            this.#loadSourceURL();
        }
    }
    async #loadSourceURL() {
        if (this.enabled && this.isActive && !this.complete && this.sourceURL) {
            this.element.loaded = this.#visit($dae3d0e46d061380$var$expandURL(this.sourceURL));
            this.appearanceObserver.stop();
            await this.element.loaded;
            this.#hasBeenLoaded = true;
        }
    }
    async loadResponse(fetchResponse) {
        if (fetchResponse.redirected || fetchResponse.succeeded && fetchResponse.isHTML) this.sourceURL = fetchResponse.response.url;
        try {
            const html = await fetchResponse.responseHTML;
            if (html) {
                const document1 = $dae3d0e46d061380$var$parseHTMLDocument(html);
                const pageSnapshot = $dae3d0e46d061380$export$4f11b95e473fd113.fromDocument(document1);
                if (pageSnapshot.isVisitable) await this.#loadFrameResponse(fetchResponse, document1);
                else await this.#handleUnvisitableFrameResponse(fetchResponse);
            }
        } finally{
            this.fetchResponseLoaded = ()=>Promise.resolve();
        }
    }
    // Appearance observer delegate
    elementAppearedInViewport(element) {
        this.proposeVisitIfNavigatedWithAction(element, $dae3d0e46d061380$var$getVisitAction(element));
        this.#loadSourceURL();
    }
    // Form link click observer delegate
    willSubmitFormLinkToLocation(link) {
        return this.#shouldInterceptNavigation(link);
    }
    submittedFormLinkToLocation(link, _location, form) {
        const frame = this.#findFrameElement(link);
        if (frame) form.setAttribute("data-turbo-frame", frame.id);
    }
    // Link interceptor delegate
    shouldInterceptLinkClick(element, _location, _event) {
        return this.#shouldInterceptNavigation(element);
    }
    linkClickIntercepted(element, location1) {
        this.#navigateFrame(element, location1);
    }
    // Form submit observer delegate
    willSubmitForm(element, submitter) {
        return element.closest("turbo-frame") == this.element && this.#shouldInterceptNavigation(element, submitter);
    }
    formSubmitted(element, submitter) {
        if (this.formSubmission) this.formSubmission.stop();
        this.formSubmission = new $dae3d0e46d061380$var$FormSubmission(this, element, submitter);
        const { fetchRequest: fetchRequest } = this.formSubmission;
        this.prepareRequest(fetchRequest);
        this.formSubmission.start();
    }
    // Fetch request delegate
    prepareRequest(request) {
        request.headers["Turbo-Frame"] = this.id;
        if (this.currentNavigationElement?.hasAttribute("data-turbo-stream")) request.acceptResponseType($dae3d0e46d061380$var$StreamMessage.contentType);
    }
    requestStarted(_request) {
        $dae3d0e46d061380$var$markAsBusy(this.element);
    }
    requestPreventedHandlingResponse(_request, _response) {
        this.#resolveVisitPromise();
    }
    async requestSucceededWithResponse(request, response) {
        await this.loadResponse(response);
        this.#resolveVisitPromise();
    }
    async requestFailedWithResponse(request, response) {
        await this.loadResponse(response);
        this.#resolveVisitPromise();
    }
    requestErrored(request, error) {
        console.error(error);
        this.#resolveVisitPromise();
    }
    requestFinished(_request) {
        $dae3d0e46d061380$var$clearBusyState(this.element);
    }
    // Form submission delegate
    formSubmissionStarted({ formElement: formElement }) {
        $dae3d0e46d061380$var$markAsBusy(formElement, this.#findFrameElement(formElement));
    }
    formSubmissionSucceededWithResponse(formSubmission, response) {
        const frame = this.#findFrameElement(formSubmission.formElement, formSubmission.submitter);
        frame.delegate.proposeVisitIfNavigatedWithAction(frame, $dae3d0e46d061380$var$getVisitAction(formSubmission.submitter, formSubmission.formElement, frame));
        frame.delegate.loadResponse(response);
        if (!formSubmission.isSafe) $dae3d0e46d061380$export$55427e926be628d4.clearCache();
    }
    formSubmissionFailedWithResponse(formSubmission, fetchResponse) {
        this.element.delegate.loadResponse(fetchResponse);
        $dae3d0e46d061380$export$55427e926be628d4.clearCache();
    }
    formSubmissionErrored(formSubmission, error) {
        console.error(error);
    }
    formSubmissionFinished({ formElement: formElement }) {
        $dae3d0e46d061380$var$clearBusyState(formElement, this.#findFrameElement(formElement));
    }
    // View delegate
    allowsImmediateRender({ element: newFrame }, options) {
        const event = $dae3d0e46d061380$var$dispatch("turbo:before-frame-render", {
            target: this.element,
            detail: {
                newFrame: newFrame,
                ...options
            },
            cancelable: true
        });
        const { defaultPrevented: defaultPrevented, detail: { render: render } } = event;
        if (this.view.renderer && render) this.view.renderer.renderElement = render;
        return !defaultPrevented;
    }
    viewRenderedSnapshot(_snapshot, _isPreview, _renderMethod) {}
    preloadOnLoadLinksForView(element) {
        $dae3d0e46d061380$export$55427e926be628d4.preloadOnLoadLinksForView(element);
    }
    viewInvalidated() {}
    // Frame renderer delegate
    willRenderFrame(currentElement, _newElement) {
        this.previousFrameElement = currentElement.cloneNode(true);
    }
    visitCachedSnapshot = ({ element: element })=>{
        const frame = element.querySelector("#" + this.element.id);
        if (frame && this.previousFrameElement) frame.replaceChildren(...this.previousFrameElement.children);
        delete this.previousFrameElement;
    };
    // Private
    async #loadFrameResponse(fetchResponse, document1) {
        const newFrameElement = await this.extractForeignFrameElement(document1.body);
        if (newFrameElement) {
            const snapshot = new $dae3d0e46d061380$var$Snapshot(newFrameElement);
            const renderer = new $dae3d0e46d061380$export$f09c4165d16bb2b7(this, this.view.snapshot, snapshot, $dae3d0e46d061380$export$f09c4165d16bb2b7.renderElement, false, false);
            if (this.view.renderPromise) await this.view.renderPromise;
            this.changeHistory();
            await this.view.render(renderer);
            this.complete = true;
            $dae3d0e46d061380$export$55427e926be628d4.frameRendered(fetchResponse, this.element);
            $dae3d0e46d061380$export$55427e926be628d4.frameLoaded(this.element);
            await this.fetchResponseLoaded(fetchResponse);
        } else if (this.#willHandleFrameMissingFromResponse(fetchResponse)) this.#handleFrameMissingFromResponse(fetchResponse);
    }
    async #visit(url) {
        const request = new $dae3d0e46d061380$export$319b96fe59834522(this, $dae3d0e46d061380$export$76f6869e29ff06ee.get, url, new URLSearchParams(), this.element);
        this.#currentFetchRequest?.cancel();
        this.#currentFetchRequest = request;
        return new Promise((resolve)=>{
            this.#resolveVisitPromise = ()=>{
                this.#resolveVisitPromise = ()=>{};
                this.#currentFetchRequest = null;
                resolve();
            };
            request.perform();
        });
    }
    #navigateFrame(element, url, submitter) {
        const frame = this.#findFrameElement(element, submitter);
        frame.delegate.proposeVisitIfNavigatedWithAction(frame, $dae3d0e46d061380$var$getVisitAction(submitter, element, frame));
        this.#withCurrentNavigationElement(element, ()=>{
            frame.src = url;
        });
    }
    proposeVisitIfNavigatedWithAction(frame, action = null) {
        this.action = action;
        if (this.action) {
            const pageSnapshot = $dae3d0e46d061380$export$4f11b95e473fd113.fromElement(frame).clone();
            const { visitCachedSnapshot: visitCachedSnapshot } = frame.delegate;
            frame.delegate.fetchResponseLoaded = async (fetchResponse)=>{
                if (frame.src) {
                    const { statusCode: statusCode, redirected: redirected } = fetchResponse;
                    const responseHTML = await fetchResponse.responseHTML;
                    const response = {
                        statusCode: statusCode,
                        redirected: redirected,
                        responseHTML: responseHTML
                    };
                    const options = {
                        response: response,
                        visitCachedSnapshot: visitCachedSnapshot,
                        willRender: false,
                        updateHistory: false,
                        restorationIdentifier: this.restorationIdentifier,
                        snapshot: pageSnapshot
                    };
                    if (this.action) options.action = this.action;
                    $dae3d0e46d061380$export$55427e926be628d4.visit(frame.src, options);
                }
            };
        }
    }
    changeHistory() {
        if (this.action) {
            const method = $dae3d0e46d061380$var$getHistoryMethodForAction(this.action);
            $dae3d0e46d061380$export$55427e926be628d4.history.update(method, $dae3d0e46d061380$var$expandURL(this.element.src || ""), this.restorationIdentifier);
        }
    }
    async #handleUnvisitableFrameResponse(fetchResponse) {
        console.warn(`The response (${fetchResponse.statusCode}) from <turbo-frame id="${this.element.id}"> is performing a full page visit due to turbo-visit-control.`);
        await this.#visitResponse(fetchResponse.response);
    }
    #willHandleFrameMissingFromResponse(fetchResponse) {
        this.element.setAttribute("complete", "");
        const response = fetchResponse.response;
        const visit = async (url, options)=>{
            if (url instanceof Response) this.#visitResponse(url);
            else $dae3d0e46d061380$export$55427e926be628d4.visit(url, options);
        };
        const event = $dae3d0e46d061380$var$dispatch("turbo:frame-missing", {
            target: this.element,
            detail: {
                response: response,
                visit: visit
            },
            cancelable: true
        });
        return !event.defaultPrevented;
    }
    #handleFrameMissingFromResponse(fetchResponse) {
        this.view.missing();
        this.#throwFrameMissingError(fetchResponse);
    }
    #throwFrameMissingError(fetchResponse) {
        const message = `The response (${fetchResponse.statusCode}) did not contain the expected <turbo-frame id="${this.element.id}"> and will be ignored. To perform a full page visit instead, set turbo-visit-control to reload.`;
        throw new $dae3d0e46d061380$var$TurboFrameMissingError(message);
    }
    async #visitResponse(response) {
        const wrapped = new $dae3d0e46d061380$export$3ae09bba5b6b906e(response);
        const responseHTML = await wrapped.responseHTML;
        const { location: location1, redirected: redirected, statusCode: statusCode } = wrapped;
        return $dae3d0e46d061380$export$55427e926be628d4.visit(location1, {
            response: {
                redirected: redirected,
                statusCode: statusCode,
                responseHTML: responseHTML
            }
        });
    }
    #findFrameElement(element, submitter) {
        const id = $dae3d0e46d061380$var$getAttribute("data-turbo-frame", submitter, element) || this.element.getAttribute("target");
        return $dae3d0e46d061380$var$getFrameElementById(id) ?? this.element;
    }
    async extractForeignFrameElement(container) {
        let element;
        const id = CSS.escape(this.id);
        try {
            element = $dae3d0e46d061380$var$activateElement(container.querySelector(`turbo-frame#${id}`), this.sourceURL);
            if (element) return element;
            element = $dae3d0e46d061380$var$activateElement(container.querySelector(`turbo-frame[src][recurse~=${id}]`), this.sourceURL);
            if (element) {
                await element.loaded;
                return await this.extractForeignFrameElement(element);
            }
        } catch (error) {
            console.error(error);
            return new $dae3d0e46d061380$export$f464a8f0ffa32c44();
        }
        return null;
    }
    #formActionIsVisitable(form, submitter) {
        const action = $dae3d0e46d061380$var$getAction$1(form, submitter);
        return $dae3d0e46d061380$var$locationIsVisitable($dae3d0e46d061380$var$expandURL(action), this.rootLocation);
    }
    #shouldInterceptNavigation(element, submitter) {
        const id = $dae3d0e46d061380$var$getAttribute("data-turbo-frame", submitter, element) || this.element.getAttribute("target");
        if (element instanceof HTMLFormElement && !this.#formActionIsVisitable(element, submitter)) return false;
        if (!this.enabled || id == "_top") return false;
        if (id) {
            const frameElement = $dae3d0e46d061380$var$getFrameElementById(id);
            if (frameElement) return !frameElement.disabled;
        }
        if (!$dae3d0e46d061380$export$55427e926be628d4.elementIsNavigatable(element)) return false;
        if (submitter && !$dae3d0e46d061380$export$55427e926be628d4.elementIsNavigatable(submitter)) return false;
        return true;
    }
    // Computed properties
    get id() {
        return this.element.id;
    }
    get enabled() {
        return !this.element.disabled;
    }
    get sourceURL() {
        if (this.element.src) return this.element.src;
    }
    set sourceURL(sourceURL) {
        this.#ignoringChangesToAttribute("src", ()=>{
            this.element.src = sourceURL ?? null;
        });
    }
    get loadingStyle() {
        return this.element.loading;
    }
    get isLoading() {
        return this.formSubmission !== undefined || this.#resolveVisitPromise() !== undefined;
    }
    get complete() {
        return this.element.hasAttribute("complete");
    }
    set complete(value) {
        if (value) this.element.setAttribute("complete", "");
        else this.element.removeAttribute("complete");
    }
    get isActive() {
        return this.element.isActive && this.#connected;
    }
    get rootLocation() {
        const meta = this.element.ownerDocument.querySelector(`meta[name="turbo-root"]`);
        const root = meta?.content ?? "/";
        return $dae3d0e46d061380$var$expandURL(root);
    }
    #isIgnoringChangesTo(attributeName) {
        return this.#ignoredAttributes.has(attributeName);
    }
    #ignoringChangesToAttribute(attributeName, callback) {
        this.#ignoredAttributes.add(attributeName);
        callback();
        this.#ignoredAttributes.delete(attributeName);
    }
    #withCurrentNavigationElement(element, callback) {
        this.currentNavigationElement = element;
        callback();
        delete this.currentNavigationElement;
    }
}
function $dae3d0e46d061380$var$getFrameElementById(id) {
    if (id != null) {
        const element = document.getElementById(id);
        if (element instanceof $dae3d0e46d061380$export$f464a8f0ffa32c44) return element;
    }
}
function $dae3d0e46d061380$var$activateElement(element, currentURL) {
    if (element) {
        const src = element.getAttribute("src");
        if (src != null && currentURL != null && $dae3d0e46d061380$var$urlsAreEqual(src, currentURL)) throw new Error(`Matching <turbo-frame id="${element.id}"> element has a source URL which references itself`);
        if (element.ownerDocument !== document) element = document.importNode(element, true);
        if (element instanceof $dae3d0e46d061380$export$f464a8f0ffa32c44) {
            element.connectedCallback();
            element.disconnectedCallback();
            return element;
        }
    }
}
const $dae3d0e46d061380$export$1b814a67c3a57eeb = {
    after () {
        this.targetElements.forEach((e)=>e.parentElement?.insertBefore(this.templateContent, e.nextSibling));
    },
    append () {
        this.removeDuplicateTargetChildren();
        this.targetElements.forEach((e)=>e.append(this.templateContent));
    },
    before () {
        this.targetElements.forEach((e)=>e.parentElement?.insertBefore(this.templateContent, e));
    },
    prepend () {
        this.removeDuplicateTargetChildren();
        this.targetElements.forEach((e)=>e.prepend(this.templateContent));
    },
    remove () {
        this.targetElements.forEach((e)=>e.remove());
    },
    replace () {
        const method = this.getAttribute("method");
        this.targetElements.forEach((targetElement)=>{
            if (method === "morph") $dae3d0e46d061380$var$morphElements(targetElement, this.templateContent);
            else targetElement.replaceWith(this.templateContent);
        });
    },
    update () {
        const method = this.getAttribute("method");
        this.targetElements.forEach((targetElement)=>{
            if (method === "morph") $dae3d0e46d061380$var$morphChildren(targetElement, this.templateContent);
            else {
                targetElement.innerHTML = "";
                targetElement.append(this.templateContent);
            }
        });
    },
    refresh () {
        $dae3d0e46d061380$export$55427e926be628d4.refresh(this.baseURI, this.requestId);
    }
};
// <turbo-stream action=replace target=id><template>...
/**
 * Renders updates to the page from a stream of messages.
 *
 * Using the `action` attribute, this can be configured one of eight ways:
 *
 * - `after` - inserts the result after the target
 * - `append` - appends the result to the target
 * - `before` - inserts the result before the target
 * - `prepend` - prepends the result to the target
 * - `refresh` - initiates a page refresh
 * - `remove` - removes the target
 * - `replace` - replaces the outer HTML of the target
 * - `update` - replaces the inner HTML of the target
 *
 * @customElement turbo-stream
 * @example
 *   <turbo-stream action="append" target="dom_id">
 *     <template>
 *       Content to append to target designated with the dom_id.
 *     </template>
 *   </turbo-stream>
 */ class $dae3d0e46d061380$export$144d499d37929ea8 extends HTMLElement {
    static async renderElement(newElement) {
        await newElement.performAction();
    }
    async connectedCallback() {
        try {
            await this.render();
        } catch (error) {
            console.error(error);
        } finally{
            this.disconnect();
        }
    }
    async render() {
        return this.renderPromise ??= (async ()=>{
            const event = this.beforeRenderEvent;
            if (this.dispatchEvent(event)) {
                await $dae3d0e46d061380$var$nextRepaint();
                await event.detail.render(this);
            }
        })();
    }
    disconnect() {
        try {
            this.remove();
        // eslint-disable-next-line no-empty
        } catch  {}
    }
    /**
   * Removes duplicate children (by ID)
   */ removeDuplicateTargetChildren() {
        this.duplicateChildren.forEach((c)=>c.remove());
    }
    /**
   * Gets the list of duplicate children (i.e. those with the same ID)
   */ get duplicateChildren() {
        const existingChildren = this.targetElements.flatMap((e)=>[
                ...e.children
            ]).filter((c)=>!!c.id);
        const newChildrenIds = [
            ...this.templateContent?.children || []
        ].filter((c)=>!!c.id).map((c)=>c.id);
        return existingChildren.filter((c)=>newChildrenIds.includes(c.id));
    }
    /**
   * Gets the action function to be performed.
   */ get performAction() {
        if (this.action) {
            const actionFunction = $dae3d0e46d061380$export$1b814a67c3a57eeb[this.action];
            if (actionFunction) return actionFunction;
            this.#raise("unknown action");
        }
        this.#raise("action attribute is missing");
    }
    /**
   * Gets the target elements which the template will be rendered to.
   */ get targetElements() {
        if (this.target) return this.targetElementsById;
        else if (this.targets) return this.targetElementsByQuery;
        else this.#raise("target or targets attribute is missing");
    }
    /**
   * Gets the contents of the main `<template>`.
   */ get templateContent() {
        return this.templateElement.content.cloneNode(true);
    }
    /**
   * Gets the main `<template>` used for rendering
   */ get templateElement() {
        if (this.firstElementChild === null) {
            const template = this.ownerDocument.createElement("template");
            this.appendChild(template);
            return template;
        } else if (this.firstElementChild instanceof HTMLTemplateElement) return this.firstElementChild;
        this.#raise("first child element must be a <template> element");
    }
    /**
   * Gets the current action.
   */ get action() {
        return this.getAttribute("action");
    }
    /**
   * Gets the current target (an element ID) to which the result will
   * be rendered.
   */ get target() {
        return this.getAttribute("target");
    }
    /**
   * Gets the current "targets" selector (a CSS selector)
   */ get targets() {
        return this.getAttribute("targets");
    }
    /**
   * Reads the request-id attribute
   */ get requestId() {
        return this.getAttribute("request-id");
    }
    #raise(message) {
        throw new Error(`${this.description}: ${message}`);
    }
    get description() {
        return (this.outerHTML.match(/<[^>]+>/) ?? [])[0] ?? "<turbo-stream>";
    }
    get beforeRenderEvent() {
        return new CustomEvent("turbo:before-stream-render", {
            bubbles: true,
            cancelable: true,
            detail: {
                newStream: this,
                render: $dae3d0e46d061380$export$144d499d37929ea8.renderElement
            }
        });
    }
    get targetElementsById() {
        const element = this.ownerDocument?.getElementById(this.target);
        if (element !== null) return [
            element
        ];
        else return [];
    }
    get targetElementsByQuery() {
        const elements = this.ownerDocument?.querySelectorAll(this.targets);
        if (elements.length !== 0) return Array.prototype.slice.call(elements);
        else return [];
    }
}
class $dae3d0e46d061380$export$e87d51a4385a3903 extends HTMLElement {
    streamSource = null;
    connectedCallback() {
        this.streamSource = this.src.match(/^ws{1,2}:/) ? new WebSocket(this.src) : new EventSource(this.src);
        $dae3d0e46d061380$export$83667c2845c80463(this.streamSource);
    }
    disconnectedCallback() {
        if (this.streamSource) {
            this.streamSource.close();
            $dae3d0e46d061380$export$26c56c74e3b67c59(this.streamSource);
        }
    }
    get src() {
        return this.getAttribute("src") || "";
    }
}
$dae3d0e46d061380$export$f464a8f0ffa32c44.delegateConstructor = $dae3d0e46d061380$var$FrameController;
if (customElements.get("turbo-frame") === undefined) customElements.define("turbo-frame", $dae3d0e46d061380$export$f464a8f0ffa32c44);
if (customElements.get("turbo-stream") === undefined) customElements.define("turbo-stream", $dae3d0e46d061380$export$144d499d37929ea8);
if (customElements.get("turbo-stream-source") === undefined) customElements.define("turbo-stream-source", $dae3d0e46d061380$export$e87d51a4385a3903);
(()=>{
    let element = document.currentScript;
    if (!element) return;
    if (element.hasAttribute("data-turbo-suppress-warning")) return;
    element = element.parentElement;
    while(element){
        if (element == document.body) return console.warn($dae3d0e46d061380$var$unindent`
        You are loading Turbo from a <script> element inside the <body> element. This is probably not what you meant to do!

        Load your application’s JavaScript bundle inside the <head> element instead. <script> elements in <body> are evaluated with each page change.

        For more information, see: https://turbo.hotwired.dev/handbook/building#working-with-script-elements

        ——
        Suppress this warning by adding a "data-turbo-suppress-warning" attribute to: %s
      `, element.outerHTML);
        element = element.parentElement;
    }
})();
window.Turbo = {
    ...$dae3d0e46d061380$var$Turbo,
    StreamActions: $dae3d0e46d061380$export$1b814a67c3a57eeb
};
$dae3d0e46d061380$export$b3571188c770cc5a();


/*
Stimulus 3.2.1
Copyright © 2023 Basecamp, LLC
 */ class $1e1b4d625d583414$var$EventListener {
    constructor(eventTarget, eventName, eventOptions){
        this.eventTarget = eventTarget;
        this.eventName = eventName;
        this.eventOptions = eventOptions;
        this.unorderedBindings = new Set();
    }
    connect() {
        this.eventTarget.addEventListener(this.eventName, this, this.eventOptions);
    }
    disconnect() {
        this.eventTarget.removeEventListener(this.eventName, this, this.eventOptions);
    }
    bindingConnected(binding) {
        this.unorderedBindings.add(binding);
    }
    bindingDisconnected(binding) {
        this.unorderedBindings.delete(binding);
    }
    handleEvent(event) {
        const extendedEvent = $1e1b4d625d583414$var$extendEvent(event);
        for (const binding of this.bindings){
            if (extendedEvent.immediatePropagationStopped) break;
            else binding.handleEvent(extendedEvent);
        }
    }
    hasBindings() {
        return this.unorderedBindings.size > 0;
    }
    get bindings() {
        return Array.from(this.unorderedBindings).sort((left, right)=>{
            const leftIndex = left.index, rightIndex = right.index;
            return leftIndex < rightIndex ? -1 : leftIndex > rightIndex ? 1 : 0;
        });
    }
}
function $1e1b4d625d583414$var$extendEvent(event) {
    if ("immediatePropagationStopped" in event) return event;
    else {
        const { stopImmediatePropagation: stopImmediatePropagation } = event;
        return Object.assign(event, {
            immediatePropagationStopped: false,
            stopImmediatePropagation () {
                this.immediatePropagationStopped = true;
                stopImmediatePropagation.call(this);
            }
        });
    }
}
class $1e1b4d625d583414$var$Dispatcher {
    constructor(application){
        this.application = application;
        this.eventListenerMaps = new Map();
        this.started = false;
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.eventListeners.forEach((eventListener)=>eventListener.connect());
        }
    }
    stop() {
        if (this.started) {
            this.started = false;
            this.eventListeners.forEach((eventListener)=>eventListener.disconnect());
        }
    }
    get eventListeners() {
        return Array.from(this.eventListenerMaps.values()).reduce((listeners, map)=>listeners.concat(Array.from(map.values())), []);
    }
    bindingConnected(binding) {
        this.fetchEventListenerForBinding(binding).bindingConnected(binding);
    }
    bindingDisconnected(binding, clearEventListeners = false) {
        this.fetchEventListenerForBinding(binding).bindingDisconnected(binding);
        if (clearEventListeners) this.clearEventListenersForBinding(binding);
    }
    handleError(error, message, detail = {}) {
        this.application.handleError(error, `Error ${message}`, detail);
    }
    clearEventListenersForBinding(binding) {
        const eventListener = this.fetchEventListenerForBinding(binding);
        if (!eventListener.hasBindings()) {
            eventListener.disconnect();
            this.removeMappedEventListenerFor(binding);
        }
    }
    removeMappedEventListenerFor(binding) {
        const { eventTarget: eventTarget, eventName: eventName, eventOptions: eventOptions } = binding;
        const eventListenerMap = this.fetchEventListenerMapForEventTarget(eventTarget);
        const cacheKey = this.cacheKey(eventName, eventOptions);
        eventListenerMap.delete(cacheKey);
        if (eventListenerMap.size == 0) this.eventListenerMaps.delete(eventTarget);
    }
    fetchEventListenerForBinding(binding) {
        const { eventTarget: eventTarget, eventName: eventName, eventOptions: eventOptions } = binding;
        return this.fetchEventListener(eventTarget, eventName, eventOptions);
    }
    fetchEventListener(eventTarget, eventName, eventOptions) {
        const eventListenerMap = this.fetchEventListenerMapForEventTarget(eventTarget);
        const cacheKey = this.cacheKey(eventName, eventOptions);
        let eventListener = eventListenerMap.get(cacheKey);
        if (!eventListener) {
            eventListener = this.createEventListener(eventTarget, eventName, eventOptions);
            eventListenerMap.set(cacheKey, eventListener);
        }
        return eventListener;
    }
    createEventListener(eventTarget, eventName, eventOptions) {
        const eventListener = new $1e1b4d625d583414$var$EventListener(eventTarget, eventName, eventOptions);
        if (this.started) eventListener.connect();
        return eventListener;
    }
    fetchEventListenerMapForEventTarget(eventTarget) {
        let eventListenerMap = this.eventListenerMaps.get(eventTarget);
        if (!eventListenerMap) {
            eventListenerMap = new Map();
            this.eventListenerMaps.set(eventTarget, eventListenerMap);
        }
        return eventListenerMap;
    }
    cacheKey(eventName, eventOptions) {
        const parts = [
            eventName
        ];
        Object.keys(eventOptions).sort().forEach((key)=>{
            parts.push(`${eventOptions[key] ? "" : "!"}${key}`);
        });
        return parts.join(":");
    }
}
const $1e1b4d625d583414$var$defaultActionDescriptorFilters = {
    stop ({ event: event, value: value }) {
        if (value) event.stopPropagation();
        return true;
    },
    prevent ({ event: event, value: value }) {
        if (value) event.preventDefault();
        return true;
    },
    self ({ event: event, value: value, element: element }) {
        if (value) return element === event.target;
        else return true;
    }
};
const $1e1b4d625d583414$var$descriptorPattern = /^(?:(?:([^.]+?)\+)?(.+?)(?:\.(.+?))?(?:@(window|document))?->)?(.+?)(?:#([^:]+?))(?::(.+))?$/;
function $1e1b4d625d583414$var$parseActionDescriptorString(descriptorString) {
    const source = descriptorString.trim();
    const matches = source.match($1e1b4d625d583414$var$descriptorPattern) || [];
    let eventName = matches[2];
    let keyFilter = matches[3];
    if (keyFilter && ![
        "keydown",
        "keyup",
        "keypress"
    ].includes(eventName)) {
        eventName += `.${keyFilter}`;
        keyFilter = "";
    }
    return {
        eventTarget: $1e1b4d625d583414$var$parseEventTarget(matches[4]),
        eventName: eventName,
        eventOptions: matches[7] ? $1e1b4d625d583414$var$parseEventOptions(matches[7]) : {},
        identifier: matches[5],
        methodName: matches[6],
        keyFilter: matches[1] || keyFilter
    };
}
function $1e1b4d625d583414$var$parseEventTarget(eventTargetName) {
    if (eventTargetName == "window") return window;
    else if (eventTargetName == "document") return document;
}
function $1e1b4d625d583414$var$parseEventOptions(eventOptions) {
    return eventOptions.split(":").reduce((options, token)=>Object.assign(options, {
            [token.replace(/^!/, "")]: !/^!/.test(token)
        }), {});
}
function $1e1b4d625d583414$var$stringifyEventTarget(eventTarget) {
    if (eventTarget == window) return "window";
    else if (eventTarget == document) return "document";
}
function $1e1b4d625d583414$var$camelize(value) {
    return value.replace(/(?:[_-])([a-z0-9])/g, (_, char)=>char.toUpperCase());
}
function $1e1b4d625d583414$var$namespaceCamelize(value) {
    return $1e1b4d625d583414$var$camelize(value.replace(/--/g, "-").replace(/__/g, "_"));
}
function $1e1b4d625d583414$var$capitalize(value) {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
function $1e1b4d625d583414$var$dasherize(value) {
    return value.replace(/([A-Z])/g, (_, char)=>`-${char.toLowerCase()}`);
}
function $1e1b4d625d583414$var$tokenize(value) {
    return value.match(/[^\s]+/g) || [];
}
function $1e1b4d625d583414$var$isSomething(object) {
    return object !== null && object !== undefined;
}
function $1e1b4d625d583414$var$hasProperty(object, property) {
    return Object.prototype.hasOwnProperty.call(object, property);
}
const $1e1b4d625d583414$var$allModifiers = [
    "meta",
    "ctrl",
    "alt",
    "shift"
];
class $1e1b4d625d583414$var$Action {
    constructor(element, index, descriptor, schema){
        this.element = element;
        this.index = index;
        this.eventTarget = descriptor.eventTarget || element;
        this.eventName = descriptor.eventName || $1e1b4d625d583414$var$getDefaultEventNameForElement(element) || $1e1b4d625d583414$var$error("missing event name");
        this.eventOptions = descriptor.eventOptions || {};
        this.identifier = descriptor.identifier || $1e1b4d625d583414$var$error("missing identifier");
        this.methodName = descriptor.methodName || $1e1b4d625d583414$var$error("missing method name");
        this.keyFilter = descriptor.keyFilter || "";
        this.schema = schema;
    }
    static forToken(token, schema) {
        return new this(token.element, token.index, $1e1b4d625d583414$var$parseActionDescriptorString(token.content), schema);
    }
    toString() {
        const eventFilter = this.keyFilter ? `.${this.keyFilter}` : "";
        const eventTarget = this.eventTargetName ? `@${this.eventTargetName}` : "";
        return `${this.eventName}${eventFilter}${eventTarget}->${this.identifier}#${this.methodName}`;
    }
    shouldIgnoreKeyboardEvent(event) {
        if (!this.keyFilter) return false;
        const filters = this.keyFilter.split("+");
        if (this.keyFilterDissatisfied(event, filters)) return true;
        const standardFilter = filters.filter((key)=>!$1e1b4d625d583414$var$allModifiers.includes(key))[0];
        if (!standardFilter) return false;
        if (!$1e1b4d625d583414$var$hasProperty(this.keyMappings, standardFilter)) $1e1b4d625d583414$var$error(`contains unknown key filter: ${this.keyFilter}`);
        return this.keyMappings[standardFilter].toLowerCase() !== event.key.toLowerCase();
    }
    shouldIgnoreMouseEvent(event) {
        if (!this.keyFilter) return false;
        const filters = [
            this.keyFilter
        ];
        if (this.keyFilterDissatisfied(event, filters)) return true;
        return false;
    }
    get params() {
        const params = {};
        const pattern = new RegExp(`^data-${this.identifier}-(.+)-param$`, "i");
        for (const { name: name, value: value } of Array.from(this.element.attributes)){
            const match = name.match(pattern);
            const key = match && match[1];
            if (key) params[$1e1b4d625d583414$var$camelize(key)] = $1e1b4d625d583414$var$typecast(value);
        }
        return params;
    }
    get eventTargetName() {
        return $1e1b4d625d583414$var$stringifyEventTarget(this.eventTarget);
    }
    get keyMappings() {
        return this.schema.keyMappings;
    }
    keyFilterDissatisfied(event, filters) {
        const [meta, ctrl, alt, shift] = $1e1b4d625d583414$var$allModifiers.map((modifier)=>filters.includes(modifier));
        return event.metaKey !== meta || event.ctrlKey !== ctrl || event.altKey !== alt || event.shiftKey !== shift;
    }
}
const $1e1b4d625d583414$var$defaultEventNames = {
    a: ()=>"click",
    button: ()=>"click",
    form: ()=>"submit",
    details: ()=>"toggle",
    input: (e)=>e.getAttribute("type") == "submit" ? "click" : "input",
    select: ()=>"change",
    textarea: ()=>"input"
};
function $1e1b4d625d583414$var$getDefaultEventNameForElement(element) {
    const tagName = element.tagName.toLowerCase();
    if (tagName in $1e1b4d625d583414$var$defaultEventNames) return $1e1b4d625d583414$var$defaultEventNames[tagName](element);
}
function $1e1b4d625d583414$var$error(message) {
    throw new Error(message);
}
function $1e1b4d625d583414$var$typecast(value) {
    try {
        return JSON.parse(value);
    } catch (o_O) {
        return value;
    }
}
class $1e1b4d625d583414$var$Binding {
    constructor(context, action){
        this.context = context;
        this.action = action;
    }
    get index() {
        return this.action.index;
    }
    get eventTarget() {
        return this.action.eventTarget;
    }
    get eventOptions() {
        return this.action.eventOptions;
    }
    get identifier() {
        return this.context.identifier;
    }
    handleEvent(event) {
        const actionEvent = this.prepareActionEvent(event);
        if (this.willBeInvokedByEvent(event) && this.applyEventModifiers(actionEvent)) this.invokeWithEvent(actionEvent);
    }
    get eventName() {
        return this.action.eventName;
    }
    get method() {
        const method = this.controller[this.methodName];
        if (typeof method == "function") return method;
        throw new Error(`Action "${this.action}" references undefined method "${this.methodName}"`);
    }
    applyEventModifiers(event) {
        const { element: element } = this.action;
        const { actionDescriptorFilters: actionDescriptorFilters } = this.context.application;
        const { controller: controller } = this.context;
        let passes = true;
        for (const [name, value] of Object.entries(this.eventOptions)){
            if (name in actionDescriptorFilters) {
                const filter = actionDescriptorFilters[name];
                passes = passes && filter({
                    name: name,
                    value: value,
                    event: event,
                    element: element,
                    controller: controller
                });
            } else continue;
        }
        return passes;
    }
    prepareActionEvent(event) {
        return Object.assign(event, {
            params: this.action.params
        });
    }
    invokeWithEvent(event) {
        const { target: target, currentTarget: currentTarget } = event;
        try {
            this.method.call(this.controller, event);
            this.context.logDebugActivity(this.methodName, {
                event: event,
                target: target,
                currentTarget: currentTarget,
                action: this.methodName
            });
        } catch (error) {
            const { identifier: identifier, controller: controller, element: element, index: index } = this;
            const detail = {
                identifier: identifier,
                controller: controller,
                element: element,
                index: index,
                event: event
            };
            this.context.handleError(error, `invoking action "${this.action}"`, detail);
        }
    }
    willBeInvokedByEvent(event) {
        const eventTarget = event.target;
        if (event instanceof KeyboardEvent && this.action.shouldIgnoreKeyboardEvent(event)) return false;
        if (event instanceof MouseEvent && this.action.shouldIgnoreMouseEvent(event)) return false;
        if (this.element === eventTarget) return true;
        else if (eventTarget instanceof Element && this.element.contains(eventTarget)) return this.scope.containsElement(eventTarget);
        else return this.scope.containsElement(this.action.element);
    }
    get controller() {
        return this.context.controller;
    }
    get methodName() {
        return this.action.methodName;
    }
    get element() {
        return this.scope.element;
    }
    get scope() {
        return this.context.scope;
    }
}
class $1e1b4d625d583414$export$12e07a2a9f87578a {
    constructor(element, delegate){
        this.mutationObserverInit = {
            attributes: true,
            childList: true,
            subtree: true
        };
        this.element = element;
        this.started = false;
        this.delegate = delegate;
        this.elements = new Set();
        this.mutationObserver = new MutationObserver((mutations)=>this.processMutations(mutations));
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.mutationObserver.observe(this.element, this.mutationObserverInit);
            this.refresh();
        }
    }
    pause(callback) {
        if (this.started) {
            this.mutationObserver.disconnect();
            this.started = false;
        }
        callback();
        if (!this.started) {
            this.mutationObserver.observe(this.element, this.mutationObserverInit);
            this.started = true;
        }
    }
    stop() {
        if (this.started) {
            this.mutationObserver.takeRecords();
            this.mutationObserver.disconnect();
            this.started = false;
        }
    }
    refresh() {
        if (this.started) {
            const matches = new Set(this.matchElementsInTree());
            for (const element of Array.from(this.elements))if (!matches.has(element)) this.removeElement(element);
            for (const element of Array.from(matches))this.addElement(element);
        }
    }
    processMutations(mutations) {
        if (this.started) for (const mutation of mutations)this.processMutation(mutation);
    }
    processMutation(mutation) {
        if (mutation.type == "attributes") this.processAttributeChange(mutation.target, mutation.attributeName);
        else if (mutation.type == "childList") {
            this.processRemovedNodes(mutation.removedNodes);
            this.processAddedNodes(mutation.addedNodes);
        }
    }
    processAttributeChange(element, attributeName) {
        if (this.elements.has(element)) {
            if (this.delegate.elementAttributeChanged && this.matchElement(element)) this.delegate.elementAttributeChanged(element, attributeName);
            else this.removeElement(element);
        } else if (this.matchElement(element)) this.addElement(element);
    }
    processRemovedNodes(nodes) {
        for (const node of Array.from(nodes)){
            const element = this.elementFromNode(node);
            if (element) this.processTree(element, this.removeElement);
        }
    }
    processAddedNodes(nodes) {
        for (const node of Array.from(nodes)){
            const element = this.elementFromNode(node);
            if (element && this.elementIsActive(element)) this.processTree(element, this.addElement);
        }
    }
    matchElement(element) {
        return this.delegate.matchElement(element);
    }
    matchElementsInTree(tree = this.element) {
        return this.delegate.matchElementsInTree(tree);
    }
    processTree(tree, processor) {
        for (const element of this.matchElementsInTree(tree))processor.call(this, element);
    }
    elementFromNode(node) {
        if (node.nodeType == Node.ELEMENT_NODE) return node;
    }
    elementIsActive(element) {
        if (element.isConnected != this.element.isConnected) return false;
        else return this.element.contains(element);
    }
    addElement(element) {
        if (!this.elements.has(element)) {
            if (this.elementIsActive(element)) {
                this.elements.add(element);
                if (this.delegate.elementMatched) this.delegate.elementMatched(element);
            }
        }
    }
    removeElement(element) {
        if (this.elements.has(element)) {
            this.elements.delete(element);
            if (this.delegate.elementUnmatched) this.delegate.elementUnmatched(element);
        }
    }
}
class $1e1b4d625d583414$export$fe68319d413d05cb {
    constructor(element, attributeName, delegate){
        this.attributeName = attributeName;
        this.delegate = delegate;
        this.elementObserver = new $1e1b4d625d583414$export$12e07a2a9f87578a(element, this);
    }
    get element() {
        return this.elementObserver.element;
    }
    get selector() {
        return `[${this.attributeName}]`;
    }
    start() {
        this.elementObserver.start();
    }
    pause(callback) {
        this.elementObserver.pause(callback);
    }
    stop() {
        this.elementObserver.stop();
    }
    refresh() {
        this.elementObserver.refresh();
    }
    get started() {
        return this.elementObserver.started;
    }
    matchElement(element) {
        return element.hasAttribute(this.attributeName);
    }
    matchElementsInTree(tree) {
        const match = this.matchElement(tree) ? [
            tree
        ] : [];
        const matches = Array.from(tree.querySelectorAll(this.selector));
        return match.concat(matches);
    }
    elementMatched(element) {
        if (this.delegate.elementMatchedAttribute) this.delegate.elementMatchedAttribute(element, this.attributeName);
    }
    elementUnmatched(element) {
        if (this.delegate.elementUnmatchedAttribute) this.delegate.elementUnmatchedAttribute(element, this.attributeName);
    }
    elementAttributeChanged(element, attributeName) {
        if (this.delegate.elementAttributeValueChanged && this.attributeName == attributeName) this.delegate.elementAttributeValueChanged(element, attributeName);
    }
}
function $1e1b4d625d583414$export$e16d8520af44a096(map, key, value) {
    $1e1b4d625d583414$export$e7aa7bc5c1b3cfb3(map, key).add(value);
}
function $1e1b4d625d583414$export$1d2f21e549771e67(map, key, value) {
    $1e1b4d625d583414$export$e7aa7bc5c1b3cfb3(map, key).delete(value);
    $1e1b4d625d583414$export$d6cf75919f12ca5a(map, key);
}
function $1e1b4d625d583414$export$e7aa7bc5c1b3cfb3(map, key) {
    let values = map.get(key);
    if (!values) {
        values = new Set();
        map.set(key, values);
    }
    return values;
}
function $1e1b4d625d583414$export$d6cf75919f12ca5a(map, key) {
    const values = map.get(key);
    if (values != null && values.size == 0) map.delete(key);
}
class $1e1b4d625d583414$export$8bd4ad37b76db6ba {
    constructor(){
        this.valuesByKey = new Map();
    }
    get keys() {
        return Array.from(this.valuesByKey.keys());
    }
    get values() {
        const sets = Array.from(this.valuesByKey.values());
        return sets.reduce((values, set)=>values.concat(Array.from(set)), []);
    }
    get size() {
        const sets = Array.from(this.valuesByKey.values());
        return sets.reduce((size, set)=>size + set.size, 0);
    }
    add(key, value) {
        $1e1b4d625d583414$export$e16d8520af44a096(this.valuesByKey, key, value);
    }
    delete(key, value) {
        $1e1b4d625d583414$export$1d2f21e549771e67(this.valuesByKey, key, value);
    }
    has(key, value) {
        const values = this.valuesByKey.get(key);
        return values != null && values.has(value);
    }
    hasKey(key) {
        return this.valuesByKey.has(key);
    }
    hasValue(value) {
        const sets = Array.from(this.valuesByKey.values());
        return sets.some((set)=>set.has(value));
    }
    getValuesForKey(key) {
        const values = this.valuesByKey.get(key);
        return values ? Array.from(values) : [];
    }
    getKeysForValue(value) {
        return Array.from(this.valuesByKey).filter(([_key, values])=>values.has(value)).map(([key, _values])=>key);
    }
}
class $1e1b4d625d583414$export$a7c3d2c2552a674d extends $1e1b4d625d583414$export$8bd4ad37b76db6ba {
    constructor(){
        super();
        this.keysByValue = new Map();
    }
    get values() {
        return Array.from(this.keysByValue.keys());
    }
    add(key, value) {
        super.add(key, value);
        $1e1b4d625d583414$export$e16d8520af44a096(this.keysByValue, value, key);
    }
    delete(key, value) {
        super.delete(key, value);
        $1e1b4d625d583414$export$1d2f21e549771e67(this.keysByValue, value, key);
    }
    hasValue(value) {
        return this.keysByValue.has(value);
    }
    getKeysForValue(value) {
        const set = this.keysByValue.get(value);
        return set ? Array.from(set) : [];
    }
}
class $1e1b4d625d583414$export$116e0b48509995b {
    constructor(element, selector, delegate, details){
        this._selector = selector;
        this.details = details;
        this.elementObserver = new $1e1b4d625d583414$export$12e07a2a9f87578a(element, this);
        this.delegate = delegate;
        this.matchesByElement = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
    }
    get started() {
        return this.elementObserver.started;
    }
    get selector() {
        return this._selector;
    }
    set selector(selector) {
        this._selector = selector;
        this.refresh();
    }
    start() {
        this.elementObserver.start();
    }
    pause(callback) {
        this.elementObserver.pause(callback);
    }
    stop() {
        this.elementObserver.stop();
    }
    refresh() {
        this.elementObserver.refresh();
    }
    get element() {
        return this.elementObserver.element;
    }
    matchElement(element) {
        const { selector: selector } = this;
        if (selector) {
            const matches = element.matches(selector);
            if (this.delegate.selectorMatchElement) return matches && this.delegate.selectorMatchElement(element, this.details);
            return matches;
        } else return false;
    }
    matchElementsInTree(tree) {
        const { selector: selector } = this;
        if (selector) {
            const match = this.matchElement(tree) ? [
                tree
            ] : [];
            const matches = Array.from(tree.querySelectorAll(selector)).filter((match)=>this.matchElement(match));
            return match.concat(matches);
        } else return [];
    }
    elementMatched(element) {
        const { selector: selector } = this;
        if (selector) this.selectorMatched(element, selector);
    }
    elementUnmatched(element) {
        const selectors = this.matchesByElement.getKeysForValue(element);
        for (const selector of selectors)this.selectorUnmatched(element, selector);
    }
    elementAttributeChanged(element, _attributeName) {
        const { selector: selector } = this;
        if (selector) {
            const matches = this.matchElement(element);
            const matchedBefore = this.matchesByElement.has(selector, element);
            if (matches && !matchedBefore) this.selectorMatched(element, selector);
            else if (!matches && matchedBefore) this.selectorUnmatched(element, selector);
        }
    }
    selectorMatched(element, selector) {
        this.delegate.selectorMatched(element, selector, this.details);
        this.matchesByElement.add(selector, element);
    }
    selectorUnmatched(element, selector) {
        this.delegate.selectorUnmatched(element, selector, this.details);
        this.matchesByElement.delete(selector, element);
    }
}
class $1e1b4d625d583414$export$da226bebbf7f6caa {
    constructor(element, delegate){
        this.element = element;
        this.delegate = delegate;
        this.started = false;
        this.stringMap = new Map();
        this.mutationObserver = new MutationObserver((mutations)=>this.processMutations(mutations));
    }
    start() {
        if (!this.started) {
            this.started = true;
            this.mutationObserver.observe(this.element, {
                attributes: true,
                attributeOldValue: true
            });
            this.refresh();
        }
    }
    stop() {
        if (this.started) {
            this.mutationObserver.takeRecords();
            this.mutationObserver.disconnect();
            this.started = false;
        }
    }
    refresh() {
        if (this.started) for (const attributeName of this.knownAttributeNames)this.refreshAttribute(attributeName, null);
    }
    processMutations(mutations) {
        if (this.started) for (const mutation of mutations)this.processMutation(mutation);
    }
    processMutation(mutation) {
        const attributeName = mutation.attributeName;
        if (attributeName) this.refreshAttribute(attributeName, mutation.oldValue);
    }
    refreshAttribute(attributeName, oldValue) {
        const key = this.delegate.getStringMapKeyForAttribute(attributeName);
        if (key != null) {
            if (!this.stringMap.has(attributeName)) this.stringMapKeyAdded(key, attributeName);
            const value = this.element.getAttribute(attributeName);
            if (this.stringMap.get(attributeName) != value) this.stringMapValueChanged(value, key, oldValue);
            if (value == null) {
                const oldValue = this.stringMap.get(attributeName);
                this.stringMap.delete(attributeName);
                if (oldValue) this.stringMapKeyRemoved(key, attributeName, oldValue);
            } else this.stringMap.set(attributeName, value);
        }
    }
    stringMapKeyAdded(key, attributeName) {
        if (this.delegate.stringMapKeyAdded) this.delegate.stringMapKeyAdded(key, attributeName);
    }
    stringMapValueChanged(value, key, oldValue) {
        if (this.delegate.stringMapValueChanged) this.delegate.stringMapValueChanged(value, key, oldValue);
    }
    stringMapKeyRemoved(key, attributeName, oldValue) {
        if (this.delegate.stringMapKeyRemoved) this.delegate.stringMapKeyRemoved(key, attributeName, oldValue);
    }
    get knownAttributeNames() {
        return Array.from(new Set(this.currentAttributeNames.concat(this.recordedAttributeNames)));
    }
    get currentAttributeNames() {
        return Array.from(this.element.attributes).map((attribute)=>attribute.name);
    }
    get recordedAttributeNames() {
        return Array.from(this.stringMap.keys());
    }
}
class $1e1b4d625d583414$export$3aa21c2f03ef6b3b {
    constructor(element, attributeName, delegate){
        this.attributeObserver = new $1e1b4d625d583414$export$fe68319d413d05cb(element, attributeName, this);
        this.delegate = delegate;
        this.tokensByElement = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
    }
    get started() {
        return this.attributeObserver.started;
    }
    start() {
        this.attributeObserver.start();
    }
    pause(callback) {
        this.attributeObserver.pause(callback);
    }
    stop() {
        this.attributeObserver.stop();
    }
    refresh() {
        this.attributeObserver.refresh();
    }
    get element() {
        return this.attributeObserver.element;
    }
    get attributeName() {
        return this.attributeObserver.attributeName;
    }
    elementMatchedAttribute(element) {
        this.tokensMatched(this.readTokensForElement(element));
    }
    elementAttributeValueChanged(element) {
        const [unmatchedTokens, matchedTokens] = this.refreshTokensForElement(element);
        this.tokensUnmatched(unmatchedTokens);
        this.tokensMatched(matchedTokens);
    }
    elementUnmatchedAttribute(element) {
        this.tokensUnmatched(this.tokensByElement.getValuesForKey(element));
    }
    tokensMatched(tokens) {
        tokens.forEach((token)=>this.tokenMatched(token));
    }
    tokensUnmatched(tokens) {
        tokens.forEach((token)=>this.tokenUnmatched(token));
    }
    tokenMatched(token) {
        this.delegate.tokenMatched(token);
        this.tokensByElement.add(token.element, token);
    }
    tokenUnmatched(token) {
        this.delegate.tokenUnmatched(token);
        this.tokensByElement.delete(token.element, token);
    }
    refreshTokensForElement(element) {
        const previousTokens = this.tokensByElement.getValuesForKey(element);
        const currentTokens = this.readTokensForElement(element);
        const firstDifferingIndex = $1e1b4d625d583414$var$zip(previousTokens, currentTokens).findIndex(([previousToken, currentToken])=>!$1e1b4d625d583414$var$tokensAreEqual(previousToken, currentToken));
        if (firstDifferingIndex == -1) return [
            [],
            []
        ];
        else return [
            previousTokens.slice(firstDifferingIndex),
            currentTokens.slice(firstDifferingIndex)
        ];
    }
    readTokensForElement(element) {
        const attributeName = this.attributeName;
        const tokenString = element.getAttribute(attributeName) || "";
        return $1e1b4d625d583414$var$parseTokenString(tokenString, element, attributeName);
    }
}
function $1e1b4d625d583414$var$parseTokenString(tokenString, element, attributeName) {
    return tokenString.trim().split(/\s+/).filter((content)=>content.length).map((content, index)=>({
            element: element,
            attributeName: attributeName,
            content: content,
            index: index
        }));
}
function $1e1b4d625d583414$var$zip(left, right) {
    const length = Math.max(left.length, right.length);
    return Array.from({
        length: length
    }, (_, index)=>[
            left[index],
            right[index]
        ]);
}
function $1e1b4d625d583414$var$tokensAreEqual(left, right) {
    return left && right && left.index == right.index && left.content == right.content;
}
class $1e1b4d625d583414$export$d27fa4c7330a452e {
    constructor(element, attributeName, delegate){
        this.tokenListObserver = new $1e1b4d625d583414$export$3aa21c2f03ef6b3b(element, attributeName, this);
        this.delegate = delegate;
        this.parseResultsByToken = new WeakMap();
        this.valuesByTokenByElement = new WeakMap();
    }
    get started() {
        return this.tokenListObserver.started;
    }
    start() {
        this.tokenListObserver.start();
    }
    stop() {
        this.tokenListObserver.stop();
    }
    refresh() {
        this.tokenListObserver.refresh();
    }
    get element() {
        return this.tokenListObserver.element;
    }
    get attributeName() {
        return this.tokenListObserver.attributeName;
    }
    tokenMatched(token) {
        const { element: element } = token;
        const { value: value } = this.fetchParseResultForToken(token);
        if (value) {
            this.fetchValuesByTokenForElement(element).set(token, value);
            this.delegate.elementMatchedValue(element, value);
        }
    }
    tokenUnmatched(token) {
        const { element: element } = token;
        const { value: value } = this.fetchParseResultForToken(token);
        if (value) {
            this.fetchValuesByTokenForElement(element).delete(token);
            this.delegate.elementUnmatchedValue(element, value);
        }
    }
    fetchParseResultForToken(token) {
        let parseResult = this.parseResultsByToken.get(token);
        if (!parseResult) {
            parseResult = this.parseToken(token);
            this.parseResultsByToken.set(token, parseResult);
        }
        return parseResult;
    }
    fetchValuesByTokenForElement(element) {
        let valuesByToken = this.valuesByTokenByElement.get(element);
        if (!valuesByToken) {
            valuesByToken = new Map();
            this.valuesByTokenByElement.set(element, valuesByToken);
        }
        return valuesByToken;
    }
    parseToken(token) {
        try {
            const value = this.delegate.parseValueForToken(token);
            return {
                value: value
            };
        } catch (error) {
            return {
                error: error
            };
        }
    }
}
class $1e1b4d625d583414$var$BindingObserver {
    constructor(context, delegate){
        this.context = context;
        this.delegate = delegate;
        this.bindingsByAction = new Map();
    }
    start() {
        if (!this.valueListObserver) {
            this.valueListObserver = new $1e1b4d625d583414$export$d27fa4c7330a452e(this.element, this.actionAttribute, this);
            this.valueListObserver.start();
        }
    }
    stop() {
        if (this.valueListObserver) {
            this.valueListObserver.stop();
            delete this.valueListObserver;
            this.disconnectAllActions();
        }
    }
    get element() {
        return this.context.element;
    }
    get identifier() {
        return this.context.identifier;
    }
    get actionAttribute() {
        return this.schema.actionAttribute;
    }
    get schema() {
        return this.context.schema;
    }
    get bindings() {
        return Array.from(this.bindingsByAction.values());
    }
    connectAction(action) {
        const binding = new $1e1b4d625d583414$var$Binding(this.context, action);
        this.bindingsByAction.set(action, binding);
        this.delegate.bindingConnected(binding);
    }
    disconnectAction(action) {
        const binding = this.bindingsByAction.get(action);
        if (binding) {
            this.bindingsByAction.delete(action);
            this.delegate.bindingDisconnected(binding);
        }
    }
    disconnectAllActions() {
        this.bindings.forEach((binding)=>this.delegate.bindingDisconnected(binding, true));
        this.bindingsByAction.clear();
    }
    parseValueForToken(token) {
        const action = $1e1b4d625d583414$var$Action.forToken(token, this.schema);
        if (action.identifier == this.identifier) return action;
    }
    elementMatchedValue(element, action) {
        this.connectAction(action);
    }
    elementUnmatchedValue(element, action) {
        this.disconnectAction(action);
    }
}
class $1e1b4d625d583414$var$ValueObserver {
    constructor(context, receiver){
        this.context = context;
        this.receiver = receiver;
        this.stringMapObserver = new $1e1b4d625d583414$export$da226bebbf7f6caa(this.element, this);
        this.valueDescriptorMap = this.controller.valueDescriptorMap;
    }
    start() {
        this.stringMapObserver.start();
        this.invokeChangedCallbacksForDefaultValues();
    }
    stop() {
        this.stringMapObserver.stop();
    }
    get element() {
        return this.context.element;
    }
    get controller() {
        return this.context.controller;
    }
    getStringMapKeyForAttribute(attributeName) {
        if (attributeName in this.valueDescriptorMap) return this.valueDescriptorMap[attributeName].name;
    }
    stringMapKeyAdded(key, attributeName) {
        const descriptor = this.valueDescriptorMap[attributeName];
        if (!this.hasValue(key)) this.invokeChangedCallback(key, descriptor.writer(this.receiver[key]), descriptor.writer(descriptor.defaultValue));
    }
    stringMapValueChanged(value, name, oldValue) {
        const descriptor = this.valueDescriptorNameMap[name];
        if (value === null) return;
        if (oldValue === null) oldValue = descriptor.writer(descriptor.defaultValue);
        this.invokeChangedCallback(name, value, oldValue);
    }
    stringMapKeyRemoved(key, attributeName, oldValue) {
        const descriptor = this.valueDescriptorNameMap[key];
        if (this.hasValue(key)) this.invokeChangedCallback(key, descriptor.writer(this.receiver[key]), oldValue);
        else this.invokeChangedCallback(key, descriptor.writer(descriptor.defaultValue), oldValue);
    }
    invokeChangedCallbacksForDefaultValues() {
        for (const { key: key, name: name, defaultValue: defaultValue, writer: writer } of this.valueDescriptors)if (defaultValue != undefined && !this.controller.data.has(key)) this.invokeChangedCallback(name, writer(defaultValue), undefined);
    }
    invokeChangedCallback(name, rawValue, rawOldValue) {
        const changedMethodName = `${name}Changed`;
        const changedMethod = this.receiver[changedMethodName];
        if (typeof changedMethod == "function") {
            const descriptor = this.valueDescriptorNameMap[name];
            try {
                const value = descriptor.reader(rawValue);
                let oldValue = rawOldValue;
                if (rawOldValue) oldValue = descriptor.reader(rawOldValue);
                changedMethod.call(this.receiver, value, oldValue);
            } catch (error) {
                if (error instanceof TypeError) error.message = `Stimulus Value "${this.context.identifier}.${descriptor.name}" - ${error.message}`;
                throw error;
            }
        }
    }
    get valueDescriptors() {
        const { valueDescriptorMap: valueDescriptorMap } = this;
        return Object.keys(valueDescriptorMap).map((key)=>valueDescriptorMap[key]);
    }
    get valueDescriptorNameMap() {
        const descriptors = {};
        Object.keys(this.valueDescriptorMap).forEach((key)=>{
            const descriptor = this.valueDescriptorMap[key];
            descriptors[descriptor.name] = descriptor;
        });
        return descriptors;
    }
    hasValue(attributeName) {
        const descriptor = this.valueDescriptorNameMap[attributeName];
        const hasMethodName = `has${$1e1b4d625d583414$var$capitalize(descriptor.name)}`;
        return this.receiver[hasMethodName];
    }
}
class $1e1b4d625d583414$var$TargetObserver {
    constructor(context, delegate){
        this.context = context;
        this.delegate = delegate;
        this.targetsByName = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
    }
    start() {
        if (!this.tokenListObserver) {
            this.tokenListObserver = new $1e1b4d625d583414$export$3aa21c2f03ef6b3b(this.element, this.attributeName, this);
            this.tokenListObserver.start();
        }
    }
    stop() {
        if (this.tokenListObserver) {
            this.disconnectAllTargets();
            this.tokenListObserver.stop();
            delete this.tokenListObserver;
        }
    }
    tokenMatched({ element: element, content: name }) {
        if (this.scope.containsElement(element)) this.connectTarget(element, name);
    }
    tokenUnmatched({ element: element, content: name }) {
        this.disconnectTarget(element, name);
    }
    connectTarget(element, name) {
        var _a;
        if (!this.targetsByName.has(name, element)) {
            this.targetsByName.add(name, element);
            (_a = this.tokenListObserver) === null || _a === void 0 || _a.pause(()=>this.delegate.targetConnected(element, name));
        }
    }
    disconnectTarget(element, name) {
        var _a;
        if (this.targetsByName.has(name, element)) {
            this.targetsByName.delete(name, element);
            (_a = this.tokenListObserver) === null || _a === void 0 || _a.pause(()=>this.delegate.targetDisconnected(element, name));
        }
    }
    disconnectAllTargets() {
        for (const name of this.targetsByName.keys)for (const element of this.targetsByName.getValuesForKey(name))this.disconnectTarget(element, name);
    }
    get attributeName() {
        return `data-${this.context.identifier}-target`;
    }
    get element() {
        return this.context.element;
    }
    get scope() {
        return this.context.scope;
    }
}
function $1e1b4d625d583414$var$readInheritableStaticArrayValues(constructor, propertyName) {
    const ancestors = $1e1b4d625d583414$var$getAncestorsForConstructor(constructor);
    return Array.from(ancestors.reduce((values, constructor)=>{
        $1e1b4d625d583414$var$getOwnStaticArrayValues(constructor, propertyName).forEach((name)=>values.add(name));
        return values;
    }, new Set()));
}
function $1e1b4d625d583414$var$readInheritableStaticObjectPairs(constructor, propertyName) {
    const ancestors = $1e1b4d625d583414$var$getAncestorsForConstructor(constructor);
    return ancestors.reduce((pairs, constructor)=>{
        pairs.push(...$1e1b4d625d583414$var$getOwnStaticObjectPairs(constructor, propertyName));
        return pairs;
    }, []);
}
function $1e1b4d625d583414$var$getAncestorsForConstructor(constructor) {
    const ancestors = [];
    while(constructor){
        ancestors.push(constructor);
        constructor = Object.getPrototypeOf(constructor);
    }
    return ancestors.reverse();
}
function $1e1b4d625d583414$var$getOwnStaticArrayValues(constructor, propertyName) {
    const definition = constructor[propertyName];
    return Array.isArray(definition) ? definition : [];
}
function $1e1b4d625d583414$var$getOwnStaticObjectPairs(constructor, propertyName) {
    const definition = constructor[propertyName];
    return definition ? Object.keys(definition).map((key)=>[
            key,
            definition[key]
        ]) : [];
}
class $1e1b4d625d583414$var$OutletObserver {
    constructor(context, delegate){
        this.started = false;
        this.context = context;
        this.delegate = delegate;
        this.outletsByName = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
        this.outletElementsByName = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
        this.selectorObserverMap = new Map();
        this.attributeObserverMap = new Map();
    }
    start() {
        if (!this.started) {
            this.outletDefinitions.forEach((outletName)=>{
                this.setupSelectorObserverForOutlet(outletName);
                this.setupAttributeObserverForOutlet(outletName);
            });
            this.started = true;
            this.dependentContexts.forEach((context)=>context.refresh());
        }
    }
    refresh() {
        this.selectorObserverMap.forEach((observer)=>observer.refresh());
        this.attributeObserverMap.forEach((observer)=>observer.refresh());
    }
    stop() {
        if (this.started) {
            this.started = false;
            this.disconnectAllOutlets();
            this.stopSelectorObservers();
            this.stopAttributeObservers();
        }
    }
    stopSelectorObservers() {
        if (this.selectorObserverMap.size > 0) {
            this.selectorObserverMap.forEach((observer)=>observer.stop());
            this.selectorObserverMap.clear();
        }
    }
    stopAttributeObservers() {
        if (this.attributeObserverMap.size > 0) {
            this.attributeObserverMap.forEach((observer)=>observer.stop());
            this.attributeObserverMap.clear();
        }
    }
    selectorMatched(element, _selector, { outletName: outletName }) {
        const outlet = this.getOutlet(element, outletName);
        if (outlet) this.connectOutlet(outlet, element, outletName);
    }
    selectorUnmatched(element, _selector, { outletName: outletName }) {
        const outlet = this.getOutletFromMap(element, outletName);
        if (outlet) this.disconnectOutlet(outlet, element, outletName);
    }
    selectorMatchElement(element, { outletName: outletName }) {
        const selector = this.selector(outletName);
        const hasOutlet = this.hasOutlet(element, outletName);
        const hasOutletController = element.matches(`[${this.schema.controllerAttribute}~=${outletName}]`);
        if (selector) return hasOutlet && hasOutletController && element.matches(selector);
        else return false;
    }
    elementMatchedAttribute(_element, attributeName) {
        const outletName = this.getOutletNameFromOutletAttributeName(attributeName);
        if (outletName) this.updateSelectorObserverForOutlet(outletName);
    }
    elementAttributeValueChanged(_element, attributeName) {
        const outletName = this.getOutletNameFromOutletAttributeName(attributeName);
        if (outletName) this.updateSelectorObserverForOutlet(outletName);
    }
    elementUnmatchedAttribute(_element, attributeName) {
        const outletName = this.getOutletNameFromOutletAttributeName(attributeName);
        if (outletName) this.updateSelectorObserverForOutlet(outletName);
    }
    connectOutlet(outlet, element, outletName) {
        var _a;
        if (!this.outletElementsByName.has(outletName, element)) {
            this.outletsByName.add(outletName, outlet);
            this.outletElementsByName.add(outletName, element);
            (_a = this.selectorObserverMap.get(outletName)) === null || _a === void 0 || _a.pause(()=>this.delegate.outletConnected(outlet, element, outletName));
        }
    }
    disconnectOutlet(outlet, element, outletName) {
        var _a;
        if (this.outletElementsByName.has(outletName, element)) {
            this.outletsByName.delete(outletName, outlet);
            this.outletElementsByName.delete(outletName, element);
            (_a = this.selectorObserverMap.get(outletName)) === null || _a === void 0 || _a.pause(()=>this.delegate.outletDisconnected(outlet, element, outletName));
        }
    }
    disconnectAllOutlets() {
        for (const outletName of this.outletElementsByName.keys){
            for (const element of this.outletElementsByName.getValuesForKey(outletName))for (const outlet of this.outletsByName.getValuesForKey(outletName))this.disconnectOutlet(outlet, element, outletName);
        }
    }
    updateSelectorObserverForOutlet(outletName) {
        const observer = this.selectorObserverMap.get(outletName);
        if (observer) observer.selector = this.selector(outletName);
    }
    setupSelectorObserverForOutlet(outletName) {
        const selector = this.selector(outletName);
        const selectorObserver = new $1e1b4d625d583414$export$116e0b48509995b(document.body, selector, this, {
            outletName: outletName
        });
        this.selectorObserverMap.set(outletName, selectorObserver);
        selectorObserver.start();
    }
    setupAttributeObserverForOutlet(outletName) {
        const attributeName = this.attributeNameForOutletName(outletName);
        const attributeObserver = new $1e1b4d625d583414$export$fe68319d413d05cb(this.scope.element, attributeName, this);
        this.attributeObserverMap.set(outletName, attributeObserver);
        attributeObserver.start();
    }
    selector(outletName) {
        return this.scope.outlets.getSelectorForOutletName(outletName);
    }
    attributeNameForOutletName(outletName) {
        return this.scope.schema.outletAttributeForScope(this.identifier, outletName);
    }
    getOutletNameFromOutletAttributeName(attributeName) {
        return this.outletDefinitions.find((outletName)=>this.attributeNameForOutletName(outletName) === attributeName);
    }
    get outletDependencies() {
        const dependencies = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
        this.router.modules.forEach((module)=>{
            const constructor = module.definition.controllerConstructor;
            const outlets = $1e1b4d625d583414$var$readInheritableStaticArrayValues(constructor, "outlets");
            outlets.forEach((outlet)=>dependencies.add(outlet, module.identifier));
        });
        return dependencies;
    }
    get outletDefinitions() {
        return this.outletDependencies.getKeysForValue(this.identifier);
    }
    get dependentControllerIdentifiers() {
        return this.outletDependencies.getValuesForKey(this.identifier);
    }
    get dependentContexts() {
        const identifiers = this.dependentControllerIdentifiers;
        return this.router.contexts.filter((context)=>identifiers.includes(context.identifier));
    }
    hasOutlet(element, outletName) {
        return !!this.getOutlet(element, outletName) || !!this.getOutletFromMap(element, outletName);
    }
    getOutlet(element, outletName) {
        return this.application.getControllerForElementAndIdentifier(element, outletName);
    }
    getOutletFromMap(element, outletName) {
        return this.outletsByName.getValuesForKey(outletName).find((outlet)=>outlet.element === element);
    }
    get scope() {
        return this.context.scope;
    }
    get schema() {
        return this.context.schema;
    }
    get identifier() {
        return this.context.identifier;
    }
    get application() {
        return this.context.application;
    }
    get router() {
        return this.application.router;
    }
}
class $1e1b4d625d583414$export$841858b892ce1f4c {
    constructor(module, scope){
        this.logDebugActivity = (functionName, detail = {})=>{
            const { identifier: identifier, controller: controller, element: element } = this;
            detail = Object.assign({
                identifier: identifier,
                controller: controller,
                element: element
            }, detail);
            this.application.logDebugActivity(this.identifier, functionName, detail);
        };
        this.module = module;
        this.scope = scope;
        this.controller = new module.controllerConstructor(this);
        this.bindingObserver = new $1e1b4d625d583414$var$BindingObserver(this, this.dispatcher);
        this.valueObserver = new $1e1b4d625d583414$var$ValueObserver(this, this.controller);
        this.targetObserver = new $1e1b4d625d583414$var$TargetObserver(this, this);
        this.outletObserver = new $1e1b4d625d583414$var$OutletObserver(this, this);
        try {
            this.controller.initialize();
            this.logDebugActivity("initialize");
        } catch (error) {
            this.handleError(error, "initializing controller");
        }
    }
    connect() {
        this.bindingObserver.start();
        this.valueObserver.start();
        this.targetObserver.start();
        this.outletObserver.start();
        try {
            this.controller.connect();
            this.logDebugActivity("connect");
        } catch (error) {
            this.handleError(error, "connecting controller");
        }
    }
    refresh() {
        this.outletObserver.refresh();
    }
    disconnect() {
        try {
            this.controller.disconnect();
            this.logDebugActivity("disconnect");
        } catch (error) {
            this.handleError(error, "disconnecting controller");
        }
        this.outletObserver.stop();
        this.targetObserver.stop();
        this.valueObserver.stop();
        this.bindingObserver.stop();
    }
    get application() {
        return this.module.application;
    }
    get identifier() {
        return this.module.identifier;
    }
    get schema() {
        return this.application.schema;
    }
    get dispatcher() {
        return this.application.dispatcher;
    }
    get element() {
        return this.scope.element;
    }
    get parentElement() {
        return this.element.parentElement;
    }
    handleError(error, message, detail = {}) {
        const { identifier: identifier, controller: controller, element: element } = this;
        detail = Object.assign({
            identifier: identifier,
            controller: controller,
            element: element
        }, detail);
        this.application.handleError(error, `Error ${message}`, detail);
    }
    targetConnected(element, name) {
        this.invokeControllerMethod(`${name}TargetConnected`, element);
    }
    targetDisconnected(element, name) {
        this.invokeControllerMethod(`${name}TargetDisconnected`, element);
    }
    outletConnected(outlet, element, name) {
        this.invokeControllerMethod(`${$1e1b4d625d583414$var$namespaceCamelize(name)}OutletConnected`, outlet, element);
    }
    outletDisconnected(outlet, element, name) {
        this.invokeControllerMethod(`${$1e1b4d625d583414$var$namespaceCamelize(name)}OutletDisconnected`, outlet, element);
    }
    invokeControllerMethod(methodName, ...args) {
        const controller = this.controller;
        if (typeof controller[methodName] == "function") controller[methodName](...args);
    }
}
function $1e1b4d625d583414$var$bless(constructor) {
    return $1e1b4d625d583414$var$shadow(constructor, $1e1b4d625d583414$var$getBlessedProperties(constructor));
}
function $1e1b4d625d583414$var$shadow(constructor, properties) {
    const shadowConstructor = $1e1b4d625d583414$var$extend(constructor);
    const shadowProperties = $1e1b4d625d583414$var$getShadowProperties(constructor.prototype, properties);
    Object.defineProperties(shadowConstructor.prototype, shadowProperties);
    return shadowConstructor;
}
function $1e1b4d625d583414$var$getBlessedProperties(constructor) {
    const blessings = $1e1b4d625d583414$var$readInheritableStaticArrayValues(constructor, "blessings");
    return blessings.reduce((blessedProperties, blessing)=>{
        const properties = blessing(constructor);
        for(const key in properties){
            const descriptor = blessedProperties[key] || {};
            blessedProperties[key] = Object.assign(descriptor, properties[key]);
        }
        return blessedProperties;
    }, {});
}
function $1e1b4d625d583414$var$getShadowProperties(prototype, properties) {
    return $1e1b4d625d583414$var$getOwnKeys(properties).reduce((shadowProperties, key)=>{
        const descriptor = $1e1b4d625d583414$var$getShadowedDescriptor(prototype, properties, key);
        if (descriptor) Object.assign(shadowProperties, {
            [key]: descriptor
        });
        return shadowProperties;
    }, {});
}
function $1e1b4d625d583414$var$getShadowedDescriptor(prototype, properties, key) {
    const shadowingDescriptor = Object.getOwnPropertyDescriptor(prototype, key);
    const shadowedByValue = shadowingDescriptor && "value" in shadowingDescriptor;
    if (!shadowedByValue) {
        const descriptor = Object.getOwnPropertyDescriptor(properties, key).value;
        if (shadowingDescriptor) {
            descriptor.get = shadowingDescriptor.get || descriptor.get;
            descriptor.set = shadowingDescriptor.set || descriptor.set;
        }
        return descriptor;
    }
}
const $1e1b4d625d583414$var$getOwnKeys = (()=>{
    if (typeof Object.getOwnPropertySymbols == "function") return (object)=>[
            ...Object.getOwnPropertyNames(object),
            ...Object.getOwnPropertySymbols(object)
        ];
    else return Object.getOwnPropertyNames;
})();
const $1e1b4d625d583414$var$extend = (()=>{
    function extendWithReflect(constructor) {
        function extended() {
            return Reflect.construct(constructor, arguments, new.target);
        }
        extended.prototype = Object.create(constructor.prototype, {
            constructor: {
                value: extended
            }
        });
        Reflect.setPrototypeOf(extended, constructor);
        return extended;
    }
    function testReflectExtension() {
        const a = function() {
            this.a.call(this);
        };
        const b = extendWithReflect(a);
        b.prototype.a = function() {};
        return new b();
    }
    try {
        testReflectExtension();
        return extendWithReflect;
    } catch (error) {
        return (constructor)=>class extended extends constructor {
            };
    }
})();
function $1e1b4d625d583414$var$blessDefinition(definition) {
    return {
        identifier: definition.identifier,
        controllerConstructor: $1e1b4d625d583414$var$bless(definition.controllerConstructor)
    };
}
class $1e1b4d625d583414$var$Module {
    constructor(application, definition){
        this.application = application;
        this.definition = $1e1b4d625d583414$var$blessDefinition(definition);
        this.contextsByScope = new WeakMap();
        this.connectedContexts = new Set();
    }
    get identifier() {
        return this.definition.identifier;
    }
    get controllerConstructor() {
        return this.definition.controllerConstructor;
    }
    get contexts() {
        return Array.from(this.connectedContexts);
    }
    connectContextForScope(scope) {
        const context = this.fetchContextForScope(scope);
        this.connectedContexts.add(context);
        context.connect();
    }
    disconnectContextForScope(scope) {
        const context = this.contextsByScope.get(scope);
        if (context) {
            this.connectedContexts.delete(context);
            context.disconnect();
        }
    }
    fetchContextForScope(scope) {
        let context = this.contextsByScope.get(scope);
        if (!context) {
            context = new $1e1b4d625d583414$export$841858b892ce1f4c(this, scope);
            this.contextsByScope.set(scope, context);
        }
        return context;
    }
}
class $1e1b4d625d583414$var$ClassMap {
    constructor(scope){
        this.scope = scope;
    }
    has(name) {
        return this.data.has(this.getDataKey(name));
    }
    get(name) {
        return this.getAll(name)[0];
    }
    getAll(name) {
        const tokenString = this.data.get(this.getDataKey(name)) || "";
        return $1e1b4d625d583414$var$tokenize(tokenString);
    }
    getAttributeName(name) {
        return this.data.getAttributeNameForKey(this.getDataKey(name));
    }
    getDataKey(name) {
        return `${name}-class`;
    }
    get data() {
        return this.scope.data;
    }
}
class $1e1b4d625d583414$var$DataMap {
    constructor(scope){
        this.scope = scope;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get(key) {
        const name = this.getAttributeNameForKey(key);
        return this.element.getAttribute(name);
    }
    set(key, value) {
        const name = this.getAttributeNameForKey(key);
        this.element.setAttribute(name, value);
        return this.get(key);
    }
    has(key) {
        const name = this.getAttributeNameForKey(key);
        return this.element.hasAttribute(name);
    }
    delete(key) {
        if (this.has(key)) {
            const name = this.getAttributeNameForKey(key);
            this.element.removeAttribute(name);
            return true;
        } else return false;
    }
    getAttributeNameForKey(key) {
        return `data-${this.identifier}-${$1e1b4d625d583414$var$dasherize(key)}`;
    }
}
class $1e1b4d625d583414$var$Guide {
    constructor(logger){
        this.warnedKeysByObject = new WeakMap();
        this.logger = logger;
    }
    warn(object, key, message) {
        let warnedKeys = this.warnedKeysByObject.get(object);
        if (!warnedKeys) {
            warnedKeys = new Set();
            this.warnedKeysByObject.set(object, warnedKeys);
        }
        if (!warnedKeys.has(key)) {
            warnedKeys.add(key);
            this.logger.warn(message, object);
        }
    }
}
function $1e1b4d625d583414$var$attributeValueContainsToken(attributeName, token) {
    return `[${attributeName}~="${token}"]`;
}
class $1e1b4d625d583414$var$TargetSet {
    constructor(scope){
        this.scope = scope;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get schema() {
        return this.scope.schema;
    }
    has(targetName) {
        return this.find(targetName) != null;
    }
    find(...targetNames) {
        return targetNames.reduce((target, targetName)=>target || this.findTarget(targetName) || this.findLegacyTarget(targetName), undefined);
    }
    findAll(...targetNames) {
        return targetNames.reduce((targets, targetName)=>[
                ...targets,
                ...this.findAllTargets(targetName),
                ...this.findAllLegacyTargets(targetName)
            ], []);
    }
    findTarget(targetName) {
        const selector = this.getSelectorForTargetName(targetName);
        return this.scope.findElement(selector);
    }
    findAllTargets(targetName) {
        const selector = this.getSelectorForTargetName(targetName);
        return this.scope.findAllElements(selector);
    }
    getSelectorForTargetName(targetName) {
        const attributeName = this.schema.targetAttributeForScope(this.identifier);
        return $1e1b4d625d583414$var$attributeValueContainsToken(attributeName, targetName);
    }
    findLegacyTarget(targetName) {
        const selector = this.getLegacySelectorForTargetName(targetName);
        return this.deprecate(this.scope.findElement(selector), targetName);
    }
    findAllLegacyTargets(targetName) {
        const selector = this.getLegacySelectorForTargetName(targetName);
        return this.scope.findAllElements(selector).map((element)=>this.deprecate(element, targetName));
    }
    getLegacySelectorForTargetName(targetName) {
        const targetDescriptor = `${this.identifier}.${targetName}`;
        return $1e1b4d625d583414$var$attributeValueContainsToken(this.schema.targetAttribute, targetDescriptor);
    }
    deprecate(element, targetName) {
        if (element) {
            const { identifier: identifier } = this;
            const attributeName = this.schema.targetAttribute;
            const revisedAttributeName = this.schema.targetAttributeForScope(identifier);
            this.guide.warn(element, `target:${targetName}`, `Please replace ${attributeName}="${identifier}.${targetName}" with ${revisedAttributeName}="${targetName}". ` + `The ${attributeName} attribute is deprecated and will be removed in a future version of Stimulus.`);
        }
        return element;
    }
    get guide() {
        return this.scope.guide;
    }
}
class $1e1b4d625d583414$var$OutletSet {
    constructor(scope, controllerElement){
        this.scope = scope;
        this.controllerElement = controllerElement;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get schema() {
        return this.scope.schema;
    }
    has(outletName) {
        return this.find(outletName) != null;
    }
    find(...outletNames) {
        return outletNames.reduce((outlet, outletName)=>outlet || this.findOutlet(outletName), undefined);
    }
    findAll(...outletNames) {
        return outletNames.reduce((outlets, outletName)=>[
                ...outlets,
                ...this.findAllOutlets(outletName)
            ], []);
    }
    getSelectorForOutletName(outletName) {
        const attributeName = this.schema.outletAttributeForScope(this.identifier, outletName);
        return this.controllerElement.getAttribute(attributeName);
    }
    findOutlet(outletName) {
        const selector = this.getSelectorForOutletName(outletName);
        if (selector) return this.findElement(selector, outletName);
    }
    findAllOutlets(outletName) {
        const selector = this.getSelectorForOutletName(outletName);
        return selector ? this.findAllElements(selector, outletName) : [];
    }
    findElement(selector, outletName) {
        const elements = this.scope.queryElements(selector);
        return elements.filter((element)=>this.matchesElement(element, selector, outletName))[0];
    }
    findAllElements(selector, outletName) {
        const elements = this.scope.queryElements(selector);
        return elements.filter((element)=>this.matchesElement(element, selector, outletName));
    }
    matchesElement(element, selector, outletName) {
        const controllerAttribute = element.getAttribute(this.scope.schema.controllerAttribute) || "";
        return element.matches(selector) && controllerAttribute.split(" ").includes(outletName);
    }
}
class $1e1b4d625d583414$var$Scope {
    constructor(schema, element, identifier, logger){
        this.targets = new $1e1b4d625d583414$var$TargetSet(this);
        this.classes = new $1e1b4d625d583414$var$ClassMap(this);
        this.data = new $1e1b4d625d583414$var$DataMap(this);
        this.containsElement = (element)=>{
            return element.closest(this.controllerSelector) === this.element;
        };
        this.schema = schema;
        this.element = element;
        this.identifier = identifier;
        this.guide = new $1e1b4d625d583414$var$Guide(logger);
        this.outlets = new $1e1b4d625d583414$var$OutletSet(this.documentScope, element);
    }
    findElement(selector) {
        return this.element.matches(selector) ? this.element : this.queryElements(selector).find(this.containsElement);
    }
    findAllElements(selector) {
        return [
            ...this.element.matches(selector) ? [
                this.element
            ] : [],
            ...this.queryElements(selector).filter(this.containsElement)
        ];
    }
    queryElements(selector) {
        return Array.from(this.element.querySelectorAll(selector));
    }
    get controllerSelector() {
        return $1e1b4d625d583414$var$attributeValueContainsToken(this.schema.controllerAttribute, this.identifier);
    }
    get isDocumentScope() {
        return this.element === document.documentElement;
    }
    get documentScope() {
        return this.isDocumentScope ? this : new $1e1b4d625d583414$var$Scope(this.schema, document.documentElement, this.identifier, this.guide.logger);
    }
}
class $1e1b4d625d583414$var$ScopeObserver {
    constructor(element, schema, delegate){
        this.element = element;
        this.schema = schema;
        this.delegate = delegate;
        this.valueListObserver = new $1e1b4d625d583414$export$d27fa4c7330a452e(this.element, this.controllerAttribute, this);
        this.scopesByIdentifierByElement = new WeakMap();
        this.scopeReferenceCounts = new WeakMap();
    }
    start() {
        this.valueListObserver.start();
    }
    stop() {
        this.valueListObserver.stop();
    }
    get controllerAttribute() {
        return this.schema.controllerAttribute;
    }
    parseValueForToken(token) {
        const { element: element, content: identifier } = token;
        return this.parseValueForElementAndIdentifier(element, identifier);
    }
    parseValueForElementAndIdentifier(element, identifier) {
        const scopesByIdentifier = this.fetchScopesByIdentifierForElement(element);
        let scope = scopesByIdentifier.get(identifier);
        if (!scope) {
            scope = this.delegate.createScopeForElementAndIdentifier(element, identifier);
            scopesByIdentifier.set(identifier, scope);
        }
        return scope;
    }
    elementMatchedValue(element, value) {
        const referenceCount = (this.scopeReferenceCounts.get(value) || 0) + 1;
        this.scopeReferenceCounts.set(value, referenceCount);
        if (referenceCount == 1) this.delegate.scopeConnected(value);
    }
    elementUnmatchedValue(element, value) {
        const referenceCount = this.scopeReferenceCounts.get(value);
        if (referenceCount) {
            this.scopeReferenceCounts.set(value, referenceCount - 1);
            if (referenceCount == 1) this.delegate.scopeDisconnected(value);
        }
    }
    fetchScopesByIdentifierForElement(element) {
        let scopesByIdentifier = this.scopesByIdentifierByElement.get(element);
        if (!scopesByIdentifier) {
            scopesByIdentifier = new Map();
            this.scopesByIdentifierByElement.set(element, scopesByIdentifier);
        }
        return scopesByIdentifier;
    }
}
class $1e1b4d625d583414$var$Router {
    constructor(application){
        this.application = application;
        this.scopeObserver = new $1e1b4d625d583414$var$ScopeObserver(this.element, this.schema, this);
        this.scopesByIdentifier = new $1e1b4d625d583414$export$8bd4ad37b76db6ba();
        this.modulesByIdentifier = new Map();
    }
    get element() {
        return this.application.element;
    }
    get schema() {
        return this.application.schema;
    }
    get logger() {
        return this.application.logger;
    }
    get controllerAttribute() {
        return this.schema.controllerAttribute;
    }
    get modules() {
        return Array.from(this.modulesByIdentifier.values());
    }
    get contexts() {
        return this.modules.reduce((contexts, module)=>contexts.concat(module.contexts), []);
    }
    start() {
        this.scopeObserver.start();
    }
    stop() {
        this.scopeObserver.stop();
    }
    loadDefinition(definition) {
        this.unloadIdentifier(definition.identifier);
        const module = new $1e1b4d625d583414$var$Module(this.application, definition);
        this.connectModule(module);
        const afterLoad = definition.controllerConstructor.afterLoad;
        if (afterLoad) afterLoad.call(definition.controllerConstructor, definition.identifier, this.application);
    }
    unloadIdentifier(identifier) {
        const module = this.modulesByIdentifier.get(identifier);
        if (module) this.disconnectModule(module);
    }
    getContextForElementAndIdentifier(element, identifier) {
        const module = this.modulesByIdentifier.get(identifier);
        if (module) return module.contexts.find((context)=>context.element == element);
    }
    proposeToConnectScopeForElementAndIdentifier(element, identifier) {
        const scope = this.scopeObserver.parseValueForElementAndIdentifier(element, identifier);
        if (scope) this.scopeObserver.elementMatchedValue(scope.element, scope);
        else console.error(`Couldn't find or create scope for identifier: "${identifier}" and element:`, element);
    }
    handleError(error, message, detail) {
        this.application.handleError(error, message, detail);
    }
    createScopeForElementAndIdentifier(element, identifier) {
        return new $1e1b4d625d583414$var$Scope(this.schema, element, identifier, this.logger);
    }
    scopeConnected(scope) {
        this.scopesByIdentifier.add(scope.identifier, scope);
        const module = this.modulesByIdentifier.get(scope.identifier);
        if (module) module.connectContextForScope(scope);
    }
    scopeDisconnected(scope) {
        this.scopesByIdentifier.delete(scope.identifier, scope);
        const module = this.modulesByIdentifier.get(scope.identifier);
        if (module) module.disconnectContextForScope(scope);
    }
    connectModule(module) {
        this.modulesByIdentifier.set(module.identifier, module);
        const scopes = this.scopesByIdentifier.getValuesForKey(module.identifier);
        scopes.forEach((scope)=>module.connectContextForScope(scope));
    }
    disconnectModule(module) {
        this.modulesByIdentifier.delete(module.identifier);
        const scopes = this.scopesByIdentifier.getValuesForKey(module.identifier);
        scopes.forEach((scope)=>module.disconnectContextForScope(scope));
    }
}
const $1e1b4d625d583414$export$1db618b7b2275ea1 = {
    controllerAttribute: "data-controller",
    actionAttribute: "data-action",
    targetAttribute: "data-target",
    targetAttributeForScope: (identifier)=>`data-${identifier}-target`,
    outletAttributeForScope: (identifier, outlet)=>`data-${identifier}-${outlet}-outlet`,
    keyMappings: Object.assign(Object.assign({
        enter: "Enter",
        tab: "Tab",
        esc: "Escape",
        space: " ",
        up: "ArrowUp",
        down: "ArrowDown",
        left: "ArrowLeft",
        right: "ArrowRight",
        home: "Home",
        end: "End",
        page_up: "PageUp",
        page_down: "PageDown"
    }, $1e1b4d625d583414$var$objectFromEntries("abcdefghijklmnopqrstuvwxyz".split("").map((c)=>[
            c,
            c
        ]))), $1e1b4d625d583414$var$objectFromEntries("0123456789".split("").map((n)=>[
            n,
            n
        ])))
};
function $1e1b4d625d583414$var$objectFromEntries(array) {
    return array.reduce((memo, [k, v])=>Object.assign(Object.assign({}, memo), {
            [k]: v
        }), {});
}
class $1e1b4d625d583414$export$16975c34e60e1e61 {
    constructor(element = document.documentElement, schema = $1e1b4d625d583414$export$1db618b7b2275ea1){
        this.logger = console;
        this.debug = false;
        this.logDebugActivity = (identifier, functionName, detail = {})=>{
            if (this.debug) this.logFormattedMessage(identifier, functionName, detail);
        };
        this.element = element;
        this.schema = schema;
        this.dispatcher = new $1e1b4d625d583414$var$Dispatcher(this);
        this.router = new $1e1b4d625d583414$var$Router(this);
        this.actionDescriptorFilters = Object.assign({}, $1e1b4d625d583414$var$defaultActionDescriptorFilters);
    }
    static start(element, schema) {
        const application = new this(element, schema);
        application.start();
        return application;
    }
    async start() {
        await $1e1b4d625d583414$var$domReady();
        this.logDebugActivity("application", "starting");
        this.dispatcher.start();
        this.router.start();
        this.logDebugActivity("application", "start");
    }
    stop() {
        this.logDebugActivity("application", "stopping");
        this.dispatcher.stop();
        this.router.stop();
        this.logDebugActivity("application", "stop");
    }
    register(identifier, controllerConstructor) {
        this.load({
            identifier: identifier,
            controllerConstructor: controllerConstructor
        });
    }
    registerActionOption(name, filter) {
        this.actionDescriptorFilters[name] = filter;
    }
    load(head, ...rest) {
        const definitions = Array.isArray(head) ? head : [
            head,
            ...rest
        ];
        definitions.forEach((definition)=>{
            if (definition.controllerConstructor.shouldLoad) this.router.loadDefinition(definition);
        });
    }
    unload(head, ...rest) {
        const identifiers = Array.isArray(head) ? head : [
            head,
            ...rest
        ];
        identifiers.forEach((identifier)=>this.router.unloadIdentifier(identifier));
    }
    get controllers() {
        return this.router.contexts.map((context)=>context.controller);
    }
    getControllerForElementAndIdentifier(element, identifier) {
        const context = this.router.getContextForElementAndIdentifier(element, identifier);
        return context ? context.controller : null;
    }
    handleError(error, message, detail) {
        var _a;
        this.logger.error(`%s\n\n%o\n\n%o`, message, error, detail);
        (_a = window.onerror) === null || _a === void 0 || _a.call(window, message, "", 0, 0, error);
    }
    logFormattedMessage(identifier, functionName, detail = {}) {
        detail = Object.assign({
            application: this
        }, detail);
        this.logger.groupCollapsed(`${identifier} #${functionName}`);
        this.logger.log("details:", Object.assign({}, detail));
        this.logger.groupEnd();
    }
}
function $1e1b4d625d583414$var$domReady() {
    return new Promise((resolve)=>{
        if (document.readyState == "loading") document.addEventListener("DOMContentLoaded", ()=>resolve());
        else resolve();
    });
}
function $1e1b4d625d583414$var$ClassPropertiesBlessing(constructor) {
    const classes = $1e1b4d625d583414$var$readInheritableStaticArrayValues(constructor, "classes");
    return classes.reduce((properties, classDefinition)=>{
        return Object.assign(properties, $1e1b4d625d583414$var$propertiesForClassDefinition(classDefinition));
    }, {});
}
function $1e1b4d625d583414$var$propertiesForClassDefinition(key) {
    return {
        [`${key}Class`]: {
            get () {
                const { classes: classes } = this;
                if (classes.has(key)) return classes.get(key);
                else {
                    const attribute = classes.getAttributeName(key);
                    throw new Error(`Missing attribute "${attribute}"`);
                }
            }
        },
        [`${key}Classes`]: {
            get () {
                return this.classes.getAll(key);
            }
        },
        [`has${$1e1b4d625d583414$var$capitalize(key)}Class`]: {
            get () {
                return this.classes.has(key);
            }
        }
    };
}
function $1e1b4d625d583414$var$OutletPropertiesBlessing(constructor) {
    const outlets = $1e1b4d625d583414$var$readInheritableStaticArrayValues(constructor, "outlets");
    return outlets.reduce((properties, outletDefinition)=>{
        return Object.assign(properties, $1e1b4d625d583414$var$propertiesForOutletDefinition(outletDefinition));
    }, {});
}
function $1e1b4d625d583414$var$getOutletController(controller, element, identifier) {
    return controller.application.getControllerForElementAndIdentifier(element, identifier);
}
function $1e1b4d625d583414$var$getControllerAndEnsureConnectedScope(controller, element, outletName) {
    let outletController = $1e1b4d625d583414$var$getOutletController(controller, element, outletName);
    if (outletController) return outletController;
    controller.application.router.proposeToConnectScopeForElementAndIdentifier(element, outletName);
    outletController = $1e1b4d625d583414$var$getOutletController(controller, element, outletName);
    if (outletController) return outletController;
}
function $1e1b4d625d583414$var$propertiesForOutletDefinition(name) {
    const camelizedName = $1e1b4d625d583414$var$namespaceCamelize(name);
    return {
        [`${camelizedName}Outlet`]: {
            get () {
                const outletElement = this.outlets.find(name);
                const selector = this.outlets.getSelectorForOutletName(name);
                if (outletElement) {
                    const outletController = $1e1b4d625d583414$var$getControllerAndEnsureConnectedScope(this, outletElement, name);
                    if (outletController) return outletController;
                    throw new Error(`The provided outlet element is missing an outlet controller "${name}" instance for host controller "${this.identifier}"`);
                }
                throw new Error(`Missing outlet element "${name}" for host controller "${this.identifier}". Stimulus couldn't find a matching outlet element using selector "${selector}".`);
            }
        },
        [`${camelizedName}Outlets`]: {
            get () {
                const outlets = this.outlets.findAll(name);
                if (outlets.length > 0) return outlets.map((outletElement)=>{
                    const outletController = $1e1b4d625d583414$var$getControllerAndEnsureConnectedScope(this, outletElement, name);
                    if (outletController) return outletController;
                    console.warn(`The provided outlet element is missing an outlet controller "${name}" instance for host controller "${this.identifier}"`, outletElement);
                }).filter((controller)=>controller);
                return [];
            }
        },
        [`${camelizedName}OutletElement`]: {
            get () {
                const outletElement = this.outlets.find(name);
                const selector = this.outlets.getSelectorForOutletName(name);
                if (outletElement) return outletElement;
                else throw new Error(`Missing outlet element "${name}" for host controller "${this.identifier}". Stimulus couldn't find a matching outlet element using selector "${selector}".`);
            }
        },
        [`${camelizedName}OutletElements`]: {
            get () {
                return this.outlets.findAll(name);
            }
        },
        [`has${$1e1b4d625d583414$var$capitalize(camelizedName)}Outlet`]: {
            get () {
                return this.outlets.has(name);
            }
        }
    };
}
function $1e1b4d625d583414$var$TargetPropertiesBlessing(constructor) {
    const targets = $1e1b4d625d583414$var$readInheritableStaticArrayValues(constructor, "targets");
    return targets.reduce((properties, targetDefinition)=>{
        return Object.assign(properties, $1e1b4d625d583414$var$propertiesForTargetDefinition(targetDefinition));
    }, {});
}
function $1e1b4d625d583414$var$propertiesForTargetDefinition(name) {
    return {
        [`${name}Target`]: {
            get () {
                const target = this.targets.find(name);
                if (target) return target;
                else throw new Error(`Missing target element "${name}" for "${this.identifier}" controller`);
            }
        },
        [`${name}Targets`]: {
            get () {
                return this.targets.findAll(name);
            }
        },
        [`has${$1e1b4d625d583414$var$capitalize(name)}Target`]: {
            get () {
                return this.targets.has(name);
            }
        }
    };
}
function $1e1b4d625d583414$var$ValuePropertiesBlessing(constructor) {
    const valueDefinitionPairs = $1e1b4d625d583414$var$readInheritableStaticObjectPairs(constructor, "values");
    const propertyDescriptorMap = {
        valueDescriptorMap: {
            get () {
                return valueDefinitionPairs.reduce((result, valueDefinitionPair)=>{
                    const valueDescriptor = $1e1b4d625d583414$var$parseValueDefinitionPair(valueDefinitionPair, this.identifier);
                    const attributeName = this.data.getAttributeNameForKey(valueDescriptor.key);
                    return Object.assign(result, {
                        [attributeName]: valueDescriptor
                    });
                }, {});
            }
        }
    };
    return valueDefinitionPairs.reduce((properties, valueDefinitionPair)=>{
        return Object.assign(properties, $1e1b4d625d583414$var$propertiesForValueDefinitionPair(valueDefinitionPair));
    }, propertyDescriptorMap);
}
function $1e1b4d625d583414$var$propertiesForValueDefinitionPair(valueDefinitionPair, controller) {
    const definition = $1e1b4d625d583414$var$parseValueDefinitionPair(valueDefinitionPair, controller);
    const { key: key, name: name, reader: read, writer: write } = definition;
    return {
        [name]: {
            get () {
                const value = this.data.get(key);
                if (value !== null) return read(value);
                else return definition.defaultValue;
            },
            set (value) {
                if (value === undefined) this.data.delete(key);
                else this.data.set(key, write(value));
            }
        },
        [`has${$1e1b4d625d583414$var$capitalize(name)}`]: {
            get () {
                return this.data.has(key) || definition.hasCustomDefaultValue;
            }
        }
    };
}
function $1e1b4d625d583414$var$parseValueDefinitionPair([token, typeDefinition], controller) {
    return $1e1b4d625d583414$var$valueDescriptorForTokenAndTypeDefinition({
        controller: controller,
        token: token,
        typeDefinition: typeDefinition
    });
}
function $1e1b4d625d583414$var$parseValueTypeConstant(constant) {
    switch(constant){
        case Array:
            return "array";
        case Boolean:
            return "boolean";
        case Number:
            return "number";
        case Object:
            return "object";
        case String:
            return "string";
    }
}
function $1e1b4d625d583414$var$parseValueTypeDefault(defaultValue) {
    switch(typeof defaultValue){
        case "boolean":
            return "boolean";
        case "number":
            return "number";
        case "string":
            return "string";
    }
    if (Array.isArray(defaultValue)) return "array";
    if (Object.prototype.toString.call(defaultValue) === "[object Object]") return "object";
}
function $1e1b4d625d583414$var$parseValueTypeObject(payload) {
    const { controller: controller, token: token, typeObject: typeObject } = payload;
    const hasType = $1e1b4d625d583414$var$isSomething(typeObject.type);
    const hasDefault = $1e1b4d625d583414$var$isSomething(typeObject.default);
    const fullObject = hasType && hasDefault;
    const onlyType = hasType && !hasDefault;
    const onlyDefault = !hasType && hasDefault;
    const typeFromObject = $1e1b4d625d583414$var$parseValueTypeConstant(typeObject.type);
    const typeFromDefaultValue = $1e1b4d625d583414$var$parseValueTypeDefault(payload.typeObject.default);
    if (onlyType) return typeFromObject;
    if (onlyDefault) return typeFromDefaultValue;
    if (typeFromObject !== typeFromDefaultValue) {
        const propertyPath = controller ? `${controller}.${token}` : token;
        throw new Error(`The specified default value for the Stimulus Value "${propertyPath}" must match the defined type "${typeFromObject}". The provided default value of "${typeObject.default}" is of type "${typeFromDefaultValue}".`);
    }
    if (fullObject) return typeFromObject;
}
function $1e1b4d625d583414$var$parseValueTypeDefinition(payload) {
    const { controller: controller, token: token, typeDefinition: typeDefinition } = payload;
    const typeObject = {
        controller: controller,
        token: token,
        typeObject: typeDefinition
    };
    const typeFromObject = $1e1b4d625d583414$var$parseValueTypeObject(typeObject);
    const typeFromDefaultValue = $1e1b4d625d583414$var$parseValueTypeDefault(typeDefinition);
    const typeFromConstant = $1e1b4d625d583414$var$parseValueTypeConstant(typeDefinition);
    const type = typeFromObject || typeFromDefaultValue || typeFromConstant;
    if (type) return type;
    const propertyPath = controller ? `${controller}.${typeDefinition}` : token;
    throw new Error(`Unknown value type "${propertyPath}" for "${token}" value`);
}
function $1e1b4d625d583414$var$defaultValueForDefinition(typeDefinition) {
    const constant = $1e1b4d625d583414$var$parseValueTypeConstant(typeDefinition);
    if (constant) return $1e1b4d625d583414$var$defaultValuesByType[constant];
    const hasDefault = $1e1b4d625d583414$var$hasProperty(typeDefinition, "default");
    const hasType = $1e1b4d625d583414$var$hasProperty(typeDefinition, "type");
    const typeObject = typeDefinition;
    if (hasDefault) return typeObject.default;
    if (hasType) {
        const { type: type } = typeObject;
        const constantFromType = $1e1b4d625d583414$var$parseValueTypeConstant(type);
        if (constantFromType) return $1e1b4d625d583414$var$defaultValuesByType[constantFromType];
    }
    return typeDefinition;
}
function $1e1b4d625d583414$var$valueDescriptorForTokenAndTypeDefinition(payload) {
    const { token: token, typeDefinition: typeDefinition } = payload;
    const key = `${$1e1b4d625d583414$var$dasherize(token)}-value`;
    const type = $1e1b4d625d583414$var$parseValueTypeDefinition(payload);
    return {
        type: type,
        key: key,
        name: $1e1b4d625d583414$var$camelize(key),
        get defaultValue () {
            return $1e1b4d625d583414$var$defaultValueForDefinition(typeDefinition);
        },
        get hasCustomDefaultValue () {
            return $1e1b4d625d583414$var$parseValueTypeDefault(typeDefinition) !== undefined;
        },
        reader: $1e1b4d625d583414$var$readers[type],
        writer: $1e1b4d625d583414$var$writers[type] || $1e1b4d625d583414$var$writers.default
    };
}
const $1e1b4d625d583414$var$defaultValuesByType = {
    get array () {
        return [];
    },
    boolean: false,
    number: 0,
    get object () {
        return {};
    },
    string: ""
};
const $1e1b4d625d583414$var$readers = {
    array (value) {
        const array = JSON.parse(value);
        if (!Array.isArray(array)) throw new TypeError(`expected value of type "array" but instead got value "${value}" of type "${$1e1b4d625d583414$var$parseValueTypeDefault(array)}"`);
        return array;
    },
    boolean (value) {
        return !(value == "0" || String(value).toLowerCase() == "false");
    },
    number (value) {
        return Number(value.replace(/_/g, ""));
    },
    object (value) {
        const object = JSON.parse(value);
        if (object === null || typeof object != "object" || Array.isArray(object)) throw new TypeError(`expected value of type "object" but instead got value "${value}" of type "${$1e1b4d625d583414$var$parseValueTypeDefault(object)}"`);
        return object;
    },
    string (value) {
        return value;
    }
};
const $1e1b4d625d583414$var$writers = {
    default: $1e1b4d625d583414$var$writeString,
    array: $1e1b4d625d583414$var$writeJSON,
    object: $1e1b4d625d583414$var$writeJSON
};
function $1e1b4d625d583414$var$writeJSON(value) {
    return JSON.stringify(value);
}
function $1e1b4d625d583414$var$writeString(value) {
    return `${value}`;
}
class $1e1b4d625d583414$export$bd0bf19f25da8474 {
    constructor(context){
        this.context = context;
    }
    static get shouldLoad() {
        return true;
    }
    static afterLoad(_identifier, _application) {
        return;
    }
    get application() {
        return this.context.application;
    }
    get scope() {
        return this.context.scope;
    }
    get element() {
        return this.scope.element;
    }
    get identifier() {
        return this.scope.identifier;
    }
    get targets() {
        return this.scope.targets;
    }
    get outlets() {
        return this.scope.outlets;
    }
    get classes() {
        return this.scope.classes;
    }
    get data() {
        return this.scope.data;
    }
    initialize() {}
    connect() {}
    disconnect() {}
    dispatch(eventName, { target: target = this.element, detail: detail = {}, prefix: prefix = this.identifier, bubbles: bubbles = true, cancelable: cancelable = true } = {}) {
        const type = prefix ? `${prefix}:${eventName}` : eventName;
        const event = new CustomEvent(type, {
            detail: detail,
            bubbles: bubbles,
            cancelable: cancelable
        });
        target.dispatchEvent(event);
        return event;
    }
}
$1e1b4d625d583414$export$bd0bf19f25da8474.blessings = [
    $1e1b4d625d583414$var$ClassPropertiesBlessing,
    $1e1b4d625d583414$var$TargetPropertiesBlessing,
    $1e1b4d625d583414$var$ValuePropertiesBlessing,
    $1e1b4d625d583414$var$OutletPropertiesBlessing
];
$1e1b4d625d583414$export$bd0bf19f25da8474.targets = [];
$1e1b4d625d583414$export$bd0bf19f25da8474.outlets = [];
$1e1b4d625d583414$export$bd0bf19f25da8474.values = {};



class $7e74619ac6bb31b5$export$2e2bcd8739ae039 extends (0, $1e1b4d625d583414$export$bd0bf19f25da8474) {
    static values = {
        siteKey: String
    };
    connect() {
        let siteKey = this.siteKeyValue;
        turnstile.ready(function() {
            turnstile.render(".cf-turnstile", {
                sitekey: siteKey,
                action: "submit-joke",
                theme: "light"
            });
        });
    }
}



class $53e3e83cb350b912$export$2e2bcd8739ae039 extends (0, $1e1b4d625d583414$export$bd0bf19f25da8474) {
    static targets = [
        "row",
        "dialog",
        "deleteForm"
    ];
    showDialog(event) {
        event.preventDefault();
        this.dialogTarget.showModal();
        this.deleteFormTarget.action = `/admin/joke/${event.target.dataset.jokeId}/process`;
    }
    closeDialog() {
        this.dialogTarget.close();
    }
    deleteJoke(event) {
        event.preventDefault();
        this.deleteFormTarget.requestSubmit();
        this.closeDialog();
    }
}



class $1494fd29c42d123f$export$2e2bcd8739ae039 extends (0, $1e1b4d625d583414$export$bd0bf19f25da8474) {
    static targets = [
        "joke",
        "notification"
    ];
    copyToClipboard() {
        let joke = this.jokeTarget.innerText;
        navigator.clipboard.writeText(joke);
        if (this.notificationTarget.hasChildNodes()) this.notificationTarget.firstChild.remove();
        const toast = document.createElement("div");
        const article = document.createElement("article");
        article.classList.add("pico-background-green-500");
        const content = document.createElement("div");
        content.innerText = "Copied to clipboard!";
        toast.appendChild(article);
        article.appendChild(content);
        this.notificationTarget.appendChild(toast);
        setTimeout(()=>{
            toast.remove();
        }, 3000);
    }
}


window.Stimulus = (0, $1e1b4d625d583414$export$16975c34e60e1e61).start();
Stimulus.register("captcha", (0, $7e74619ac6bb31b5$export$2e2bcd8739ae039));
Stimulus.register("joke-list", (0, $53e3e83cb350b912$export$2e2bcd8739ae039));
Stimulus.register("random-joke", (0, $1494fd29c42d123f$export$2e2bcd8739ae039));

})();
//# sourceMappingURL=app.js.map
